import React from "react";
import { createRoot } from "react-dom/client";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.mjs?url";
import {
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Database,
  Download,
  FileText,
  Images,
  LayoutDashboard,
  Library,
  PlaySquare,
  Plus,
  Settings,
  Share2,
  Sparkles,
  Users,
} from "lucide-react";
import {
  apiBaseUrl,
  apiLogin,
  checkDuplicates,
  createKnowledgePublishRecord,
  createRemote,
  deleteKnowledgePublishRecord,
  deleteRemote,
  fetchBootstrap,
  fetchHealth,
  fetchKnowledgeFile,
  fetchKnowledgeTree,
  postRemote,
  previewCollectionSource,
  updateKnowledgePublishRecord,
  updateKnowledgePublishStatus,
  updateRemote,
  uploadGeneratedImage,
  uploadRemoteAsset,
  type ApiHealth,
  type CollectedPreviewItem,
  type DuplicateMatch,
  type KnowledgePublishRecord,
  type RemoteKnowledgeNode,
} from "./api";
import "./styles.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

type KnowledgeTreeNode = {
  id: string;
  name: string;
  title: string;
  path: string;
  kind: "folder" | "file";
  children: KnowledgeTreeNode[];
  file?: File;
  updatedAt?: number;
  tags?: string[];
  nodeId?: string;
  publishStatus?: string;
  publishedAt?: string;
  publishNote?: string;
};
type KnowledgeArticle = {
  title: string;
  path: string;
  content: string;
  updatedAt: number;
  tags: string[];
  nodeId: string;
  publishStatus: string;
  publishedAt: string;
  publishNote: string;
  publishRecords: KnowledgePublishRecord[];
};

const knowledgePublishStatuses = [
  { value: "unpublished", label: "未发布" },
  { value: "pending", label: "待发布" },
  { value: "published", label: "已发布" },
  { value: "ignored", label: "不发布" },
] as const;

const knowledgeRecordStatuses = [
  { value: "pending", label: "待发布" },
  { value: "published", label: "已发布" },
  { value: "failed", label: "发布失败" },
  { value: "ignored", label: "不发布" },
] as const;

function knowledgePublishStatusLabel(value = "unpublished") {
  return (
    knowledgePublishStatuses.find((item) => item.value === value)?.label ||
    "未发布"
  );
}

function knowledgeRecordStatusLabel(value = "pending") {
  return (
    knowledgeRecordStatuses.find((item) => item.value === value)?.label ||
    "待发布"
  );
}

type NavKey =
  | "dashboard"
  | "tasks"
  | "collection"
  | "topics"
  | "master"
  | "platformVersions"
  | "imageText"
  | "video"
  | "assets"
  | "publishing"
  | "analytics"
  | "accounts"
  | "settings";
type CollectionSubKey =
  | "new"
  | "history"
  | "sources"
  | "rules"
  | "tasks"
  | "list";
type AccountSubKey = "platforms" | "accounts";
type MasterSubKey = "new" | "generate" | "list";
type ImageTextSubKey = "versions" | "projects";
type TaskSubKey = "new" | "list";
type TopicSubKey = "new" | "list";
type PlatformVersionSubKey = "masterToPlatform" | "versions";
type VideoSubKey = "versions" | "projects";
type AssetSubKey = "assets" | "knowledge";
type SettingsSubKey =
  | "status"
  | "attachments"
  | "users"
  | "stats"
  | "ai"
  | "aiModels"
  | "aiPrompts";
type DashboardSubKey = "home" | "guide";

type BaseRow = { id: string; createdAt: string };
type BusinessLine = BaseRow & {
  name: string;
  description: string;
  enabled: boolean;
};
type Platform = BaseRow & {
  name: string;
  code: string;
  logoUrl?: string;
  ratio: string;
  pages: number;
  publishMode: "manual" | "draft" | "api";
  enabled: boolean;
};
type AccountBindMode = "manual" | "oauth" | "qr" | "cookie" | "api";
type AccountBindStatus = "unbound" | "pending" | "bound" | "expired";
type MediaAccount = BaseRow & {
  platformId: string;
  name: string;
  businessLineIds: string[];
  persona: string;
  style: string;
  targetCustomer: string;
  cta: string;
  frequency: string;
  bindMode?: AccountBindMode;
  bindStatus?: AccountBindStatus;
  authSubject?: string;
  credentialRef?: string;
  tokenExpiresAt?: string;
  enabled: boolean;
};
type Topic = BaseRow & {
  title: string;
  businessLineId: string;
  contentType: string;
  keywords: string;
  priority: "low" | "medium" | "high";
  status: "pending" | "approved" | "converted";
};
type ContentTask = BaseRow & {
  title: string;
  topicId: string;
  businessLineId: string;
  contentType: string;
  status: string;
  targetPlatformIds: string[];
  targetAccountIds: string[];
  direction: string;
  outline: string;
  dueDate: string;
};
type MasterArticle = BaseRow & {
  taskId: string;
  title: string;
  outline: string;
  body: string;
  status: string;
  aiGenerated: boolean;
};
type PlatformVersion = BaseRow & {
  taskId: string;
  masterArticleId: string;
  platformId: string;
  title: string;
  body: string;
  summary: string;
  tags: string;
  status: string;
};
type AccountVersion = BaseRow & {
  platformVersionId: string;
  accountId: string;
  title: string;
  body: string;
  coverText: string;
  hashtags: string;
  cta: string;
  status: string;
  similarityScore: number;
};
type PromptTemplate = BaseRow & {
  name: string;
  scene: string;
  promptBody: string;
  version: string;
  enabled: boolean;
};
type AiRewritePrompt = BaseRow & {
  sequence: number;
  category: string;
  name: string;
  prompt: string;
  description: string;
  enabled: boolean;
};
type AiModel = BaseRow & {
  provider: string;
  name: string;
  code: string;
  taskType: string;
  baseUrl?: string;
  apiKeyName?: string;
  apiKey?: string;
  notes?: string;
  enabled: boolean;
};
type AiLog = BaseRow & {
  taskId: string;
  objectType: string;
  input: string;
  output: string;
  cost: number;
  status: string;
};
type PublishPlan = BaseRow & {
  accountVersionId: string;
  accountId: string;
  platformId: string;
  scheduledAt: string;
  actualPublishedAt: string;
  status: string;
  publishUrl: string;
  remark: string;
};
type CollectionSourceType =
  | "rss"
  | "atom"
  | "rdf"
  | "api"
  | "website"
  | "wechat";
type CollectionSource = BaseRow & {
  name: string;
  category: string;
  sourceType: CollectionSourceType;
  url: string;
  language: string;
  enabled: boolean;
  lastCollectedAt: string;
  failureLog: string;
  notes: string;
};
type CollectionRule = BaseRow & {
  name: string;
  mode: "keyword" | "all";
  keywords: string;
  excludeKeywords: string;
  businessLineId: string;
  contentType: string;
  autoAi: boolean;
  autoTopic: boolean;
  enabled: boolean;
};
type CollectionTask = BaseRow & {
  name: string;
  sourceId: string;
  ruleId: string;
  schedule: "manual" | "hourly" | "daily" | "weekly";
  enabled: boolean;
  status: string;
  lastRunAt: string;
  lastResult: string;
};
type CollectedItem = BaseRow & {
  sourceId?: string;
  title: string;
  url: string;
  author?: string;
  publishedAt?: string;
  language?: string;
  translationStatus?: "none" | "pending" | "translated" | "failed";
  translatedTitle?: string;
  translatedContent?: string;
  rawContent: string;
  summary: string;
  keywords: string;
  status: string;
};
type MaterialAsset = BaseRow & {
  name: string;
  type: string;
  path: string;
  tags: string;
  businessLineId: string;
  platformId: string;
  mimeType: string;
  size: number;
  storageKey: string;
  status: string;
  contentHash: string;
  duplicateStatus: string;
  duplicateNotes: string;
  archiveDir: string;
  fileName: string;
};
type ImageTextProject = BaseRow & {
  accountVersionId: string;
  title: string;
  ratio: string;
  pages: string[];
  imagePrompts: string[];
  status: string;
  coverTitle?: string;
  publishTitle?: string;
  publishContent?: string;
  designStyle?: string;
  assetIds?: string[];
  generatedImagePaths?: string[];
  parentProjectId?: string;
  variantIndex?: number;
  variantBatchId?: string;
  imageHashes?: string[];
  imageArchivePaths?: string[];
  variantSettings?: string;
};
type VideoProject = BaseRow & {
  accountVersionId: string;
  title: string;
  script: string;
  storyboard: string;
  subtitles: string;
  voice: string;
  status: string;
  duration?: string;
  aspectRatio?: string;
  editNotes?: string;
  assetIds?: string[];
};
type MetricRecord = BaseRow & {
  publishPlanId: string;
  views: number;
  likes: number;
  favorites: number;
  comments: number;
  shares: number;
  followersGain: number;
};
type AsyncJob = BaseRow & { type: string; status: string; payload: string };
type ExportPackage = BaseRow & {
  publishPlanId: string;
  accountVersionId: string;
  accountId: string;
  platformId: string;
  title: string;
  markdown: string;
  text: string;
  json: string;
  status: string;
  archiveDir: string;
  fileName: string;
};
type Role = BaseRow & {
  name: string;
  code: string;
  description: string;
  permissions: string[];
  enabled: boolean;
};
type User = BaseRow & {
  name: string;
  email: string;
  password: string;
  roleId: string;
  enabled: boolean;
  lastLoginAt: string;
};
type AppSettings = {
  attachmentRoot: string;
  knowledgeRoot: string;
  cloudSyncPrefix: string;
  archiveMode: "local" | "cloud" | "hybrid";
};

type Store = {
  businessLines: BusinessLine[];
  platforms: Platform[];
  accounts: MediaAccount[];
  topics: Topic[];
  tasks: ContentTask[];
  masterArticles: MasterArticle[];
  platformVersions: PlatformVersion[];
  accountVersions: AccountVersion[];
  promptTemplates: PromptTemplate[];
  aiRewritePrompts: AiRewritePrompt[];
  aiModels: AiModel[];
  aiLogs: AiLog[];
  publishPlans: PublishPlan[];
  collectionSources: CollectionSource[];
  collectionRules: CollectionRule[];
  collectionTasks: CollectionTask[];
  collectedItems: CollectedItem[];
  assets: MaterialAsset[];
  imageTextProjects: ImageTextProject[];
  videoProjects: VideoProject[];
  metrics: MetricRecord[];
  jobs: AsyncJob[];
  exportPackages: ExportPackage[];
  roles: Role[];
  users: User[];
  appSettings: AppSettings;
};
const navItems: Array<{
  key: NavKey;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
}> = [
  { key: "dashboard", label: "工作台", icon: LayoutDashboard },
  { key: "collection", label: "采集中心", icon: Database },
  { key: "topics", label: "选题池", icon: Sparkles },
  { key: "tasks", label: "内容任务", icon: ClipboardList },
  { key: "master", label: "母版文章", icon: FileText },
  { key: "platformVersions", label: "平台版本", icon: Share2 },
  { key: "imageText", label: "图文生成", icon: Images },
  { key: "video", label: "视频生成", icon: PlaySquare },
  { key: "assets", label: "素材内容", icon: Library },
  { key: "publishing", label: "发布排期", icon: CalendarDays },
  { key: "analytics", label: "数据复盘", icon: BarChart3 },
  { key: "accounts", label: "账号矩阵", icon: Users },
  { key: "settings", label: "系统设置", icon: Settings },
];
type PermissionAction =
  | "view"
  | "create"
  | "edit"
  | "delete"
  | "run"
  | "export"
  | "manage";
const permissionActions: Array<{ key: PermissionAction; label: string }> = [
  { key: "view", label: "查看" },
  { key: "create", label: "新增" },
  { key: "edit", label: "修改" },
  { key: "delete", label: "删除" },
  { key: "run", label: "执行" },
  { key: "export", label: "导出" },
  { key: "manage", label: "管理" },
];
const permissionModules: Array<{
  key: NavKey;
  label: string;
  actions: PermissionAction[];
}> = [
  { key: "dashboard", label: "工作台", actions: ["view"] },
  { key: "collection", label: "采集中心", actions: ["view", "create", "edit", "delete", "run"] },
  { key: "topics", label: "选题池", actions: ["view", "create", "edit", "delete"] },
  { key: "tasks", label: "内容任务", actions: ["view", "create", "edit", "delete", "run"] },
  { key: "master", label: "母版文章", actions: ["view", "create", "edit", "delete", "run"] },
  { key: "platformVersions", label: "平台版本", actions: ["view", "create", "edit", "delete", "run"] },
  { key: "imageText", label: "图文生成", actions: ["view", "create", "edit", "delete", "run", "export"] },
  { key: "video", label: "视频生成", actions: ["view", "create", "edit", "delete", "run", "export"] },
  { key: "assets", label: "素材内容", actions: ["view", "create", "edit", "delete", "export"] },
  { key: "publishing", label: "发布排期", actions: ["view", "create", "edit", "delete", "export"] },
  { key: "analytics", label: "数据复盘", actions: ["view", "create", "edit", "delete", "export"] },
  { key: "accounts", label: "账号矩阵", actions: ["view", "create", "edit", "delete", "manage"] },
  { key: "settings", label: "系统设置", actions: ["view", "create", "edit", "delete", "manage"] },
];
const collectionSubItems: Array<{ key: CollectionSubKey; label: string }> = [
  { key: "new", label: "新增采集" },
  { key: "history", label: "历史文件导入" },
  { key: "list", label: "采集列表" },
];
const collectionExtendedSubItems: Array<{
  key: CollectionSubKey;
  label: string;
}> = [
  ...collectionSubItems.slice(0, 2),
  { key: "sources", label: "采集源管理" },
  { key: "rules", label: "采集规则" },
  { key: "tasks", label: "采集任务" },
  collectionSubItems[2],
];

const accountSubItems: Array<{ key: AccountSubKey; label: string }> = [
  { key: "platforms", label: "平台管理" },
  { key: "accounts", label: "账号矩阵" },
];
const masterSubItems: Array<{ key: MasterSubKey; label: string }> = [
  { key: "new", label: "新增母版文章" },
  { key: "generate", label: "从任务生成母版" },
  { key: "list", label: "母版文章列表" },
];
const imageTextSubItems: Array<{ key: ImageTextSubKey; label: string }> = [
  { key: "versions", label: "账号版本转图文" },
  { key: "projects", label: "图文项目" },
];
const taskSubItems: Array<{ key: TaskSubKey; label: string }> = [
  { key: "new", label: "新建内容任务" },
  { key: "list", label: "内容任务列表" },
];
const topicSubItems: Array<{ key: TopicSubKey; label: string }> = [
  { key: "new", label: "新建选题" },
  { key: "list", label: "选题列表" },
];
const platformVersionSubItems: Array<{
  key: PlatformVersionSubKey;
  label: string;
}> = [
  { key: "masterToPlatform", label: "母版转平台版本" },
  { key: "versions", label: "平台版本与账号版本" },
];
const videoSubItems: Array<{ key: VideoSubKey; label: string }> = [
  { key: "versions", label: "账号版本转视频" },
  { key: "projects", label: "视频工程" },
];
const assetSubItems: Array<{ key: AssetSubKey; label: string }> = [
  { key: "assets", label: "素材库" },
  { key: "knowledge", label: "知识库" },
];
const settingsSubItems: Array<{ key: SettingsSubKey; label: string }> = [
  { key: "status", label: "系统状态" },
  { key: "attachments", label: "附件存放与同步" },
  { key: "users", label: "用户与角色" },
  { key: "stats", label: "数据统计" },
  { key: "ai", label: "AI配置" },
  { key: "aiModels", label: "AI模型列表" },
  { key: "aiPrompts", label: "AI提示词" },
];
const dashboardSubItems: Array<{ key: DashboardSubKey; label: string }> = [
  { key: "home", label: "工作台" },
  { key: "guide", label: "系统使用文档" },
];

function defaultRoles(): Role[] {
  return [
    {
      id: "role_super_admin",
      name: "超级管理员",
      code: "super_admin",
      description: "全部权限",
      permissions: ["*"],
      enabled: true,
      createdAt: now(),
    },
    {
      id: "role_ops_admin",
      name: "运营管理员",
      code: "ops_admin",
      description: "除系统高危配置外的运营全流程权限",
      permissions: [
        "dashboard:view",
        "collection:*",
        "topics:*",
        "tasks:*",
        "master:*",
        "platformVersions:*",
        "imageText:*",
        "video:*",
        "assets:*",
        "publishing:*",
        "analytics:*",
        "accounts:view",
        "accounts:manage",
        "settings:view",
      ],
      enabled: true,
      createdAt: now(),
    },
    {
      id: "role_editor",
      name: "内容编辑",
      code: "editor",
      description: "负责选题、任务、文章和内容版本制作",
      permissions: [
        "dashboard:view",
        "collection:view",
        "topics:*",
        "tasks:*",
        "master:*",
        "platformVersions:*",
        "imageText:*",
        "video:*",
        "assets:view",
      ],
      enabled: true,
      createdAt: now(),
    },
    {
      id: "role_publisher",
      name: "发布运营",
      code: "publisher",
      description: "负责发布排期、发布包导出和数据回填",
      permissions: [
        "dashboard:view",
        "imageText:view",
        "video:view",
        "publishing:*",
        "analytics:*",
      ],
      enabled: true,
      createdAt: now(),
    },
    {
      id: "role_viewer",
      name: "数据查看员",
      code: "viewer",
      description: "主要模块只读",
      permissions: permissionModules.map((module) => `${module.key}:view`),
      enabled: true,
      createdAt: now(),
    },
  ];
}

const contentTypes = [
  "政策解读",
  "风险提醒",
  "案例复盘",
  "物流知识",
  "清关知识",
  "税费计算",
  "服务介绍",
  "行业新闻",
  "招商/获客内容",
];
const collectionLanguages = [
  { code: "auto", name: "自动识别" },
  { code: "zh-CN", name: "中文" },
  { code: "en", name: "英文" },
  { code: "es", name: "西班牙语" },
  { code: "pt", name: "葡萄牙语" },
  { code: "fr", name: "法语" },
  { code: "ru", name: "俄语" },
  { code: "ar", name: "阿拉伯语" },
  { code: "other", name: "其他" },
];
const defaultAiModels: AiModel[] = [
  {
    id: "model_qwen_plus",
    provider: "阿里云百炼 / 通义千问",
    name: "通义千问 Plus",
    code: "qwen-plus",
    taskType: "内容生成",
    baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1",
    apiKeyName: "DASHSCOPE_API_KEY",
    apiKey: "",
    notes: "适合中文内容生产、改写、选题提炼。",
    enabled: true,
    createdAt: now(),
  },
  {
    id: "model_qwen_turbo",
    provider: "阿里云百炼 / 通义千问",
    name: "通义千问 Turbo",
    code: "qwen-turbo",
    taskType: "改写",
    baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1",
    apiKeyName: "DASHSCOPE_API_KEY",
    apiKey: "",
    notes: "适合低成本批量改写、摘要和关键词提取。",
    enabled: true,
    createdAt: now(),
  },
  {
    id: "model_doubao_pro",
    provider: "火山引擎 / 豆包",
    name: "豆包 Pro",
    code: "doubao-seed-2-0-pro-260215",
    taskType: "内容生成",
    baseUrl: "https://ark.cn-beijing.volces.com/api/v3",
    apiKeyName: "ARK_API_KEY",
    apiKey: "",
    notes: "使用火山方舟 Responses API。模型代码可按官方控制台展示值修改。",
    enabled: true,
    createdAt: now(),
  },
  {
    id: "model_kimi_k2",
    provider: "Moonshot / Kimi",
    name: "Kimi K2",
    code: "kimi-k2-0711-preview",
    taskType: "长文分析",
    baseUrl: "https://api.moonshot.cn/v1",
    apiKeyName: "MOONSHOT_API_KEY",
    apiKey: "",
    notes: "适合长文档分析、采集内容总结、母版文章生成。",
    enabled: true,
    createdAt: now(),
  },
  {
    id: "model_hunyuan_turbo",
    provider: "腾讯云 / 混元",
    name: "混元 Turbo",
    code: "hunyuan-turbo",
    taskType: "内容生成",
    baseUrl: "https://api.hunyuan.cloud.tencent.com/v1",
    apiKeyName: "TENCENT_HUNYUAN_API_KEY",
    apiKey: "",
    notes: "可按腾讯云实际接口配置。",
    enabled: true,
    createdAt: now(),
  },
  {
    id: "model_deepseek_chat",
    provider: "DeepSeek",
    name: "DeepSeek Chat",
    code: "deepseek-chat",
    taskType: "改写",
    baseUrl: "https://api.deepseek.com",
    apiKeyName: "DEEPSEEK_API_KEY",
    apiKey: "",
    notes: "适合结构化改写、低成本批量生成。",
    enabled: true,
    createdAt: now(),
  },
  {
    id: "model_zhipu_glm",
    provider: "智谱 AI",
    name: "GLM-4",
    code: "glm-4-plus",
    taskType: "内容生成",
    baseUrl: "https://open.bigmodel.cn/api/paas/v4",
    apiKeyName: "ZHIPU_API_KEY",
    apiKey: "",
    notes: "适合中文内容生成、标题优化和审核。",
    enabled: true,
    createdAt: now(),
  },
  {
    id: "model_baidu_ernie",
    provider: "百度智能云 / 千帆",
    name: "文心 ERNIE",
    code: "ernie-4.0-turbo-8k",
    taskType: "内容生成",
    baseUrl: "https://qianfan.baidubce.com/v2",
    apiKeyName: "QIANFAN_API_KEY",
    apiKey: "",
    notes: "可按千帆实际接口配置。",
    enabled: true,
    createdAt: now(),
  },
  {
    id: "model_openai",
    provider: "OpenAI",
    name: "GPT 内容生产",
    code: "gpt-4.1",
    taskType: "内容生成",
    baseUrl: "https://api.openai.com/v1",
    apiKeyName: "OPENAI_API_KEY",
    apiKey: "",
    notes: "备用国际模型配置。",
    enabled: true,
    createdAt: now(),
  },
];
const defaultAiRewritePrompts: AiRewritePrompt[] = [
  {
    id: "rewrite_news_wechat",
    sequence: 1,
    category: "新闻洗稿",
    name: "公众号新闻深度改写",
    prompt:
      "请基于原文重新生成一篇适合公众号发布的中文文章。保留事实、数据、机构名称和核心观点，但更换表达方式、段落结构和标题。文章需要有清晰标题、导语、小标题、风险/机会解读和结尾行动建议，避免照搬原文句子。",
    description: "适合新闻、政策动态、行业资讯洗稿成公众号长文。",
    enabled: true,
    createdAt: now(),
  },
  {
    id: "rewrite_knowledge_wechat",
    sequence: 2,
    category: "知识库改写",
    name: "知识库转公众号科普",
    prompt:
      "请把原文整理为一篇适合公众号发布的知识型文章。要求逻辑清晰、语言通俗、结构完整，包含问题背景、核心知识点、常见误区、实操建议和总结。表达内容不变，但不能直接复制原文句式。",
    description: "适合清关、税费、贸易规则等知识库内容。",
    enabled: true,
    createdAt: now(),
  },
  {
    id: "rewrite_risk_alert",
    sequence: 3,
    category: "风险提醒",
    name: "风险提醒型公众号文章",
    prompt:
      "请把原文改写成一篇风险提醒型公众号文章。突出风险信号、影响对象、可能后果、应对建议和检查清单。语气专业克制，不夸大，不虚构事实，适合外贸企业负责人阅读。",
    description: "适合政策变化、海关监管、贸易摩擦等风险内容。",
    enabled: true,
    createdAt: now(),
  },
];
const storageKey = "mediapilotx:mvp-store:v2";

function uid(prefix: string) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function now() {
  return new Date().toISOString();
}

const defaultCollectionKeywords = [
  "customs",
  "trade",
  "tariff",
  "import",
  "export",
  "duty",
  "border",
  "cargo",
  "freight",
  "clearance",
  "anti-dumping",
  "countervailing",
  "safeguard",
  "section 232",
  "reciprocal tariff",
  "steel",
  "aluminum",
  "pharmaceutical",
  "customs clearance",
  "customs duty",
  "import duty",
  "export control",
  "sanctions",
  "supply chain",
  "port",
  "hs code",
  "customs broker",
  "inspection",
  "compliance",
  "logistics",
  "warehouse",
].join(", ");

const defaultCollectionSourceSeed: Array<
  Omit<
    CollectionSource,
    "createdAt" | "lastCollectedAt" | "failureLog" | "language"
  >
> = [
  {
    id: "cs_ustr",
    name: "USTR — 美国贸易代表署",
    category: "贸易政策",
    sourceType: "rss",
    url: "https://ustr.gov/rss.xml",
    enabled: true,
    notes: "美国贸易政策 RSS。",
  },
  {
    id: "cs_fr_customs_tariff",
    name: "Federal Register — 关税/海关法规",
    category: "海关法规",
    sourceType: "rss",
    url: "https://www.federalregister.gov/documents/search.rss?conditions[term]=customs+tariff&conditions[type][]=RULE&conditions[type][]=PROPOSED_RULE",
    enabled: true,
    notes: "美国联邦公告关税与海关规则。",
  },
  {
    id: "cs_fr_import_export",
    name: "Federal Register — 进出口规则",
    category: "海关法规",
    sourceType: "rss",
    url: "https://www.federalregister.gov/documents/search.rss?conditions[term]=import+export+duty&conditions[type][]=RULE",
    enabled: true,
    notes: "美国进出口规则。",
  },
  {
    id: "cs_whitehouse_fact_sheets",
    name: "The White House — Fact Sheets",
    category: "贸易政策",
    sourceType: "rss",
    url: "https://www.whitehouse.gov/fact-sheets/feed/",
    enabled: true,
    notes: "白宫事实清单。",
  },
  {
    id: "cs_wto_news",
    name: "WTO — 世界贸易组织新闻",
    category: "国际贸易",
    sourceType: "rss",
    url: "https://www.wto.org/rss/english/news_e.xml",
    enabled: true,
    notes: "WTO 新闻。",
  },
  {
    id: "cs_wto_press",
    name: "WTO — 世界贸易组织新闻稿",
    category: "国际贸易",
    sourceType: "rss",
    url: "https://www.wto.org/rss/english/pressrelease_e.xml",
    enabled: true,
    notes: "WTO 新闻稿。",
  },
];

function seedStore(): Store {
  const businessLines: BusinessLine[] = [
    { id: "bl_us_ddp", name: "美国DDP清关", description: "美国 DDP、关税、清关风险内容", enabled: true, createdAt: now() },
    { id: "bl_central_asia", name: "中亚运输", description: "中亚整车、拼车、大件运输", enabled: true, createdAt: now() },
  ];
  const platforms: Platform[] = [
    { id: "pf_wechat", name: "公众号", code: "wechat", ratio: "2.35:1", pages: 2, publishMode: "manual", enabled: true, createdAt: now() },
    { id: "pf_xhs", name: "小红书", code: "xiaohongshu", ratio: "3:4", pages: 6, publishMode: "manual", enabled: true, logoUrl: "/demo/image-text/foreign-trade-phrases-1.png", createdAt: now() },
    { id: "pf_video", name: "视频号", code: "shipinhao", ratio: "9:16", pages: 1, publishMode: "manual", enabled: true, createdAt: now() },
  ];
  const accounts: MediaAccount[] = [
    { id: "acc_xhs_a", platformId: "pf_xhs", name: "小红书-清关顾问A", businessLineIds: ["bl_us_ddp"], persona: "专业清关顾问", style: "实用、清单式、口语化", targetCustomer: "跨境电商卖家", cta: "需要核算清关成本可以私信", frequency: "每周3篇", enabled: true, createdAt: now() },
    { id: "acc_wechat_main", platformId: "pf_wechat", name: "公众号-国际物流观察", businessLineIds: ["bl_us_ddp", "bl_central_asia"], persona: "行业分析型编辑", style: "深度、结构化、案例充分", targetCustomer: "外贸企业负责人", cta: "欢迎留言咨询路线方案", frequency: "每周2篇", enabled: true, createdAt: now() },
  ];
  const topics: Topic[] = [
    { id: "topic_1", title: "美国DDP清关旺季风险提醒", businessLineId: "bl_us_ddp", contentType: "风险提醒", keywords: "DDP,清关,旺季,查验", priority: "high", status: "approved", createdAt: now() },
  ];
  const tasks: ContentTask[] = [
    { id: "task_1", title: "美国DDP清关旺季风险提醒", topicId: "topic_1", businessLineId: "bl_us_ddp", contentType: "风险提醒", status: "待生成平台版本", targetPlatformIds: ["pf_wechat", "pf_xhs"], targetAccountIds: ["acc_xhs_a", "acc_wechat_main"], direction: "强调旺季查验、资料不全、申报不一致的风险。", outline: "1. 旺季为什么容易延误\n2. 高频风险\n3. 发货前检查清单\n4. 咨询引导", dueDate: "2026-06-07", createdAt: now() },
  ];
  const masterArticles: MasterArticle[] = [
    { id: "ma_1", taskId: "task_1", title: "美国DDP清关旺季风险提醒", outline: tasks[0].outline, body: makeMasterArticle(tasks[0]), status: "待审核", aiGenerated: true, createdAt: now() },
  ];
  return {
    businessLines,
    platforms,
    accounts,
    topics,
    tasks,
    masterArticles,
    platformVersions: [],
    accountVersions: [],
    promptTemplates: [],
    aiRewritePrompts: defaultAiRewritePrompts.map((prompt) => ({ ...prompt })),
    aiModels: defaultAiModels.map((model) => ({ ...model })),
    aiLogs: [],
    publishPlans: [],
    collectionSources: defaultCollectionSourceSeed.map((source) => ({ ...source, language: inferCollectionLanguage(source), lastCollectedAt: "", failureLog: "", createdAt: now() })),
    collectionRules: [{ id: "cr_trade_keywords", name: "贸易/海关关键词保留", mode: "keyword", keywords: defaultCollectionKeywords, excludeKeywords: "", businessLineId: "bl_us_ddp", contentType: "行业新闻", autoAi: false, autoTopic: false, enabled: true, createdAt: now() }],
    collectionTasks: defaultCollectionSourceSeed.map((source) => ({ id: `ct_${source.id.replace(/^cs_/, "")}`, name: `${source.name} 手动采集`, sourceId: source.id, ruleId: "cr_trade_keywords", schedule: "manual", enabled: false, status: source.enabled ? "待启动" : "源已停用", lastRunAt: "", lastResult: source.notes, createdAt: now() })),
    collectedItems: [],
    assets: demoGeneratedImageAssets(),
    imageTextProjects: [demoGeneratedImageTextProject()],
    videoProjects: [],
    metrics: [],
    jobs: [],
    exportPackages: [],
    roles: defaultRoles(),
    users: [
      { id: "user_admin", name: "老板", email: "admin@mediapilotx.local", password: "md888888", roleId: "role_super_admin", enabled: true, lastLoginAt: now(), createdAt: now() },
    ],
    appSettings: {
      attachmentRoot: "D:\\MediaPilotX\\attachments",
      knowledgeRoot: "D:\\MediaPilotX\\knowledge",
      cloudSyncPrefix: "r2://mediapilotx-media/attachments",
      archiveMode: "hybrid",
    },
  };
}

function loadStore(): Store {
  const raw = localStorage.getItem(storageKey);
  if (!raw) return seedStore();
  try {
    return normalizeStore({ ...seedStore(), ...JSON.parse(raw) } as Store);
  } catch {
    return seedStore();
  }
}

function demoGeneratedImageTextProject(): ImageTextProject {
  const pages = [
    "客户问：你们这个价格还能便宜吗？",
    "可以这样回：我先帮您确认成本结构。",
    "不要只说最低价，要先讲清楚服务边界。",
    "把报价拆成运输、清关、税费和派送。",
    "客户比较价格时，重点说明风险差异。",
    "最后给出下一步动作：发资料，我帮您核算。",
  ];
  return {
    id: "demo_generated_image_text_project",
    accountVersionId: "demo_account_version",
    title: "外贸人常用话术 | 小红书图文成品示例",
    ratio: "3:4",
    pages,
    imagePrompts: pages.map(
      (page, index) =>
        `小红书外贸知识卡片，第 ${index + 1} 页，主题：${page}，清晰中文排版，商务绿色点缀`,
    ),
    status: "图片已生成",
    coverTitle: "外贸人常用话术",
    publishTitle: "外贸人常用话术：客户压价时这样回",
    publishContent:
      "客户一上来就问还能不能便宜，千万别急着降价。\n\n更稳的回复方式是：先确认货物信息、路线、清关方式和派送要求，再把报价拆成运输、清关、税费和末端派送几部分。\n\n这样客户看到的不是一个单纯价格，而是一套风险可控的方案。\n\n#外贸话术 #跨境物流 #DDP清关 #货代日常 #外贸沟通",
    designStyle: "小红书知识卡片，强标题，清单式排版，适合外贸和跨境物流账号",
    assetIds: [1, 2, 3, 4, 5, 6].map(
      (index) => `demo_foreign_trade_phrase_image_${index}`,
    ),
    generatedImagePaths: [1, 2, 3, 4, 5, 6].map(
      (index) => `/demo/image-text/foreign-trade-phrases-${index}.png`,
    ),
    createdAt: now(),
  };
}

function demoGeneratedImageAssets(): MaterialAsset[] {
  const sizes = [2004588, 1986596, 1948062, 1897029, 2084292, 1906822];
  const archiveDir = "image-text\\2026-05-23\\foreign-trade-phrases";
  return sizes.map((size, index) => {
    const page = index + 1;
    const fileName = `foreign-trade-phrases-${page}.png`;
    return {
      id: `demo_foreign_trade_phrase_image_${page}`,
      name: `外贸人常用话术成图 ${page}`,
      type: "图片",
      path: "",
      tags: "外贸话术,小红书图文,成品图",
      businessLineId: "bl_us_ddp",
      platformId: "pf_xhs",
      mimeType: "image/png",
      size,
      storageKey: "",
      status: "local",
      contentHash: "",
      duplicateStatus: "clear",
      duplicateNotes: "",
      archiveDir,
      fileName,
      createdAt: now(),
    };
  });
}

function normalizeStore(store: Store): Store {
  const next = { ...seedStore(), ...store } as Store;
  const existingSourceIds = new Set((next.collectionSources || []).map((item) => item.id));
  const missingSources = defaultCollectionSourceSeed
    .filter((source) => !existingSourceIds.has(source.id))
    .map((source) => ({
      ...source,
      language: inferCollectionLanguage(source),
      lastCollectedAt: "",
      failureLog: "",
      createdAt: now(),
    }));
  next.collectionSources = [...(next.collectionSources || []), ...missingSources];

  if (!next.collectionRules?.length) {
    next.collectionRules = [
      {
        id: "cr_trade_keywords",
        name: "贸易/海关关键词保留",
        mode: "keyword",
        keywords: defaultCollectionKeywords,
        excludeKeywords: "",
        businessLineId: next.businessLines[0]?.id || "",
        contentType: "行业新闻",
        autoAi: false,
        autoTopic: false,
        enabled: true,
        createdAt: now(),
      },
    ];
  }

  next.collectionTasks = next.collectionTasks || [];
  const existingTaskSourceIds = new Set(next.collectionTasks.map((item) => item.sourceId));
  const defaultRuleId = next.collectionRules[0]?.id || "cr_trade_keywords";
  next.collectionTasks = [
    ...next.collectionTasks,
    ...defaultCollectionSourceSeed
      .filter((source) => !existingTaskSourceIds.has(source.id))
      .map((source) => ({
        id: `ct_${source.id.replace(/^cs_/, "")}`,
        name: `${source.name} 手动采集`,
        sourceId: source.id,
        ruleId: defaultRuleId,
        schedule: "manual" as const,
        enabled: false,
        status: source.enabled ? "待启动" : "源已停用",
        lastRunAt: "",
        lastResult: source.notes,
        createdAt: now(),
      })),
  ];

  const existingModelIds = new Set((next.aiModels || []).map((item) => item.id));
  next.aiModels = [
    ...(next.aiModels || []),
    ...defaultAiModels
      .filter((model) => !existingModelIds.has(model.id))
      .map((model) => ({ ...model })),
  ];
  const existingRewritePromptIds = new Set(
    (next.aiRewritePrompts || []).map((item) => item.id),
  );
  next.aiRewritePrompts = [
    ...(next.aiRewritePrompts || []),
    ...defaultAiRewritePrompts
      .filter((prompt) => !existingRewritePromptIds.has(prompt.id))
      .map((prompt) => ({ ...prompt })),
  ].sort((a, b) => a.sequence - b.sequence);

  const existingRoleIds = new Set((next.roles || []).map((role) => role.id));
  next.roles = [
    ...(next.roles || []).map((role) => ({
      ...role,
      permissions: normalizePermissions(role.permissions || []),
    })),
    ...defaultRoles().filter((role) => !existingRoleIds.has(role.id)),
  ];
  next.users = (next.users || []).map((user) => ({
    ...user,
    password:
      user.password ||
      (user.email.toLowerCase() === "admin@mediapilotx.local"
        ? "md888888"
        : ""),
  }));
  next.appSettings = {
    ...seedStore().appSettings,
    ...(next.appSettings || {}),
    knowledgeRoot:
      next.appSettings?.knowledgeRoot ||
      "D:\\MediaPilotX\\knowledge",
  };

  next.assets = next.assets || [];
  for (const asset of demoGeneratedImageAssets()) {
    if (!next.assets.some((item) => item.id === asset.id)) next.assets.push(asset);
  }

  next.imageTextProjects = next.imageTextProjects || [];
  const demo = demoGeneratedImageTextProject();
  const demoIndex = next.imageTextProjects.findIndex((project) => project.id === demo.id);
  if (demoIndex === -1) {
    next.imageTextProjects.unshift(demo);
  } else {
    next.imageTextProjects[demoIndex] = {
      ...next.imageTextProjects[demoIndex],
      publishTitle: next.imageTextProjects[demoIndex].publishTitle || demo.publishTitle,
      publishContent: next.imageTextProjects[demoIndex].publishContent || demo.publishContent,
      assetIds: next.imageTextProjects[demoIndex].assetIds?.length
        ? next.imageTextProjects[demoIndex].assetIds
        : demo.assetIds,
      generatedImagePaths: next.imageTextProjects[demoIndex].generatedImagePaths?.length
        ? next.imageTextProjects[demoIndex].generatedImagePaths
        : demo.generatedImagePaths,
    };
  }
  next.imageTextProjects = next.imageTextProjects.map((project) => ({
    ...project,
    generatedImagePaths:
      project.parentProjectId && project.generatedImagePaths?.some((path) => path.startsWith("data:image/"))
        ? project.generatedImagePaths.map((path, index) =>
            path.startsWith("data:image/")
              ? buildVariantAttachmentPath(
                  next.appSettings,
                  project,
                  project.variantIndex || 1,
                  index + 1,
                )
              : path,
          )
        : project.generatedImagePaths,
    status: repairStatusText(project.status),
  }));
  next.videoProjects = (next.videoProjects || []).map((project) => ({
    ...project,
    status: repairStatusText(project.status),
  }));
  next.publishPlans = (next.publishPlans || []).map((plan) => ({
    ...plan,
    status: repairStatusText(plan.status),
  }));
  return next;
}

function repairStatusText(value = "") {
  const statusMap: Record<string, string> = {
    "寰呯敓鎴愬浘鐗": "待生成图片",
    "鍥剧墖宸茬敓鎴": "图片已生成",
    "鍥剧墖鍒朵綔涓": "图片制作中",
    "鑴氭湰宸茬敓鎴": "脚本已生成",
    "寰呭彂甯": "待发布",
    "宸插彂甯": "已发布",
    "宸茶鍒": "已计划",
    "鍒朵綔涓": "制作中",
  };
  return statusMap[value] || value;
}

function normalizePermissions(permissions: string[]) {
  const aliases: Record<string, string> = {
    "content_tasks:*": "tasks:*",
    "content_tasks:view": "tasks:view",
    "content_tasks:create": "tasks:create",
    "content_tasks:edit": "tasks:edit",
    "content_tasks:delete": "tasks:delete",
    "content_tasks:run": "tasks:run",
  };
  return [...new Set(permissions.map((permission) => aliases[permission] || permission))];
}

function roleHasPermission(
  role: Role | undefined,
  moduleKey: NavKey,
  action: PermissionAction,
) {
  if (!role?.enabled) return false;
  const permissions = normalizePermissions(role.permissions || []);
  return (
    permissions.includes("*") ||
    permissions.includes(`${moduleKey}:*`) ||
    permissions.includes(`${moduleKey}:${action}`) ||
    (action !== "view" && permissions.includes(`${moduleKey}:manage`))
  );
}

function mergeRemoteStore(current: Store, remote: RemoteBootstrap): Store {
  const next = structuredClone(current) as Store;
  next.businessLines = mapRows(
    remote.businessLines,
    (row) => ({
      id: text(row.id),
      name: text(row.name),
      description: text(row.description),
      enabled: bool(row.enabled),
      createdAt: text(row.created_at),
    }),
    next.businessLines,
  );
  next.platforms = mapRows(
    remote.platforms,
    (row) => ({
      id: text(row.id),
      name: text(row.name),
      code: text(row.code),
      logoUrl: text(row.logo_url),
      ratio: text(row.default_image_ratio || row.ratio || "3:4"),
      pages: num(row.default_image_pages || row.pages || 1),
      publishMode: text(
        row.publish_mode || "manual",
      ) as Platform["publishMode"],
      enabled: bool(row.enabled),
      createdAt: text(row.created_at),
    }),
    next.platforms,
  );
  next.accounts = mapRows(
    remote.accounts,
    (row) => ({
      id: text(row.id),
      platformId: text(row.platform_id),
      name: text(row.name),
      businessLineIds: jsonArray(row.business_line_ids),
      persona: text(row.persona),
      style: text(row.content_style),
      targetCustomer: text(row.target_customer),
      cta: text(row.common_cta),
      frequency: text(row.publish_frequency),
      bindMode: bindMode(row.bind_mode),
      bindStatus: bindStatus(row.bind_status),
      authSubject: text(row.auth_subject),
      credentialRef: text(row.credential_ref),
      tokenExpiresAt: text(row.token_expires_at),
      enabled: bool(row.enabled),
      createdAt: text(row.created_at),
    }),
    next.accounts,
  );
  next.topics = mapRows(
    remote.topics,
    (row) => ({
      id: text(row.id),
      title: text(row.title),
      businessLineId: text(row.business_line_id),
      contentType: text(row.content_type),
      keywords: jsonArray(row.keywords).join(",") || text(row.keywords),
      priority: text(row.priority || "medium") as Topic["priority"],
      status: text(row.status || "pending") as Topic["status"],
      createdAt: text(row.created_at),
    }),
    next.topics,
  );
  next.tasks = mapRows(
    remote.contentTasks,
    (row) => ({
      id: text(row.id),
      title: text(row.title),
      topicId: text(row.topic_id),
      businessLineId: text(row.business_line_id),
      contentType: text(row.content_type),
      status: text(row.status),
      targetPlatformIds: jsonArray(row.target_platforms),
      targetAccountIds: jsonArray(row.target_accounts),
      direction: text(row.direction),
      outline: text(row.outline),
      dueDate: text(row.due_date),
      createdAt: text(row.created_at),
    }),
    next.tasks,
  );
  next.masterArticles = mapRows(
    remote.masterArticles,
    mapMasterArticle,
    next.masterArticles,
  );
  next.platformVersions = mapRows(
    remote.platformVersions,
    mapPlatformVersion,
    next.platformVersions,
  );
  next.accountVersions = mapRows(
    remote.accountVersions,
    mapAccountVersion,
    next.accountVersions,
  );
  next.aiLogs = mapRows(remote.aiLogs, mapAiLog, next.aiLogs);
  next.publishPlans = mapRows(
    remote.publishPlans,
    mapPublishPlan,
    next.publishPlans,
  );
  next.collectionSources = mapRows(
    remote.collectionSources,
    (row) => ({
      id: text(row.id),
      name: text(row.name),
      category: text(row.category),
      sourceType: collectionSourceType(row.source_type),
      url: text(row.url),
      language: text(row.language) || "auto",
      enabled: bool(row.enabled),
      lastCollectedAt: text(row.last_collected_at),
      failureLog: text(row.failure_log),
      notes: text(row.notes),
      createdAt: text(row.created_at),
    }),
    next.collectionSources,
  );
  next.collectedItems = mapRows(
    remote.collectedItems,
    (row) => ({
      id: text(row.id),
      sourceId: text(row.source_id),
      title: text(row.title),
      url: text(row.url),
      author: text(row.author),
      publishedAt: text(row.published_at),
      language: text(row.language) || "auto",
      translationStatus:
        (text(row.translation_status) as CollectedItem["translationStatus"]) ||
        "none",
      translatedTitle: text(row.translated_title),
      translatedContent: text(row.translated_content),
      rawContent: text(row.raw_content),
      summary: text(row.summary),
      keywords: jsonArray(row.keywords).join(",") || text(row.keywords),
      status: text(row.status),
      createdAt: text(row.created_at),
    }),
    next.collectedItems,
  );
  next.exportPackages = mapRows(
    remote.exportPackages,
    mapExportPackage,
    next.exportPackages,
  );
  next.assets = mapRows(remote.materialAssets, mapMaterialAsset, next.assets);
  next.roles = mapRows(
    remote.roles,
    (row) => ({
      id: text(row.id),
      name: text(row.name),
      code: text(row.code),
      description: text(row.description),
      permissions: jsonArray(row.permissions),
      enabled: bool(row.enabled),
      createdAt: text(row.created_at),
    }),
    next.roles,
  );
  next.users = mapRows(
    remote.users,
    (row) => ({
      id: text(row.id),
      name: text(row.name),
      email: text(row.email),
      password:
        text(row.email).toLowerCase() === "admin@mediapilotx.local"
          ? "md888888"
          : "",
      roleId: text(row.role_id),
      enabled: bool(row.enabled),
      lastLoginAt: text(row.last_login_at),
      createdAt: text(row.created_at),
    }),
    next.users,
  );
  return next;
}

function mapMasterArticle(row: RemoteRow): MasterArticle {
  return {
    id: text(row.id),
    taskId: text(row.content_task_id),
    title: text(row.title),
    outline: text(row.outline),
    body: text(row.body_markdown || row.body),
    status: text(row.status),
    aiGenerated: bool(row.ai_generated),
    createdAt: text(row.created_at),
  };
}

function mapPlatformVersion(row: RemoteRow): PlatformVersion {
  return {
    id: text(row.id),
    taskId: text(row.content_task_id),
    masterArticleId: text(row.master_article_id),
    platformId: text(row.platform_id),
    title: text(row.title),
    body: text(row.body),
    summary: text(row.summary),
    tags: jsonArray(row.tags).join(",") || text(row.tags),
    status: text(row.status),
    createdAt: text(row.created_at),
  };
}

function mapAccountVersion(row: RemoteRow): AccountVersion {
  return {
    id: text(row.id),
    platformVersionId: text(row.platform_version_id),
    accountId: text(row.account_id),
    title: text(row.title),
    body: text(row.body),
    coverText: text(row.cover_text),
    hashtags: jsonArray(row.hashtags).join(",") || text(row.hashtags),
    cta: text(row.cta),
    status: text(row.status),
    similarityScore: num(row.similarity_score),
    createdAt: text(row.created_at),
  };
}

function mapAiLog(row: RemoteRow): AiLog {
  return {
    id: text(row.id),
    taskId: text(row.task_id),
    objectType: text(row.object_type),
    input: text(row.input_text),
    output: text(row.output_text),
    cost: num(row.cost),
    status: text(row.status),
    createdAt: text(row.created_at),
  };
}

function mapPublishPlan(row: RemoteRow): PublishPlan {
  return {
    id: text(row.id),
    accountVersionId: text(row.account_version_id),
    accountId: text(row.account_id),
    platformId: text(row.platform_id),
    scheduledAt: text(row.scheduled_at),
    actualPublishedAt: text(row.actual_published_at),
    status: text(row.status),
    publishUrl: text(row.publish_url),
    remark: text(row.remark),
    createdAt: text(row.created_at),
  };
}

function mapExportPackage(row: RemoteRow): ExportPackage {
  return {
    id: text(row.id),
    publishPlanId: text(row.publish_plan_id),
    accountVersionId: text(row.account_version_id),
    accountId: text(row.account_id),
    platformId: text(row.platform_id),
    title: text(row.title),
    markdown: text(row.markdown_content),
    text: text(row.text_content),
    json: text(row.json_content),
    status: text(row.status),
    archiveDir: text(row.archive_dir),
    fileName: text(row.file_name),
    createdAt: text(row.created_at),
  };
}

function mapMaterialAsset(row: RemoteRow): MaterialAsset {
  return {
    id: text(row.id),
    name: text(row.name),
    type: text(row.asset_type || row.type || "other"),
    path: text(row.source_url || row.storage_key),
    tags: jsonArray(row.tags).join(",") || text(row.tags),
    businessLineId: text(row.business_line_id),
    platformId: text(row.platform_id),
    mimeType: text(row.mime_type),
    size: num(row.size),
    storageKey: text(row.storage_key),
    status: text(row.status || "ready"),
    contentHash: text(row.content_hash),
    duplicateStatus: text(row.duplicate_status || "clear"),
    duplicateNotes: text(row.duplicate_notes),
    archiveDir: text(row.archive_dir),
    fileName: text(row.file_name),
    createdAt: text(row.created_at),
  };
}

function mapRows<T>(
  value: unknown,
  mapper: (row: RemoteRow) => T,
  fallback: T[],
) {
  return Array.isArray(value) && value.length
    ? value.map((row) => mapper(row as RemoteRow))
    : fallback;
}

function text(value: unknown) {
  return typeof value === "string" ? value : value == null ? "" : String(value);
}

function num(value: unknown) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function bool(value: unknown) {
  return value === true || value === 1 || value === "1";
}

function bindMode(value: unknown): AccountBindMode {
  const mode = text(value);
  return ["oauth", "qr", "cookie", "api"].includes(mode)
    ? (mode as AccountBindMode)
    : "manual";
}

function bindStatus(value: unknown): AccountBindStatus {
  const status = text(value);
  return ["pending", "bound", "expired"].includes(status)
    ? (status as AccountBindStatus)
    : "unbound";
}

function collectionSourceType(value: unknown): CollectionSourceType {
  const sourceType = text(value).toLowerCase();
  return ["atom", "rdf", "api", "website", "wechat"].includes(sourceType)
    ? (sourceType as CollectionSourceType)
    : "rss";
}

function jsonArray(value: unknown) {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value !== "string" || !value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
}

function App() {
  const [active, setActive] = React.useState<NavKey>("dashboard");
  const [dashboardOpen, setDashboardOpen] = React.useState(false);
  const [dashboardSub, setDashboardSub] =
    React.useState<DashboardSubKey>("home");
  const [taskOpen, setTaskOpen] = React.useState(false);
  const [taskSub, setTaskSub] = React.useState<TaskSubKey>("list");
  const [collectionOpen, setCollectionOpen] = React.useState(false);
  const [collectionSub, setCollectionSub] =
    React.useState<CollectionSubKey>("list");
  const [topicOpen, setTopicOpen] = React.useState(false);
  const [topicSub, setTopicSub] = React.useState<TopicSubKey>("list");
  const [platformVersionOpen, setPlatformVersionOpen] = React.useState(false);
  const [platformVersionSub, setPlatformVersionSub] =
    React.useState<PlatformVersionSubKey>("versions");
  const [accountOpen, setAccountOpen] = React.useState(false);
  const [accountSub, setAccountSub] = React.useState<AccountSubKey>("accounts");
  const [masterOpen, setMasterOpen] = React.useState(false);
  const [masterSub, setMasterSub] = React.useState<MasterSubKey>("list");
  const [editingMasterArticleId, setEditingMasterArticleId] =
    React.useState("");
  const [imageTextOpen, setImageTextOpen] = React.useState(false);
  const [imageTextSub, setImageTextSub] =
    React.useState<ImageTextSubKey>("versions");
  const [videoOpen, setVideoOpen] = React.useState(false);
  const [videoSub, setVideoSub] = React.useState<VideoSubKey>("versions");
  const [assetOpen, setAssetOpen] = React.useState(false);
  const [assetSub, setAssetSub] = React.useState<AssetSubKey>("assets");
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [settingsSub, setSettingsSub] =
    React.useState<SettingsSubKey>("status");
  const [store, setStore] = React.useState<Store>(() => loadStore());
  const [currentUserId, setCurrentUserId] = React.useState(
    () => localStorage.getItem("mediapilotx:current-user") || "",
  );
  const [toast, setToast] = React.useState("");
  const [remoteStatus, setRemoteStatus] = React.useState(
    apiBaseUrl ? "API 未同步" : "本地演示模式",
  );
  const current = navItems.find((item) => item.key === active)!;
  const currentTitle =
    active === "dashboard"
      ? dashboardSubItems.find((item) => item.key === dashboardSub)?.label ||
        current.label
      : active === "collection"
        ? collectionExtendedSubItems.find((item) => item.key === collectionSub)
            ?.label || current.label
        : active === "tasks"
          ? taskSubItems.find((item) => item.key === taskSub)?.label ||
            current.label
          : active === "topics"
            ? topicSubItems.find((item) => item.key === topicSub)?.label ||
              current.label
            : active === "platformVersions"
              ? platformVersionSubItems.find(
                  (item) => item.key === platformVersionSub,
                )?.label || current.label
              : active === "accounts"
                ? accountSubItems.find((item) => item.key === accountSub)
                    ?.label || current.label
                : active === "master"
                  ? masterSubItems.find((item) => item.key === masterSub)
                      ?.label || current.label
                  : active === "imageText"
                    ? imageTextSubItems.find(
                        (item) => item.key === imageTextSub,
                      )?.label || current.label
                    : active === "video"
                      ? videoSubItems.find((item) => item.key === videoSub)
                          ?.label || current.label
                      : active === "assets"
                        ? assetSubItems.find((item) => item.key === assetSub)
                            ?.label || current.label
                        : active === "settings"
                          ? settingsSubItems.find(
                              (item) => item.key === settingsSub,
                            )?.label || current.label
                          : current.label;
  const contentKey = [
    active,
    dashboardSub,
    taskSub,
    collectionSub,
    topicSub,
    platformVersionSub,
    accountSub,
    masterSub,
    imageTextSub,
    videoSub,
    assetSub,
    settingsSub,
  ].join(":");
  const currentUser = store.users.find(
    (user) => user.id === currentUserId && user.enabled,
  );
  const currentRole = currentUser
    ? store.roles.find((role) => role.id === currentUser.roleId)
    : undefined;
  const can = React.useCallback(
    (moduleKey: NavKey, action: PermissionAction) =>
      roleHasPermission(currentRole, moduleKey, action),
    [currentRole],
  );
  const visibleNavItems = navItems.filter((item) => can(item.key, "view"));

  React.useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(store));
    } catch (error) {
      console.warn("MediaPilotX local cache save failed", error);
      setToast(
        "本地缓存保存失败：数据量可能过大，请减少一次生成数量或清理历史大图片数据",
      );
    }
  }, [store]);

  React.useEffect(() => {
    if (!apiBaseUrl) return;
    fetchBootstrap<RemoteBootstrap>()
      .then((remote) => {
        if (!remote) return;
        setStore((previous) => mergeRemoteStore(previous, remote));
        setRemoteStatus(`API 已同步：${new Date().toLocaleTimeString("zh-CN")}`);
      })
      .catch((error) =>
        setRemoteStatus(
          error instanceof Error
            ? `API 同步失败：${error.message}`
            : "API 同步失败",
        ),
      );
  }, []);

  React.useEffect(() => {
    if (!currentUser || can(active, "view")) return;
    const fallback = visibleNavItems[0]?.key;
    if (fallback) setActive(fallback);
  }, [active, can, currentUser, visibleNavItems]);

  function mutate(updater: (draft: Store) => void, message: string) {
    setStore((previous) => {
      const next = structuredClone(previous) as Store;
      updater(next);
      return next;
    });
    setToast(message);
    window.setTimeout(() => setToast(""), 2800);
  }

  async function login(email: string, password: string) {
    const user = store.users.find(
      (item) =>
        item.email.toLowerCase() === email.toLowerCase() && item.enabled,
    );
    if (!user) {
      setToast(
        "没有找到该用户，请先在系统设置中创建，或使用 admin@mediapilotx.local",
      );
      return;
    }
    if (!password) {
      setToast("请输入密码");
      return;
    }
    if ((user.password || "") !== password) {
      setToast("密码错误");
      return;
    }
    try {
      await apiLogin(email, password);
    } catch {
      // Local-first mode keeps the demo usable before Cloudflare API is configured.
    }
    setCurrentUserId(user.id);
    localStorage.setItem("mediapilotx:current-user", user.id);
    mutate((draft) => {
      const found = draft.users.find((item) => item.id === user.id);
      if (found) found.lastLoginAt = now();
    }, "登录成功");
  }

  function logout() {
    setCurrentUserId("");
    localStorage.removeItem("mediapilotx:current-user");
  }

  if (!currentUser) {
    return <LoginScreen store={store} onLogin={login} toast={toast} />;
  }

  const ctx = {
    store,
    mutate,
    setActive,
    setMasterSub,
    editingMasterArticleId,
    setEditingMasterArticleId,
    currentUser,
    currentRole,
    can,
    remoteStatus,
  };

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <div className="brandMark">MPX</div>
          <div>
            <strong>MediaPilotX</strong>
            <span>AI 自媒体运营中台</span>
          </div>
        </div>
        <nav>
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <React.Fragment key={item.key}>
                <button
                  className={active === item.key ? "active" : ""}
                  onClick={() => {
                    if (item.key === "dashboard") {
                      setDashboardOpen((open) => !open);
                      setActive("dashboard");
                    } else if (item.key === "tasks") {
                      setTaskOpen((open) => !open);
                      setActive("tasks");
                    } else if (item.key === "collection") {
                      setCollectionOpen((open) => !open);
                      setActive("collection");
                    } else if (item.key === "topics") {
                      setTopicOpen((open) => !open);
                      setActive("topics");
                    } else if (item.key === "platformVersions") {
                      setPlatformVersionOpen((open) => !open);
                      setActive("platformVersions");
                    } else if (item.key === "accounts") {
                      setAccountOpen((open) => !open);
                      setActive("accounts");
                    } else if (item.key === "master") {
                      setMasterOpen((open) => !open);
                      setActive("master");
                    } else if (item.key === "imageText") {
                      setImageTextOpen((open) => !open);
                      setActive("imageText");
                    } else if (item.key === "video") {
                      setVideoOpen((open) => !open);
                      setActive("video");
                    } else if (item.key === "assets") {
                      setAssetOpen((open) => !open);
                      setActive("assets");
                    } else if (item.key === "settings") {
                      setSettingsOpen((open) => !open);
                      setActive("settings");
                    } else {
                      setActive(item.key);
                    }
                  }}
                >
                  <Icon size={17} />
                  {item.label}
                  {item.key === "dashboard" ? (
                    <span className="navCaret">
                      {dashboardOpen ? "⌃" : "⌄"}
                    </span>
                  ) : null}
                  {item.key === "settings" ? (
                    <span className="navCaret">{settingsOpen ? "⌃" : "⌄"}</span>
                  ) : null}
                  {item.key === "tasks" ? (
                    <span className="navCaret">{taskOpen ? "⌃" : "⌄"}</span>
                  ) : null}
                  {item.key === "collection" ? (
                    <span className="navCaret">
                      {collectionOpen ? "⌃" : "⌄"}
                    </span>
                  ) : null}
                  {item.key === "topics" ? (
                    <span className="navCaret">{topicOpen ? "⌃" : "⌄"}</span>
                  ) : null}
                  {item.key === "platformVersions" ? (
                    <span className="navCaret">
                      {platformVersionOpen ? "⌃" : "⌄"}
                    </span>
                  ) : null}
                  {item.key === "accounts" ? (
                    <span className="navCaret">{accountOpen ? "⌃" : "⌄"}</span>
                  ) : null}
                  {item.key === "master" ? (
                    <span className="navCaret">{masterOpen ? "⌃" : "⌄"}</span>
                  ) : null}
                  {item.key === "imageText" ? (
                    <span className="navCaret">
                      {imageTextOpen ? "⌃" : "⌄"}
                    </span>
                  ) : null}
                  {item.key === "video" ? (
                    <span className="navCaret">{videoOpen ? "⌃" : "⌄"}</span>
                  ) : null}
                  {item.key === "assets" ? (
                    <span className="navCaret">{assetOpen ? "⌃" : "⌄"}</span>
                  ) : null}
                </button>
                {item.key === "dashboard" && dashboardOpen ? (
                  <div className="subNav">
                    {dashboardSubItems.map((subItem) => (
                      <button
                        key={subItem.key}
                        className={
                          active === "dashboard" && dashboardSub === subItem.key
                            ? "active"
                            : ""
                        }
                        onClick={() => {
                          setActive("dashboard");
                          setDashboardSub(subItem.key);
                        }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {item.key === "tasks" && taskOpen ? (
                  <div className="subNav">
                    {taskSubItems.map((subItem) => (
                      <button
                        key={subItem.key}
                        className={
                          active === "tasks" && taskSub === subItem.key
                            ? "active"
                            : ""
                        }
                        onClick={() => {
                          setActive("tasks");
                          setTaskSub(subItem.key);
                        }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {item.key === "collection" && collectionOpen ? (
                  <div className="subNav">
                    {collectionExtendedSubItems.map((subItem) => (
                      <button
                        key={subItem.key}
                        className={
                          active === "collection" &&
                          collectionSub === subItem.key
                            ? "active"
                            : ""
                        }
                        onClick={() => {
                          setActive("collection");
                          setCollectionSub(subItem.key);
                        }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {item.key === "topics" && topicOpen ? (
                  <div className="subNav">
                    {topicSubItems.map((subItem) => (
                      <button
                        key={subItem.key}
                        className={
                          active === "topics" && topicSub === subItem.key
                            ? "active"
                            : ""
                        }
                        onClick={() => {
                          setActive("topics");
                          setTopicSub(subItem.key);
                        }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {item.key === "platformVersions" && platformVersionOpen ? (
                  <div className="subNav">
                    {platformVersionSubItems.map((subItem) => (
                      <button
                        key={subItem.key}
                        className={
                          active === "platformVersions" &&
                          platformVersionSub === subItem.key
                            ? "active"
                            : ""
                        }
                        onClick={() => {
                          setActive("platformVersions");
                          setPlatformVersionSub(subItem.key);
                        }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {item.key === "accounts" && accountOpen ? (
                  <div className="subNav">
                    {accountSubItems.map((subItem) => (
                      <button
                        key={subItem.key}
                        className={
                          active === "accounts" && accountSub === subItem.key
                            ? "active"
                            : ""
                        }
                        onClick={() => {
                          setActive("accounts");
                          setAccountSub(subItem.key);
                        }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {item.key === "master" && masterOpen ? (
                  <div className="subNav">
                    {masterSubItems.map((subItem) => (
                      <button
                        key={subItem.key}
                        className={
                          active === "master" && masterSub === subItem.key
                            ? "active"
                            : ""
                        }
                        onClick={() => {
                          setActive("master");
                          if (subItem.key === "new") setEditingMasterArticleId("");
                          setMasterSub(subItem.key);
                        }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {item.key === "imageText" && imageTextOpen ? (
                  <div className="subNav">
                    {imageTextSubItems.map((subItem) => (
                      <button
                        key={subItem.key}
                        className={
                          active === "imageText" && imageTextSub === subItem.key
                            ? "active"
                            : ""
                        }
                        onClick={() => {
                          setActive("imageText");
                          setImageTextSub(subItem.key);
                        }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {item.key === "video" && videoOpen ? (
                  <div className="subNav">
                    {videoSubItems.map((subItem) => (
                      <button
                        key={subItem.key}
                        className={
                          active === "video" && videoSub === subItem.key
                            ? "active"
                            : ""
                        }
                        onClick={() => {
                          setActive("video");
                          setVideoSub(subItem.key);
                        }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {item.key === "assets" && assetOpen ? (
                  <div className="subNav">
                    {assetSubItems.map((subItem) => (
                      <button
                        key={subItem.key}
                        className={
                          active === "assets" && assetSub === subItem.key
                            ? "active"
                            : ""
                        }
                        onClick={() => {
                          setActive("assets");
                          setAssetSub(subItem.key);
                        }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {item.key === "settings" && settingsOpen ? (
                  <div className="subNav">
                    {settingsSubItems.map((subItem) => (
                      <button
                        key={subItem.key}
                        className={
                          active === "settings" && settingsSub === subItem.key
                            ? "active"
                            : ""
                        }
                        onClick={() => {
                          setActive("settings");
                          setSettingsSub(subItem.key);
                        }}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </nav>
      </aside>

      <main className="main">
        <header className="header">
          <div>
            <p className="eyebrow">MVP 1.0 内容生产闭环</p>
            <h1>{currentTitle}</h1>
          </div>
          <div className="headerActions">
            <span className="userBadge">
              {currentUser.name} · {nameOf(store.roles, currentUser.roleId)}
            </span>
            <span className="userBadge">{remoteStatus}</span>
            {can("publishing", "export") ? (
              <button className="ghost" onClick={() => setActive("publishing")}>
                <Download size={16} />
                发布包
              </button>
            ) : null}
            {can("tasks", "create") ? (
              <button
                onClick={() => {
                  setTaskSub("new");
                  setActive("tasks");
                }}
              >
                <Plus size={16} />
                新建任务
              </button>
            ) : null}
            <button className="ghost" onClick={logout}>
              退出
            </button>
          </div>
        </header>
        {toast && <div className="toast">{toast}</div>}
        <div key={contentKey}>
          {renderView(
            active,
            ctx,
            dashboardSub,
            taskSub,
            collectionSub,
            topicSub,
            platformVersionSub,
            accountSub,
            masterSub,
            imageTextSub,
            videoSub,
            assetSub,
            settingsSub,
          )}
        </div>
      </main>
    </div>
  );
}

type Ctx = {
  store: Store;
  mutate: (updater: (draft: Store) => void, message: string) => void;
  setActive: (key: NavKey) => void;
  setMasterSub: (key: MasterSubKey) => void;
  editingMasterArticleId: string;
  setEditingMasterArticleId: (id: string) => void;
  currentUser: User;
  currentRole?: Role;
  can: (moduleKey: NavKey, action: PermissionAction) => boolean;
  remoteStatus: string;
};
type RemoteRow = Record<string, unknown>;
type RemoteBootstrap = Record<string, RemoteRow[] | string>;

function LoginScreen({
  store,
  onLogin,
  toast,
}: {
  store: Store;
  onLogin: (email: string, password: string) => void;
  toast: string;
}) {
  const [email, setEmail] = React.useState("admin@mediapilotx.local");
  const [password, setPassword] = React.useState("");
  return (
    <div className="loginPage">
      <section className="loginPanel">
        <div className="brand large">
          <div className="brandMark">MPX</div>
          <div>
            <strong>MediaPilotX</strong>
            <span>AI 自媒体运营中台</span>
          </div>
        </div>
        <h1>登录工作台</h1>
        <p className="muted">
          当前是开发期轻量登录。Cloudflare API 配好后会自动调用
          `/auth/login`，本地仍可离线演示。
        </p>
        {toast && <div className="toast">{toast}</div>}
        <form
          className="form"
          onSubmit={(event) => {
            event.preventDefault();
            onLogin(email, password);
          }}
        >
          <label>
            账号
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label>
            密码
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="请输入密码"
            />
          </label>
          <button type="submit">进入系统</button>
        </form>
        <div className="loginUsers">
          {store.users
            .filter((user) => user.enabled)
            .map((user) => (
              <button key={user.id} onClick={() => setEmail(user.email)}>
                {user.name} · {user.email}
              </button>
            ))}
        </div>
      </section>
    </div>
  );
}

function renderView(
  active: NavKey,
  ctx: Ctx,
  dashboardSub: DashboardSubKey,
  taskSub: TaskSubKey,
  collectionSub: CollectionSubKey,
  topicSub: TopicSubKey,
  platformVersionSub: PlatformVersionSubKey,
  accountSub: AccountSubKey,
  masterSub: MasterSubKey,
  imageTextSub: ImageTextSubKey,
  videoSub: VideoSubKey,
  assetSub: AssetSubKey,
  settingsSub: SettingsSubKey,
) {
  if (!ctx.can(active, "view")) {
    return (
      <Panel title="无访问权限">
        <p className="muted">当前角色没有访问该模块的权限。</p>
      </Panel>
    );
  }
  const map: Record<NavKey, React.ReactNode> = {
    dashboard:
      dashboardSub === "guide" ? <DashboardGuide /> : <Dashboard {...ctx} />,
    tasks: <Tasks {...ctx} view={taskSub} />,
    collection: <Collection {...ctx} view={collectionSub} />,
    topics: <Topics {...ctx} view={topicSub} />,
    master: <MasterArticlesV2 {...ctx} view={masterSub} />,
    platformVersions: <PlatformVersions {...ctx} view={platformVersionSub} />,
    imageText: <ImageTextV2 {...ctx} view={imageTextSub} />,
    video: <VideoV2 {...ctx} view={videoSub} />,
    assets: <Assets {...ctx} view={assetSub} />,
    publishing: <Publishing {...ctx} />,
    analytics: <AnalyticsV2 {...ctx} />,
    accounts: <AccountsV3 {...ctx} view={accountSub} />,
    settings: <SettingsView {...ctx} view={settingsSub} />,
  };
  return map[active];
}

function Dashboard({ store, setActive }: Ctx) {
  const scheduledToday = store.publishPlans.filter(
    (item) => item.status === "scheduled",
  ).length;
  const reviewCount = store.masterArticles.filter(
    (item) => item.status.includes("待"),
  ).length;
  const childProjectCount = store.imageTextProjects.filter(
    (item) => item.parentProjectId,
  ).length;
  return (
    <>
      <section className="metrics">
        <Metric label="待发布" value={String(scheduledToday)} tone="green" />
        <Metric label="待审核" value={String(reviewCount)} tone="orange" />
        <Metric label="图文子项目" value={String(childProjectCount)} tone="blue" />
        <Metric label="素材" value={String(store.assets.length)} tone="dark" />
      </section>
      <section className="grid two">
        <Panel title="生产流程">
          <div className="timeline">
            <Step n="1" title="采集内容" text={`${store.collectedItems.length} 条资料`} />
            <Step n="2" title="选题池" text={`${store.topics.length} 个选题`} />
            <Step n="3" title="内容任务" text={`${store.tasks.length} 个任务`} />
            <Step n="4" title="图文项目" text={`${store.imageTextProjects.length} 个项目`} />
          </div>
        </Panel>
        <Panel title="快捷入口">
          <div className="cards compact">
            <button className="inlineButton" onClick={() => setActive("collection")}>采集中心</button>
            <button className="inlineButton" onClick={() => setActive("topics")}>选题池</button>
            <button className="inlineButton" onClick={() => setActive("imageText")}>图文项目</button>
            <button className="inlineButton" onClick={() => setActive("settings")}>系统设置</button>
          </div>
        </Panel>
      </section>
    </>
  );
}

function Tasks({ store, mutate, view }: Ctx & { view: TaskSubKey }) {
  const [taskFormOpen, setTaskFormOpen] = React.useState(view === "new");
  const [editingTaskId, setEditingTaskId] = React.useState("");
  const editingTask = store.tasks.find((task) => task.id === editingTaskId);

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    const taskId = editingTask?.id || uid("task");
    const payload = {
      id: taskId,
      title: data.title,
      topic_id: data.topicId,
      business_line_id: data.businessLineId,
      content_type: data.contentType,
      status: editingTask?.status || "draft",
      target_platforms: values(data.targetPlatformIds),
      target_accounts: values(data.targetAccountIds),
      outline: data.outline,
      direction: data.direction,
      due_date: data.dueDate,
    };
    const remote = editingTask
      ? updateRemote("contentTasks", taskId, payload)
      : createRemote("contentTasks", payload);
    void remote.catch(() => undefined);
    mutate(
      (draft) => {
        const task: ContentTask = {
          id: taskId,
          title: data.title || "未命名内容任务",
          topicId: data.topicId,
          businessLineId: data.businessLineId,
          contentType: data.contentType,
          status: editingTask?.status || "鑽夌",
          targetPlatformIds: values(data.targetPlatformIds),
          targetAccountIds: values(data.targetAccountIds),
          direction: data.direction,
          outline: data.outline,
          dueDate: data.dueDate,
          createdAt: editingTask?.createdAt || now(),
        };
        upsertById(draft.tasks, task);
      },
      editingTask ? "内容任务已更新" : "内容任务已创建",
    );
    setEditingTaskId("");
    setTaskFormOpen(false);
    event.currentTarget.reset();
  }

  function openNewTaskForm() {
    setEditingTaskId("");
    setTaskFormOpen(true);
  }

  function openEditTaskForm(task: ContentTask) {
    setEditingTaskId(task.id);
    setTaskFormOpen(true);
  }

  function closeTaskForm() {
    setEditingTaskId("");
    setTaskFormOpen(false);
  }

  function deleteTask(task: ContentTask) {
    if (!window.confirm(`确定删除内容任务「${task.title}」吗？`)) return;
    void deleteRemote("contentTasks", task.id).catch(() => undefined);
    mutate((draft) => {
      draft.tasks = draft.tasks.filter((item) => item.id !== task.id);
    }, "内容任务已删除");
    if (editingTaskId === task.id) closeTaskForm();
  }

  return (
    <section className="grid collectionWorkspace">
      {view === "new" || taskFormOpen || editingTask ? (
        <div className={view === "list" ? "modalOverlay" : ""}>
          <form
            className={view === "list" ? "modalPanel modelModal" : "panel form"}
            key={editingTask?.id || "new-task"}
            onSubmit={submit}
          >
            {view === "list" ? (
              <div className="modalHeader">
                <div>
                  <strong>
                    {editingTask ? "修改内容任务" : "新增内容任务"}
                  </strong>
                  <span>配置选题、业务线、目标平台、账号和创作方向。</span>
                </div>
                <button type="button" className="ghost" onClick={closeTaskForm}>
                  关闭
                </button>
              </div>
            ) : (
              <h2>{editingTask ? "修改内容任务" : "新建内容任务"}</h2>
            )}
            <div className="form modelForm">
              <label>
                任务标题
                <input
                  name="title"
                  required
                  placeholder="例如：美国DDP清关旺季风险提醒"
                  defaultValue={editingTask?.title || ""}
                />
              </label>
              <label>
                关联选题
                <select
                  name="topicId"
                  defaultValue={editingTask?.topicId || ""}
                >
                  <option value="">不关联</option>
                  {store.topics.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                业务线
                <Select
                  name="businessLineId"
                  rows={store.businessLines}
                  value={editingTask?.businessLineId}
                />
              </label>
              <label>
                内容类型
                <select
                  name="contentType"
                  defaultValue={editingTask?.contentType || contentTypes[0]}
                >
                  {contentTypes.map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </label>
              <label>
                目标平台
                <MultiSelect
                  name="targetPlatformIds"
                  rows={store.platforms}
                  value={(editingTask?.targetPlatformIds || []).join(",")}
                />
              </label>
              <label>
                目标账号
                <MultiSelect
                  name="targetAccountIds"
                  rows={store.accounts}
                  value={(editingTask?.targetAccountIds || []).join(",")}
                />
              </label>
              <label>
                截止时间
                <input
                  name="dueDate"
                  type="date"
                  defaultValue={editingTask?.dueDate || ""}
                />
              </label>
              <label>
                大纲
                <textarea
                  name="outline"
                  rows={4}
                  placeholder="1. 背景&#10;2. 风险&#10;3. 解决方案"
                  defaultValue={editingTask?.outline || ""}
                />
              </label>
              <label>
                创作方向
                <textarea
                  name="direction"
                  rows={4}
                  placeholder="输入目标客户、转化目标和注意事项"
                  defaultValue={editingTask?.direction || ""}
                />
              </label>
            </div>
            <div className={view === "list" ? "modalActions" : "rowActions"}>
              <button type="submit" className="primaryButton">
                <Plus size={16} />
                {editingTask ? "保存修改" : "保存任务"}
              </button>
              {view === "list" || editingTask ? (
                <button
                  type="button"
                  className="inlineButton"
                  onClick={closeTaskForm}
                >
                  取消
                </button>
              ) : null}
            </div>
          </form>
        </div>
      ) : null}
      {view === "list" ? (
        <Panel title="内容任务列表">
          <div className="listToolbar">
            <div>
              <strong>内容任务列表</strong>
              <p className="muted">管理选题转化后的内容生产计划。</p>
            </div>
            <button
              type="button"
              className="primaryButton"
              onClick={openNewTaskForm}
            >
              <Plus size={16} />
              新增内容任务
            </button>
          </div>
          <TaskTable
            store={store}
            actions
            onEdit={openEditTaskForm}
            onDelete={deleteTask}
          />
        </Panel>
      ) : null}
    </section>
  );
}

function Collection({
  store,
  mutate,
  view,
  setActive,
  setMasterSub,
}: Ctx & { view: CollectionSubKey }) {
  const [duplicateMatches, setDuplicateMatches] = React.useState<
    DuplicateMatch[]
  >([]);
  const [mdImportSummary, setMdImportSummary] = React.useState("");
  const [selectedCollectedId, setSelectedCollectedId] = React.useState("");
  const [editingCollectedId, setEditingCollectedId] = React.useState("");
  const [editingCollectedTitle, setEditingCollectedTitle] = React.useState("");
  const [editingCollectedBody, setEditingCollectedBody] = React.useState("");
  const [linkImportUrl, setLinkImportUrl] = React.useState("");
  const [linkImportMessage, setLinkImportMessage] = React.useState("");
  const [sourceFormOpen, setSourceFormOpen] = React.useState(false);
  const [editingSourceId, setEditingSourceId] = React.useState("");
  const [ruleFormOpen, setRuleFormOpen] = React.useState(false);
  const [editingRuleId, setEditingRuleId] = React.useState("");
  const [taskFormOpen, setTaskFormOpen] = React.useState(false);
  const [editingTaskId, setEditingTaskId] = React.useState("");
  const [taskRunMessage, setTaskRunMessage] = React.useState("");
  const [topicDialogItemId, setTopicDialogItemId] = React.useState("");
  const [topicDialogModelId, setTopicDialogModelId] = React.useState("");
  const [topicDialogSkill, setTopicDialogSkill] = React.useState("builtin");
  const [topicGenerating, setTopicGenerating] = React.useState(false);
  const [rewriteDialogItemId, setRewriteDialogItemId] = React.useState("");
  const [rewriteModelId, setRewriteModelId] = React.useState("");
  const [rewritePromptId, setRewritePromptId] = React.useState("");
  const [rewritePromptText, setRewritePromptText] = React.useState("");
  const [rewriteGenerating, setRewriteGenerating] = React.useState(false);
  const selectedCollectedItem =
    store.collectedItems.find((item) => item.id === selectedCollectedId) ||
    store.collectedItems[0];
  const selectedCleanContent = selectedCollectedItem
    ? cleanMarkdownContent(selectedCollectedItem.rawContent)
    : "";
  const editingSource = store.collectionSources.find(
    (item) => item.id === editingSourceId,
  );
  const editingRule = store.collectionRules.find(
    (item) => item.id === editingRuleId,
  );
  const editingTask = store.collectionTasks.find(
    (item) => item.id === editingTaskId,
  );
  const topicDialogItem = store.collectedItems.find(
    (item) => item.id === topicDialogItemId,
  );
  const rewriteDialogItem = store.collectedItems.find(
    (item) => item.id === rewriteDialogItemId,
  );
  const usableTopicModels = store.aiModels.filter(
    (model) => model.enabled && model.baseUrl && model.apiKey,
  );
  const usableRewriteModels = usableTopicModels;
  const usableRewritePrompts = store.aiRewritePrompts.filter(
    (prompt) => prompt.enabled,
  );

  function importPreviewItem(
    preview: CollectedPreviewItem,
    sourceId = "",
    status = "collected",
  ) {
    const source = store.collectionSources.find((item) => item.id === sourceId);
    const language = source?.language || "auto";
    const body = cleanMarkdownContent(
      preview.content || preview.summary || preview.title,
    );
    const itemId = uid("col");
    const keywords = extractKeywords(`${preview.title} ${body}`);
    const item: CollectedItem = {
      id: itemId,
      sourceId,
      title: preview.title || preview.url || "采集内容",
      url: preview.url,
      author: preview.author || "",
      publishedAt: preview.publishedAt || "",
      language,
      translationStatus: language === "zh-CN" ? "none" : "pending",
      translatedTitle: "",
      translatedContent: "",
      rawContent: body,
      summary: summarize(body),
      keywords,
      status,
      createdAt: now(),
    };
    void createRemote("collectedItems", {
      id: item.id,
      source_id: sourceId,
      title: item.title,
      url: item.url,
      author: item.author,
      published_at: item.publishedAt,
      language: item.language,
      translation_status: item.translationStatus,
      translated_title: item.translatedTitle,
      translated_content: item.translatedContent,
      raw_content: item.rawContent,
      summary: item.summary,
      keywords: values(item.keywords),
      status: item.status,
    }).catch(() => undefined);
    return item;
  }

  async function collectLink() {
    const url = linkImportUrl.trim();
    if (!url) {
      setLinkImportMessage("请先输入公众号文章或网页链接。");
      return;
    }
    setLinkImportMessage("正在采集链接...");
    try {
      const result = await previewCollectionSource({
        url,
        sourceType: "wechat",
        limit: 1,
      });
      if (!result) {
        setLinkImportMessage(
          "当前没有配置 API 地址，网页/RSS 抓取需要通过本地 API 或 Cloudflare API 代理完成。",
        );
        return;
      }
      const item = result.items[0];
      if (!item) {
        setLinkImportMessage("没有解析到可入库内容。");
        return;
      }
      const previewText = `${item.title} ${item.summary || ""} ${item.content || ""}`;
      if (
        /鐜寮傚父|褰撳墠鐜寮傚父|完成验证后即可继续访问|去验证|参数错误|瑙嗛\s*小程序\s*璧瀨鍦ㄧ湅|收藏|氓聫聜忙聲掳茅聰聶猫炉炉|猫搂聠茅垄聭|氓掳聫莽篓聥氓潞聫/.test(
          previewText,
        )
      ) {
        setLinkImportMessage(
          "微信返回验证页或参数错误页，无法直接采集正文。建议下载公众号 MD/TXT 后用历史文件导入，或复制正文到上方手工录入。",
        );
        return;
      }
      const collected = importPreviewItem(item, "", "link_collected");
      mutate((draft) => {
        draft.collectedItems.unshift(collected);
      }, "链接内容已采集入库");
      setSelectedCollectedId(collected.id);
      setLinkImportUrl("");
      setLinkImportMessage("已采集 1 条内容。");
    } catch (error) {
      setLinkImportMessage(
        `采集失败：${error instanceof Error ? error.message : "未知错误"}`,
      );
    }
  }

  function saveSource(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = formValues(form);
    const sourceId = editingSource?.id || uid("cs");
    const source: CollectionSource = {
      id: sourceId,
      name: data.name,
      category: data.category,
      sourceType: collectionSourceType(data.sourceType),
      url: data.url,
      language: data.language || editingSource?.language || "auto",
      enabled: data.enabled === "on",
      lastCollectedAt: editingSource?.lastCollectedAt || "",
      failureLog: editingSource?.failureLog || "",
      notes: data.notes,
      createdAt: editingSource?.createdAt || now(),
    };
    const payload = {
      id: source.id,
      name: source.name,
      source_type: source.sourceType,
      url: source.url,
      language: source.language,
      enabled: source.enabled ? 1 : 0,
      last_collected_at: source.lastCollectedAt,
      failure_log: source.failureLog,
    };
    const remote = editingSource
      ? updateRemote("collectionSources", source.id, payload)
      : createRemote("collectionSources", payload);
    void remote.catch(() => undefined);
    mutate(
      (draft) => {
        upsertById(draft.collectionSources, source);
      },
      editingSource ? "采集源已更新" : "采集源已新增",
    );
    setSourceFormOpen(false);
    setEditingSourceId("");
    form.reset();
  }

  function deleteSource(sourceId: string) {
    if (!window.confirm("确定删除这个采集源吗？")) return;
    void deleteRemote("collectionSources", sourceId).catch(() => undefined);
    mutate((draft) => {
      draft.collectionSources = draft.collectionSources.filter(
        (item) => item.id !== sourceId,
      );
      draft.collectionTasks = draft.collectionTasks.filter(
        (item) => item.sourceId !== sourceId,
      );
    }, "采集源已删除");
  }

  function saveRule(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = formValues(form);
    const rule: CollectionRule = {
      id: editingRule?.id || uid("cr"),
      name: data.name,
      mode: data.mode === "all" ? "all" : "keyword",
      keywords: data.keywords,
      excludeKeywords: data.excludeKeywords,
      businessLineId: data.businessLineId,
      contentType: data.contentType,
      autoAi: data.autoAi === "on",
      autoTopic: data.autoTopic === "on",
      enabled: data.enabled === "on",
      createdAt: editingRule?.createdAt || now(),
    };
    mutate(
      (draft) => upsertById(draft.collectionRules, rule),
      editingRule ? "采集规则已更新" : "采集规则已新增",
    );
    setRuleFormOpen(false);
    setEditingRuleId("");
    form.reset();
  }

  function deleteRule(ruleId: string) {
    if (!window.confirm("确定删除这个采集规则吗？")) return;
    mutate((draft) => {
      draft.collectionRules = draft.collectionRules.filter(
        (item) => item.id !== ruleId,
      );
      draft.collectionTasks = draft.collectionTasks.filter(
        (item) => item.ruleId !== ruleId,
      );
    }, "采集规则已删除");
  }

  function saveTask(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = formValues(form);
    const task: CollectionTask = {
      id: editingTask?.id || uid("ct"),
      name: data.name,
      sourceId: data.sourceId,
      ruleId: data.ruleId,
      schedule: (["hourly", "daily", "weekly"].includes(data.schedule)
        ? data.schedule
        : "manual") as CollectionTask["schedule"],
      enabled: data.enabled === "on",
      status: editingTask?.status || "待启动",
      lastRunAt: editingTask?.lastRunAt || "",
      lastResult: editingTask?.lastResult || "",
      createdAt: editingTask?.createdAt || now(),
    };
    mutate(
      (draft) => upsertById(draft.collectionTasks, task),
      editingTask ? "采集任务已更新" : "采集任务已新增",
    );
    setTaskFormOpen(false);
    setEditingTaskId("");
    form.reset();
  }

  function deleteTask(taskId: string) {
    if (!window.confirm("确定删除这个采集任务吗？")) return;
    mutate((draft) => {
      draft.collectionTasks = draft.collectionTasks.filter(
        (item) => item.id !== taskId,
      );
    }, "采集任务已删除");
  }

  function toggleTask(taskId: string) {
    mutate((draft) => {
      const task = draft.collectionTasks.find((item) => item.id === taskId);
      if (task) {
        task.enabled = !task.enabled;
        task.status = task.enabled ? "已启用" : "已停用";
      }
    }, "采集任务状态已更新");
  }

  async function runCollectionTask(task: CollectionTask) {
    const source = store.collectionSources.find(
      (item) => item.id === task.sourceId,
    );
    const rule = store.collectionRules.find((item) => item.id === task.ruleId);
    if (!source || !rule) return;
    if (!source.enabled) {
      setTaskRunMessage(`采集源已停用：${source.name}`);
      mutate((draft) => {
        const draftTask = draft.collectionTasks.find(
          (item) => item.id === task.id,
        );
        if (draftTask) {
          draftTask.status = "源已停用";
          draftTask.lastResult =
            source.notes || source.failureLog || "采集源已停用";
        }
      }, "采集源已停用");
      return;
    }
    if (!rule.enabled) {
      setTaskRunMessage(`采集规则已停用：${rule.name}`);
      return;
    }
      setTaskRunMessage(`正在刷新：${task.name}`);
    try {
      const result = await previewCollectionSource({
        url: source.url,
        sourceType: source.sourceType,
        limit: 20,
      });
      if (!result) {
        setTaskRunMessage(
          "当前没有配置 API 地址，RSS/API/网页采集需要通过本地 API 或 Cloudflare API 代理完成。",
        );
        return;
      }
      const filtered = filterPreviewItems(result.items, rule);
      const existingKeys = new Set(
        store.collectedItems.map((item) =>
          `${item.url || ""}|${item.title}`.toLowerCase(),
        ),
      );
      const imported = filtered
        .filter(
          (item) =>
            !existingKeys.has(`${item.url || ""}|${item.title}`.toLowerCase()),
        )
        .map((item) => importPreviewItem(item, source.id, "auto_collected"));
      mutate((draft) => {
        for (const item of imported.reverse())
          draft.collectedItems.unshift(item);
        const draftTask = draft.collectionTasks.find(
          (item) => item.id === task.id,
        );
        if (draftTask) {
          draftTask.lastRunAt = now();
          draftTask.lastResult = `抓取 ${result.items.length} 条，保留 ${filtered.length} 条，入库 ${imported.length} 条`;
          draftTask.status = "已刷新";
        }
        const draftSource = draft.collectionSources.find(
          (item) => item.id === source.id,
        );
        if (draftSource) {
          draftSource.lastCollectedAt = now();
          draftSource.failureLog = "";
        }
      }, `采集任务完成，入库 ${imported.length} 条`);
      setTaskRunMessage(
        `完成：抓取 ${result.items.length} 条，保留 ${filtered.length} 条，入库 ${imported.length} 条。`,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "未知错误";
      mutate((draft) => {
        const draftTask = draft.collectionTasks.find(
          (item) => item.id === task.id,
        );
        if (draftTask) {
          draftTask.lastRunAt = now();
          draftTask.lastResult = message;
          draftTask.status = "失败";
        }
        const draftSource = draft.collectionSources.find(
          (item) => item.id === source.id,
        );
        if (draftSource) draftSource.failureLog = message;
      }, "采集任务失败");
      setTaskRunMessage(`采集失败：${message}`);
    }
  }

  async function runBatchCollectionTasks() {
    const runnableTasks = store.collectionTasks.filter((task) => {
      const source = store.collectionSources.find(
        (item) => item.id === task.sourceId,
      );
      const rule = store.collectionRules.find(
        (item) => item.id === task.ruleId,
      );
      return task.enabled && source?.enabled && rule?.enabled;
    });
    if (!runnableTasks.length) {
      setTaskRunMessage(
        "没有可执行的启用任务。请先启用任务、采集源和采集规则。",
      );
      return;
    }
    if (!window.confirm(`确定批量执行 ${runnableTasks.length} 个采集任务吗？`))
      return;
    setTaskRunMessage(`批量采集开始：共 ${runnableTasks.length} 个任务。`);
    for (let index = 0; index < runnableTasks.length; index += 1) {
      setTaskRunMessage(
        `批量采集中：${index + 1}/${runnableTasks.length} - ${runnableTasks[index].name}`,
      );
      await runCollectionTask(runnableTasks[index]);
    }
    setTaskRunMessage(`批量采集完成：已执行 ${runnableTasks.length} 个任务。`);
  }

  function updateCollectedItem(itemId: string, title: string, body: string) {
    const cleanBody = cleanMarkdownContent(body);
    const summary = summarize(cleanBody);
    const keywords = extractKeywords(cleanBody);
    void updateRemote("collectedItems", itemId, {
      title,
      raw_content: cleanBody,
      summary,
      keywords: values(keywords),
    }).catch(() => undefined);
    mutate((draft) => {
      const found = draft.collectedItems.find((row) => row.id === itemId);
      if (!found) return;
      found.title = title;
      found.rawContent = cleanBody;
      found.summary = summary;
      found.keywords = keywords;
    }, "采集内容已更新");
  }
  async function importHistoryFiles(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = formValues(form);
    const input = form.elements.namedItem(
      "historyFiles",
    ) as HTMLInputElement | null;
    const files = Array.from(input?.files || []).filter((file) =>
      isSupportedHistoryFile(file),
    );
    if (!files.length) {
      setMdImportSummary("请选择 .md、.txt 或 .pdf 文件");
      return;
    }
    const imported: Array<{
      item: CollectedItem;
      asset: MaterialAsset;
      matches: DuplicateMatch[];
      warning?: string;
    }> = [];
    for (const file of files) {
      const parsed = await parseHistoryFile(file);
      const itemId = uid("col");
      const assetId = uid("asset");
      const contentHash = await fileSha256(file);
      const localMatches = localTextDuplicates(store, parsed.body);
      const remoteMatches = await checkDuplicates({
        kind: "text",
        text: parsed.body,
        platformId: data.platformId,
      }).catch(() => null);
      const matches = remoteMatches?.matches?.length
        ? remoteMatches.matches
        : localMatches;
      const archive = buildArchiveName({
        root: "history-files",
        businessLineName: nameOf(store.businessLines, data.businessLineId),
        platformName: data.platformId
          ? nameOf(store.platforms, data.platformId)
          : "wechat",
        type: parsed.assetType,
        title: parsed.title,
        idValue: assetId,
        extension: parsed.extension,
      });
      const filePath =
        (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
        file.name;
      void createRemote("collectedItems", {
        id: itemId,
        title: parsed.title,
        url: data.sourceUrl || filePath,
        raw_content: parsed.body,
        summary: summarize(parsed.body),
        keywords: values(parsed.keywords || extractKeywords(parsed.body)),
        language: "zh-CN",
        translation_status: "none",
        business_line_ids: values(data.businessLineId),
        content_type: data.contentType || "历史文件内容",
        suitable_platforms: values(data.platformId),
        duplicate_status: matches.length ? "similar" : "clear",
        duplicate_notes: matches
          .map((match) => `${match.title}:${Math.round(match.score * 100)}%`)
          .join("; "),
        status: "history",
      }).catch(() => undefined);
      void createRemote("materialAssets", {
        id: assetId,
        name: file.name,
        asset_type: parsed.assetType,
        mime_type: parsed.mimeType,
        size: file.size,
        source_url: filePath,
        business_line_id: data.businessLineId,
        platform_id: data.platformId,
        tags: values(parsed.keywords || extractKeywords(parsed.body)),
        content_hash: contentHash,
        duplicate_status: matches.length ? "duplicate" : "clear",
        duplicate_notes: matches.map((match) => match.title).join("; "),
        archive_dir: archive.archiveDir,
        file_name: archive.fileName,
        status: "history",
      }).catch(() => undefined);
      imported.push({
        item: {
          id: itemId,
          title: parsed.title,
          url: data.sourceUrl || filePath,
          rawContent: parsed.body,
          summary: summarize(parsed.body),
          keywords: parsed.keywords || extractKeywords(parsed.body),
          language: "zh-CN",
          translationStatus: "none",
          translatedTitle: "",
          translatedContent: "",
          status: matches.length ? "history_similar" : "history",
          createdAt: now(),
        },
        asset: {
          id: assetId,
          name: file.name,
          type: historyFileAssetLabel(parsed.assetType),
          path: filePath,
          tags: parsed.keywords || extractKeywords(parsed.body),
          businessLineId: data.businessLineId,
          platformId: data.platformId,
          mimeType: parsed.mimeType,
          size: file.size,
          storageKey: "",
          status: "history",
          contentHash,
          duplicateStatus: matches.length ? "duplicate" : "clear",
          duplicateNotes: matches.map((match) => match.title).join("; "),
          archiveDir: archive.archiveDir,
          fileName: archive.fileName,
          createdAt: now(),
        },
        matches,
        warning: parsed.warning,
      });
    }
    mutate((draft) => {
      for (const result of imported.reverse()) {
        upsertById(draft.collectedItems, result.item);
        upsertById(draft.assets, result.asset);
      }
    }, `已导入 ${imported.length} 个历史文件`);
    setSelectedCollectedId(imported[0]?.item.id || "");
    setDuplicateMatches(imported.flatMap((item) => item.matches).slice(0, 8));
    const warnings = imported.filter((item) => item.warning).length;
    setMdImportSummary(
      `已导入 ${imported.length} 个文件，其中 ${imported.filter((item) => item.matches.length).length} 个存在相似内容提醒${warnings ? `，${warnings} 个 PDF 需要后续转换或 OCR 才能完整提取正文` : ""}`,
    );
    form.reset();
  }
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    const itemId = uid("col");
    const localMatches = localTextDuplicates(store, data.rawContent);
    const remoteMatches = await checkDuplicates({
      kind: "text",
      text: data.rawContent,
    }).catch(() => null);
    const matches = remoteMatches?.matches?.length
      ? remoteMatches.matches
      : localMatches;
    setDuplicateMatches(matches);
    void createRemote("collectedItems", {
      id: itemId,
      title: data.title,
      url: data.url,
      raw_content: data.rawContent,
      summary: summarize(data.rawContent),
      keywords: values(extractKeywords(data.rawContent)),
      language: "zh-CN",
      translation_status: "none",
      duplicate_status: matches.length ? "similar" : "clear",
      duplicate_notes: matches
        .map((match) => `${match.title}:${Math.round(match.score * 100)}%`)
        .join("; "),
      status: "new",
    }).catch(() => undefined);
    mutate((draft) => {
      draft.collectedItems.unshift({
        id: itemId,
        title: data.title,
        url: data.url,
        rawContent: data.rawContent,
        summary: summarize(data.rawContent),
        keywords: extractKeywords(data.rawContent),
        language: "zh-CN",
        translationStatus: "none",
        translatedTitle: "",
        translatedContent: "",
        status: "new",
        createdAt: now(),
      });
    }, "采集内容已入库，并生成摘要与关键词");
    setSelectedCollectedId(itemId);
    event.currentTarget.reset();
  }

  function startCollectedEdit(item: CollectedItem) {
    setEditingCollectedId(item.id);
    setEditingCollectedTitle(item.title);
    setEditingCollectedBody(cleanMarkdownContent(item.rawContent));
  }

  function cancelCollectedEdit() {
    setEditingCollectedId("");
    setEditingCollectedTitle("");
    setEditingCollectedBody("");
  }

  function saveCollectedEdit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editingCollectedId) return;
    updateCollectedItem(
      editingCollectedId,
      editingCollectedTitle,
      editingCollectedBody,
    );
    cancelCollectedEdit();
  }

  function openCollectedItemTab(item: CollectedItem) {
    openHtmlTab(`${item.title} - MediaPilotX`, buildCollectedReaderHtml(item));
  }

  function deleteCollectedItem(item: CollectedItem) {
    if (!window.confirm(`确定删除采集内容「${item.title}」吗？`)) return;
    void deleteRemote("collectedItems", item.id).catch(() => undefined);
    mutate((draft) => {
      draft.collectedItems = draft.collectedItems.filter(
        (row) => row.id !== item.id,
      );
    }, "采集内容已删除");
    if (selectedCollectedId === item.id) setSelectedCollectedId("");
    if (editingCollectedId === item.id) cancelCollectedEdit();
  }

  function openTopicDialog(item: CollectedItem) {
    setTopicDialogItemId(item.id);
    setTopicDialogModelId(usableTopicModels[0]?.id || "rule");
    setTopicDialogSkill("builtin");
  }

  function closeTopicDialog(force = false) {
    if (topicGenerating && !force) return;
    setTopicDialogItemId("");
    setTopicDialogModelId("");
  }

  function openRewriteDialog(item: CollectedItem) {
    const firstPrompt = usableRewritePrompts[0];
    setRewriteDialogItemId(item.id);
    setRewriteModelId(usableRewriteModels[0]?.id || "local");
    setRewritePromptId(firstPrompt?.id || "");
    setRewritePromptText(firstPrompt?.prompt || "");
  }

  function closeRewriteDialog(force = false) {
    if (rewriteGenerating && !force) return;
    setRewriteDialogItemId("");
    setRewriteModelId("");
    setRewritePromptId("");
    setRewritePromptText("");
  }

  async function rewriteCollectedToMasterArticle(
    event: React.FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();
    if (!rewriteDialogItem) return;
    const form = new FormData(event.currentTarget);
    const modelId = String(form.get("modelId") || "local");
    const promptId = String(form.get("promptId") || "");
    const promptText = String(form.get("promptText") || "").trim();
    const extraRequirement = String(form.get("extraRequirement") || "").trim();
    const businessLineId =
      String(form.get("businessLineId") || "") ||
      store.businessLines[0]?.id ||
      "";
    const selectedModel = store.aiModels.find((model) => model.id === modelId);
    const selectedPrompt =
      store.aiRewritePrompts.find((prompt) => prompt.id === promptId) ||
      usableRewritePrompts[0];
    const effectivePrompt = selectedPrompt
      ? {
          ...selectedPrompt,
          prompt: promptText || selectedPrompt.prompt,
        }
      : undefined;
    const sourceBody = cleanMarkdownContent(rewriteDialogItem.rawContent);
    setRewriteGenerating(true);
    try {
      let generated:
        | {
            title?: string;
            body?: string;
            outline?: string;
          }
        | undefined;
      let fallbackReason = "";
      if (
        selectedModel &&
        selectedModel.baseUrl &&
        selectedModel.apiKey &&
        effectivePrompt
      ) {
        const result = await postRemote<{
          ok: boolean;
          article?: { title?: string; body?: string; outline?: string };
          error?: string;
        }>("/ai/rewrite-article", {
          provider: selectedModel.provider,
          name: selectedModel.name,
          code: selectedModel.code,
          baseUrl: selectedModel.baseUrl,
          apiKey: selectedModel.apiKey,
          sourceTitle: rewriteDialogItem.title,
          sourceUrl: rewriteDialogItem.url,
          sourceContent: sourceBody,
          prompt: effectivePrompt.prompt,
          promptCategory: effectivePrompt.category,
          extraRequirement,
        });
        if (result?.ok && result.article?.body) {
          generated = result.article;
        } else {
          fallbackReason = result?.error || "AI 未返回可用正文";
        }
      }
      createMasterArticleFromCollectedItem(rewriteDialogItem, {
        businessLineId,
        prompt: effectivePrompt,
        model: selectedModel,
        extraRequirement,
        title: generated?.title,
        body: generated?.body,
        outline: generated?.outline,
        usedAi: Boolean(generated),
        fallbackReason,
      });
      closeRewriteDialog(true);
    } catch (error) {
      createMasterArticleFromCollectedItem(rewriteDialogItem, {
        businessLineId,
        prompt: effectivePrompt,
        extraRequirement,
        usedAi: false,
        fallbackReason: error instanceof Error ? error.message : "AI 调用失败",
      });
      closeRewriteDialog(true);
    } finally {
      setRewriteGenerating(false);
    }
  }

  async function convertCollectedToTopic(
    event: React.FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();
    if (!topicDialogItem) return;
    const form = new FormData(event.currentTarget);
    const modelId = String(form.get("modelId") || "rule");
    const skill = String(form.get("skill") || "");
    const businessLineId =
      String(form.get("businessLineId") || "") ||
      store.businessLines[0]?.id ||
      "";
    const selectedModel = store.aiModels.find((model) => model.id === modelId);
    setTopicGenerating(true);
    try {
      let generated:
        | {
            title?: string;
            contentType?: string;
            keywords?: string[];
            priority?: string;
            reason?: string;
            angle?: string;
          }
        | undefined;
      const body = cleanMarkdownContent(topicDialogItem.rawContent);
      if (selectedModel && selectedModel.baseUrl && selectedModel.apiKey) {
        const result = await postRemote<{
          ok: boolean;
          topic?: {
            title?: string;
            contentType?: string;
            keywords?: string[];
            priority?: string;
            reason?: string;
            angle?: string;
          };
          error?: string;
        }>("/ai/generate-topic", {
          provider: selectedModel.provider,
          name: selectedModel.name,
          code: selectedModel.code,
          baseUrl: selectedModel.baseUrl,
          apiKey: selectedModel.apiKey,
          title: topicDialogItem.title,
          content: body,
          sourceUrl: topicDialogItem.url,
          contentTypes,
          skill,
        });
        if (result?.ok && result.topic?.title) generated = result.topic;
      }
      createTopicFromCollectedItem(topicDialogItem, {
        businessLineId,
        title: generated?.title,
        contentType: generated?.contentType,
        keywords: generated?.keywords,
        priority: generated?.priority,
        reason: generated?.reason,
        angle: generated?.angle,
        usedAi: Boolean(generated),
      });
      closeTopicDialog(true);
    } catch {
      createTopicFromCollectedItem(topicDialogItem, {
        businessLineId,
        usedAi: false,
      });
      closeTopicDialog(true);
    } finally {
      setTopicGenerating(false);
    }
  }

  function createTopicFromCollectedItem(
    item: CollectedItem,
    options: {
      businessLineId: string;
      title?: string;
      contentType?: string;
      keywords?: string[];
      priority?: string;
      reason?: string;
      angle?: string;
      usedAi?: boolean;
    },
  ) {
    const title = (options.title || item.title).trim();
    const existing = store.topics.find((topic) => topic.title.trim() === title);
    if (existing) {
      setActive("topics");
      mutate(() => undefined, "选题池已有同名选题，已切换到选题列表");
      return;
    }
    const body = cleanMarkdownContent(item.rawContent);
    const keywords =
      options.keywords?.filter(Boolean).join(",") ||
      item.keywords ||
      extractKeywords(`${item.title} ${body}`);
    const topicId = uid("topic");
    const contentType = contentTypes.includes(options.contentType || "")
      ? options.contentType || ""
      : inferTopicContentType(`${item.title} ${body}`);
    const priority = ["high", "medium", "low"].includes(options.priority || "")
      ? (options.priority as Topic["priority"])
      : "medium";
    const remark = [options.reason, options.angle].filter(Boolean).join("\n");
    void createRemote("topics", {
      id: topicId,
      title,
      source_type: "collected_item",
      source_id: item.id,
      business_line_id: options.businessLineId,
      content_type: contentType,
      keywords: values(keywords),
      priority,
      status: "pending",
      remark,
    }).catch(() => undefined);
    mutate(
      (draft) => {
        draft.topics.unshift({
          id: topicId,
          title,
          businessLineId: options.businessLineId,
          contentType,
          keywords,
          priority,
          status: "pending",
          createdAt: now(),
        });
      },
      options.usedAi ? "已通过 AI 生成选题" : "已通过规则生成选题",
    );
    setActive("topics");
  }

  function createMasterArticleFromCollectedItem(
    item: CollectedItem,
    options: {
      businessLineId: string;
      prompt?: AiRewritePrompt;
      model?: AiModel;
      extraRequirement?: string;
      title?: string;
      body?: string;
      outline?: string;
      usedAi?: boolean;
      fallbackReason?: string;
    },
  ) {
    const sourceBody = cleanMarkdownContent(item.rawContent);
    const articleId = uid("ma");
    const title = (options.title || `${item.title}｜公众号洗稿版`).trim();
    const body =
      options.body ||
      buildLocalRewrittenArticle(
        item.title,
        sourceBody,
        options.prompt,
        options.extraRequirement,
      );
    const outline =
      options.outline ||
      [
        "1. 事件/知识背景",
        "2. 核心信息梳理",
        "3. 对外贸企业的影响",
        "4. 操作建议与检查清单",
      ].join("\n");
    void createRemote("masterArticles", {
      id: articleId,
      content_task_id: "",
      title,
      outline,
      body_markdown: body,
      body_html: markdownToHtml(body),
      status: "待审核",
      word_count: body.length,
      ai_generated: options.usedAi ? 1 : 0,
    }).catch(() => undefined);
    mutate(
      (draft) => {
        draft.masterArticles.unshift({
          id: articleId,
          taskId: "",
          title,
          outline,
          body,
          status: "待审核",
          aiGenerated: Boolean(options.usedAi || options.model),
          createdAt: now(),
        });
        draft.aiLogs.unshift(
          aiLog(
            item.id,
            "rewrite_article",
            [
              `来源：${item.title}`,
              `提示词：${options.prompt?.name || "本地规则"}`,
              `补充要求：${options.extraRequirement || "无"}`,
              sourceBody.slice(0, 1200),
            ].join("\n\n"),
            body,
          ),
        );
      },
      options.usedAi ? "已通过 AI 洗稿生成母版文章" : "已通过本地规则生成母版文章",
    );
    if (options.fallbackReason) {
      mutate(() => undefined, `AI洗稿未调用成功，已改用本地规则：${options.fallbackReason}`);
    }
    setActive("master");
    setMasterSub("list");
  }

  return (
    <section className="grid collectionWorkspace">
      {view === "history" ? (
        <Panel title="历史文件导入">
          <form className="form" onSubmit={importHistoryFiles}>
            <label>
              历史文件
              <input
                name="historyFiles"
                type="file"
                accept=".md,.txt,.pdf,text/markdown,text/plain,application/pdf"
                multiple
                required
              />
            </label>
            <label>
              业务线
              <Select name="businessLineId" rows={store.businessLines} />
            </label>
            <label>
              来源平台
              <select name="platformId">
                <option value="">公众号 · 未指定</option>
                {store.platforms.map((platform) => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              内容类型
              <select name="contentType">
                <option>历史公众号文章</option>
                {contentTypes.map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </label>
            <label>
              来源备注
              <input
                name="sourceUrl"
                placeholder="可填公众号合集链接或本地目录备注"
              />
            </label>
            <DuplicateNotice matches={duplicateMatches} />
            {mdImportSummary ? (
              <p className="importSummary">{mdImportSummary}</p>
            ) : null}
            <button type="submit">
              <Plus size={16} />
              导入历史文件
            </button>
          </form>
        </Panel>
      ) : null}
      {view === "new" ? (
        <Panel title="新增采集">
          <form className="form" onSubmit={submit}>
            <label>
              标题
              <input name="title" required />
            </label>
            <label>
              原文链接
              <input name="url" placeholder="可选" />
            </label>
            <label>
              正文
              <textarea name="rawContent" rows={8} required />
            </label>
            <DuplicateNotice matches={duplicateMatches} />
            <button type="submit">
              <Plus size={16} />
              导入并处理
            </button>
          </form>
        </Panel>
      ) : null}
      {view === "list" ? (
        <Panel title="采集列表">
          <div className="tableWrap collectionTable">
            <table>
              <thead>
                <tr>
                  <th>标题</th>
                  <th>摘要</th>
                  <th>关键词</th>
                  <th>语言/翻译</th>
                  <th>状态</th>
                  <th>入库时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {store.collectedItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <button
                        className="linkButton"
                        onClick={() => openCollectedItemTab(item)}
                      >
                        {item.title}
                      </button>
                    </td>
                    <td>{collectedSummary(item)}</td>
                    <td>{item.keywords || "暂无"}</td>
                    <td>
                      <div className="stackedCell compact">
                        <span>{collectionLanguageLabel(item.language)}</span>
                        <span>
                          {translationStatusLabel(item.translationStatus)}
                        </span>
                      </div>
                    </td>
                    <td>{collectionStatusLabel(item.status)}</td>
                    <td>{formatDateTime(item.createdAt)}</td>
                    <td>
                      <div className="rowActions">
                        <button
                          className="inlineButton"
                          onClick={() => openCollectedItemTab(item)}
                        >
                          查看
                        </button>
                        <button
                          className="inlineButton"
                          onClick={() => startCollectedEdit(item)}
                        >
                          编辑 Markdown
                        </button>
                        <button
                          className="inlineButton"
                          onClick={() => openTopicDialog(item)}
                        >
                          转选题
                        </button>
                        <button
                          className="inlineButton"
                          onClick={() => openRewriteDialog(item)}
                        >
                          AI洗稿
                        </button>
                        <button
                          className="inlineButton"
                          onClick={() =>
                            navigator.clipboard?.writeText(
                              cleanMarkdownContent(item.rawContent),
                            )
                          }
                        >
                          复制正文
                        </button>
                        <button
                          className="dangerButton inlineDanger"
                          onClick={() => deleteCollectedItem(item)}
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!store.collectedItems.length ? (
            <p className="muted">暂无采集内容。</p>
          ) : null}
        </Panel>
      ) : null}
      {topicDialogItem ? (
        <div className="modalOverlay">
          <form
            className="modalPanel modelModal"
            onSubmit={convertCollectedToTopic}
          >
            <div className="modalHeader">
              <div>
                <strong>采集内容转选题</strong>
                <span>选择 AI 模型和 Skill 后生成选题。</span>
              </div>
              <button
                type="button"
                className="ghost"
                onClick={() => closeTopicDialog()}
                disabled={topicGenerating}
              >
                关闭
              </button>
            </div>
            <div className="form modelForm">
              <label>
                采集内容
                <input value={topicDialogItem.title} readOnly />
              </label>
              <label>
                AI 模型
                <select
                  name="modelId"
                  value={topicDialogModelId || "rule"}
                  onChange={(event) =>
                    setTopicDialogModelId(event.target.value)
                  }
                >
                  <option value="rule">规则生成（不使用 AI）</option>
                  {store.aiModels.map((model) => (
                    <option
                      key={model.id}
                      value={model.id}
                      disabled={
                        !model.enabled || !model.baseUrl || !model.apiKey
                      }
                    >
                      {model.name} · {model.code}
                      {!model.apiKey ? " · 未填 Key" : ""}
                      {!model.enabled ? " · 已停用" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Skill
                <select
                  name="skill"
                  value={topicDialogSkill}
                  onChange={(event) => setTopicDialogSkill(event.target.value)}
                >
                  <option value="builtin">内置专业选题分析</option>
                  <option value="">暂不选择 Skill</option>
                </select>
              </label>
              <label>
                业务线
                <Select name="businessLineId" rows={store.businessLines} />
              </label>
              <label>
                内容预览
                <textarea
                  rows={5}
                  value={cleanMarkdownContent(topicDialogItem.rawContent).slice(
                    0,
                    800,
                  )}
                  readOnly
                />
              </label>
            </div>
            <div className="modalActions">
              <button
                type="button"
                className="ghost"
                onClick={() => closeTopicDialog()}
                disabled={topicGenerating}
              >
                取消
              </button>
              <button
                type="submit"
                className="primaryButton"
                disabled={topicGenerating}
              >
                {topicGenerating ? "生成中..." : "生成选题"}
              </button>
            </div>
          </form>
        </div>
      ) : null}
      {rewriteDialogItem ? (
        <div className="modalOverlay">
          <form
            className="modalPanel modelModal"
            onSubmit={rewriteCollectedToMasterArticle}
          >
            <div className="modalHeader">
              <div>
                <strong>AI洗稿生成公众号文章</strong>
                <span>选择模型和提示词，生成结果会进入母版文章待审核。</span>
              </div>
              <button
                type="button"
                className="ghost"
                onClick={() => closeRewriteDialog()}
                disabled={rewriteGenerating}
              >
                关闭
              </button>
            </div>
            <div className="form modelForm">
              <label>
                采集内容
                <input value={rewriteDialogItem.title} readOnly />
              </label>
              <label>
                AI 模型
                <select
                  name="modelId"
                  value={rewriteModelId || "local"}
                  onChange={(event) => setRewriteModelId(event.target.value)}
                >
                  <option value="local">本地规则生成（不调用 AI）</option>
                  {store.aiModels.map((model) => (
                    <option
                      key={model.id}
                      value={model.id}
                      disabled={
                        !model.enabled || !model.baseUrl || !model.apiKey
                      }
                    >
                      {model.name} · {model.code}
                      {!model.apiKey ? " · 未填 Key" : ""}
                      {!model.enabled ? " · 已停用" : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                AI提示词
                <select
                  name="promptId"
                  value={rewritePromptId}
                  onChange={(event) => {
                    const nextPrompt = store.aiRewritePrompts.find(
                      (prompt) => prompt.id === event.target.value,
                    );
                    setRewritePromptId(event.target.value);
                    setRewritePromptText(nextPrompt?.prompt || "");
                  }}
                  required
                >
                  {usableRewritePrompts.map((prompt) => (
                    <option key={prompt.id} value={prompt.id}>
                      {prompt.sequence}. {prompt.category} · {prompt.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="span2">
                提示词详情
                <textarea
                  name="promptText"
                  rows={8}
                  value={rewritePromptText}
                  onChange={(event) => setRewritePromptText(event.target.value)}
                  placeholder="选择提示词后会自动带出正文，也可以临时修改。本次生成会使用这里的内容。"
                  required
                />
              </label>
              <label>
                业务线
                <Select name="businessLineId" rows={store.businessLines} />
              </label>
              <label>
                补充要求
                <textarea
                  name="extraRequirement"
                  rows={3}
                  placeholder="例如：公众号风格，1500字左右，标题更吸引人，保留关键数据"
                />
              </label>
              <label>
                原文预览
                <textarea
                  rows={5}
                  value={cleanMarkdownContent(rewriteDialogItem.rawContent).slice(
                    0,
                    1000,
                  )}
                  readOnly
                />
              </label>
            </div>
            <div className="modalActions">
              <button
                type="button"
                className="ghost"
                onClick={() => closeRewriteDialog()}
                disabled={rewriteGenerating}
              >
                取消
              </button>
              <button
                type="submit"
                className="primaryButton"
                disabled={rewriteGenerating || !usableRewritePrompts.length}
              >
                {rewriteGenerating ? "生成中..." : "生成母版文章"}
              </button>
            </div>
          </form>
        </div>
      ) : null}
      {view === "new" ? (
        <Panel title="公众号/网页链接采集">
          <div className="inlineCollectBox">
            <div>
              <strong>粘贴链接采集</strong>
              <span>适合公众号文章和普通网页，采集成功后会进入采集列表。</span>
            </div>
            <input
              value={linkImportUrl}
              onChange={(event) => setLinkImportUrl(event.target.value)}
              placeholder="https://mp.weixin.qq.com/s/..."
            />
            <button
              type="button"
              className="primaryButton"
              onClick={collectLink}
            >
              <Plus size={16} />
              采集链接
            </button>
          </div>
          {linkImportMessage ? (
            <p className="importSummary">{linkImportMessage}</p>
          ) : null}
        </Panel>
      ) : null}
      {view === "sources" ? (
        <CollectionSourcesPanel
          store={store}
          sourceFormOpen={sourceFormOpen}
          editingSource={editingSource}
          setSourceFormOpen={setSourceFormOpen}
          setEditingSourceId={setEditingSourceId}
          saveSource={saveSource}
          deleteSource={deleteSource}
        />
      ) : null}
      {view === "rules" ? (
        <CollectionRulesPanel
          store={store}
          ruleFormOpen={ruleFormOpen}
          editingRule={editingRule}
          setRuleFormOpen={setRuleFormOpen}
          setEditingRuleId={setEditingRuleId}
          saveRule={saveRule}
          deleteRule={deleteRule}
        />
      ) : null}
      {view === "tasks" ? (
        <CollectionTasksPanel
          store={store}
          taskFormOpen={taskFormOpen}
          editingTask={editingTask}
          taskRunMessage={taskRunMessage}
          setTaskFormOpen={setTaskFormOpen}
          setEditingTaskId={setEditingTaskId}
          saveTask={saveTask}
          deleteTask={deleteTask}
          toggleTask={toggleTask}
          runCollectionTask={runCollectionTask}
          runBatchCollectionTasks={runBatchCollectionTasks}
        />
      ) : null}
      {editingCollectedId ? (
        <div className="modalOverlay">
          <form className="modalPanel" onSubmit={saveCollectedEdit}>
            <div className="modalHeader">
              <div>
                <strong>编辑 Markdown</strong>
                <span>保存后会立即更新采集列表、摘要和关键词。</span>
              </div>
              <button
                type="button"
                className="ghost"
                onClick={cancelCollectedEdit}
              >
                关闭
              </button>
            </div>
            <label>
              标题
              <input
                value={editingCollectedTitle}
                onChange={(event) =>
                  setEditingCollectedTitle(event.target.value)
                }
                required
              />
            </label>
            <div className="markdownEditor">
              <label>
                Markdown 正文
                <textarea
                  value={editingCollectedBody}
                  onChange={(event) =>
                    setEditingCollectedBody(event.target.value)
                  }
                />
              </label>
              <div>
                <strong>富文本预览</strong>
                <article
                  className="markdownPreview"
                  dangerouslySetInnerHTML={{
                    __html: markdownToHtml(editingCollectedBody),
                  }}
                />
              </div>
            </div>
            <div className="rowActions">
              <button type="submit">
                <CheckCircle2 size={16} />
                保存修改
              </button>
              <button
                type="button"
                className="inlineButton"
                onClick={cancelCollectedEdit}
              >
                取消
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </section>
  );
}

function CollectionSourcesPanel({
  store,
  sourceFormOpen,
  editingSource,
  setSourceFormOpen,
  setEditingSourceId,
  saveSource,
  deleteSource,
}: {
  store: Store;
  sourceFormOpen: boolean;
  editingSource?: CollectionSource;
  setSourceFormOpen: (value: boolean) => void;
  setEditingSourceId: (value: string) => void;
  saveSource: (event: React.FormEvent<HTMLFormElement>) => void;
  deleteSource: (id: string) => void;
}) {
  return (
    <Panel title="采集源管理">
      <div className="listToolbar">
        <div className="filterBar single">
          <button
            className="inlineButton"
            onClick={() =>
              downloadText(
                "mediapilotx-collection-sources.json",
                JSON.stringify(store.collectionSources, null, 2),
                "application/json;charset=utf-8",
              )
            }
          >
            导出采集源
          </button>
        </div>
        <button
          type="button"
          className="primaryButton"
          onClick={() => {
            setEditingSourceId("");
            setSourceFormOpen(true);
          }}
        >
          <Plus size={16} />
          新增采集源
        </button>
      </div>
      {sourceFormOpen || editingSource ? (
        <form
          className="form collectionConfigForm"
          key={editingSource?.id || "new-source"}
          onSubmit={saveSource}
        >
          <label>
            鏉ユ簮鍚嶇О
            <input
              name="name"
              required
              defaultValue={editingSource?.name || ""}
            />
          </label>
          <label>
            分类
            <input
              name="category"
              defaultValue={editingSource?.category || ""}
            />
          </label>
          <label>
            采集方式
            <select
              name="sourceType"
              defaultValue={editingSource?.sourceType || "rss"}
            >
              <option value="rss">RSS</option>
              <option value="atom">Atom</option>
              <option value="rdf">RDF</option>
              <option value="api">API</option>
              <option value="website">网站</option>
              <option value="wechat">公众号链接</option>
            </select>
          </label>
          <label>
            URL / 接口
            <input
              name="url"
              required
              defaultValue={editingSource?.url || ""}
            />
          </label>
          <label>
            语言
            <select
              name="language"
              defaultValue={editingSource?.language || "auto"}
            >
              {collectionLanguages.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            备注
            <input name="notes" defaultValue={editingSource?.notes || ""} />
          </label>
          <label className="checkLine">
            <input
              name="enabled"
              type="checkbox"
              defaultChecked={editingSource?.enabled ?? true}
            />
            启用
          </label>
          <div className="rowActions">
            <button type="submit">
              <CheckCircle2 size={16} />
              保存采集源
            </button>
            <button
              type="button"
              className="inlineButton"
              onClick={() => {
                setSourceFormOpen(false);
                setEditingSourceId("");
              }}
            >
              取消
            </button>
          </div>
        </form>
      ) : null}
      <div className="tableWrap">
        <table>
          <thead>
            <tr>
              <th>来源</th>
              <th>分类</th>
              <th>方式</th>
              <th>语言</th>
              <th>状态</th>
              <th>最近采集</th>
              <th>备注/错误</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {store.collectionSources.map((source) => (
              <tr key={source.id}>
                <td>
                  <div className="stackedCell">
                    <strong>{source.name}</strong>
                    <span>{source.url}</span>
                  </div>
                </td>
                <td>{source.category || "未分类"}</td>
                <td>{source.sourceType.toUpperCase()}</td>
                <td>{collectionLanguageLabel(source.language)}</td>
                <td>{source.enabled ? "启用" : "停用"}</td>
                <td>{formatDateTime(source.lastCollectedAt)}</td>
                <td>{source.failureLog || source.notes || "无"}</td>
                <td>
                  <div className="rowActions">
                    <button
                      className="inlineButton"
                      onClick={() => {
                        setEditingSourceId(source.id);
                        setSourceFormOpen(true);
                      }}
                    >
                      修改
                    </button>
                    <button
                      className="dangerButton inlineDanger"
                      onClick={() => deleteSource(source.id)}
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function CollectionRulesPanel({
  store,
  ruleFormOpen,
  editingRule,
  setRuleFormOpen,
  setEditingRuleId,
  saveRule,
  deleteRule,
}: {
  store: Store;
  ruleFormOpen: boolean;
  editingRule?: CollectionRule;
  setRuleFormOpen: (value: boolean) => void;
  setEditingRuleId: (value: string) => void;
  saveRule: (event: React.FormEvent<HTMLFormElement>) => void;
  deleteRule: (id: string) => void;
}) {
  return (
    <Panel title="采集规则">
      <div className="listToolbar">
        <div className="filterBar single">
          <span className="muted">规则决定采集后保留哪些内容。</span>
        </div>
        <button
          type="button"
          className="primaryButton"
          onClick={() => {
            setEditingRuleId("");
            setRuleFormOpen(true);
          }}
        >
          <Plus size={16} />
          新增规则
        </button>
      </div>
      {ruleFormOpen || editingRule ? (
        <form
          className="form collectionConfigForm"
          key={editingRule?.id || "new-rule"}
          onSubmit={saveRule}
        >
          <label>
            规则名称
            <input
              name="name"
              required
              defaultValue={editingRule?.name || ""}
            />
          </label>
          <label>
            保留模式
            <select name="mode" defaultValue={editingRule?.mode || "keyword"}>
              <option value="keyword">命中关键词</option>
              <option value="all">全部保留</option>
            </select>
          </label>
          <label>
            保留关键词
            <textarea
              name="keywords"
              rows={4}
              defaultValue={editingRule?.keywords || defaultCollectionKeywords}
            />
          </label>
          <label>
            排除关键词
            <textarea
              name="excludeKeywords"
              rows={3}
              defaultValue={editingRule?.excludeKeywords || ""}
            />
          </label>
          <label>
            默认业务线
            <Select
              name="businessLineId"
              rows={store.businessLines}
              value={editingRule?.businessLineId || undefined}
            />
          </label>
          <label>
            内容类型
            <select
              name="contentType"
              defaultValue={editingRule?.contentType || contentTypes[0]}
            >
              {contentTypes.map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </label>
          <label className="checkLine">
            <input
              name="autoAi"
              type="checkbox"
              defaultChecked={editingRule?.autoAi || false}
            />
            采集后 AI 分析
          </label>
          <label className="checkLine">
            <input
              name="autoTopic"
              type="checkbox"
              defaultChecked={editingRule?.autoTopic || false}
            />
            自动生成选题
          </label>
          <label className="checkLine">
            <input
              name="enabled"
              type="checkbox"
              defaultChecked={editingRule?.enabled ?? true}
            />
            启用
          </label>
          <div className="rowActions">
            <button type="submit">
              <CheckCircle2 size={16} />
              保存规则
            </button>
            <button
              type="button"
              className="inlineButton"
              onClick={() => {
                setRuleFormOpen(false);
                setEditingRuleId("");
              }}
            >
              取消
            </button>
          </div>
        </form>
      ) : null}
      <div className="tableWrap">
        <table>
          <thead>
            <tr>
              <th>规则</th>
              <th>模式</th>
              <th>关键词</th>
              <th>自动处理</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {store.collectionRules.map((rule) => (
              <tr key={rule.id}>
                <td>{rule.name}</td>
                <td>{rule.mode === "all" ? "全部" : "关键词"}</td>
                <td>{rule.keywords}</td>
                <td>
                  {[
                    rule.autoAi ? "AI分析" : "",
                    rule.autoTopic ? "生成选题" : "",
                  ]
                    .filter(Boolean)
                    .join("、") || "关闭"}
                </td>
                <td>{rule.enabled ? "启用" : "停用"}</td>
                <td>
                  <div className="rowActions">
                    <button
                      className="inlineButton"
                      onClick={() => {
                        setEditingRuleId(rule.id);
                        setRuleFormOpen(true);
                      }}
                    >
                      修改
                    </button>
                    <button
                      className="dangerButton inlineDanger"
                      onClick={() => deleteRule(rule.id)}
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function CollectionTasksPanel({
  store,
  taskFormOpen,
  editingTask,
  taskRunMessage,
  setTaskFormOpen,
  setEditingTaskId,
  saveTask,
  deleteTask,
  toggleTask,
  runCollectionTask,
  runBatchCollectionTasks,
}: {
  store: Store;
  taskFormOpen: boolean;
  editingTask?: CollectionTask;
  taskRunMessage: string;
  setTaskFormOpen: (value: boolean) => void;
  setEditingTaskId: (value: string) => void;
  saveTask: (event: React.FormEvent<HTMLFormElement>) => void;
  deleteTask: (id: string) => void;
  toggleTask: (id: string) => void;
  runCollectionTask: (task: CollectionTask) => void;
  runBatchCollectionTasks: () => void;
}) {
  return (
    <Panel title="采集任务">
      <div className="listToolbar">
        <div className="filterBar single">
          {taskRunMessage ? (
            <span className="muted">{taskRunMessage}</span>
          ) : (
            <span className="muted">任务可启停，也可以手动刷新。</span>
          )}
        </div>
        <div className="toolbarActions">
          <button
            type="button"
            className="primaryButton"
            onClick={runBatchCollectionTasks}
          >
            <Download size={16} />
            批量采集
          </button>
          <button
            type="button"
            className="primaryButton"
            onClick={() => {
              setEditingTaskId("");
              setTaskFormOpen(true);
            }}
          >
            <Plus size={16} />
            新增任务
          </button>
        </div>
      </div>
      {taskFormOpen || editingTask ? (
        <form
          className="form collectionConfigForm"
          key={editingTask?.id || "new-task"}
          onSubmit={saveTask}
        >
          <label>
            任务名称
            <input
              name="name"
              required
              defaultValue={editingTask?.name || ""}
            />
          </label>
          <label>
            采集源
            <select
              name="sourceId"
              required
              defaultValue={
                editingTask?.sourceId || store.collectionSources[0]?.id || ""
              }
            >
              {store.collectionSources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            采集规则
            <select
              name="ruleId"
              required
              defaultValue={
                editingTask?.ruleId || store.collectionRules[0]?.id || ""
              }
            >
              {store.collectionRules.map((rule) => (
                <option key={rule.id} value={rule.id}>
                  {rule.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            计划
            <select
              name="schedule"
              defaultValue={editingTask?.schedule || "manual"}
            >
              <option value="manual">手动</option>
              <option value="hourly">每小时</option>
              <option value="daily">每天</option>
              <option value="weekly">每周</option>
            </select>
          </label>
          <label className="checkLine">
            <input
              name="enabled"
              type="checkbox"
              defaultChecked={editingTask?.enabled ?? false}
            />
            启用
          </label>
          <div className="rowActions">
            <button type="submit">
              <CheckCircle2 size={16} />
              保存任务
            </button>
            <button
              type="button"
              className="inlineButton"
              onClick={() => {
                setTaskFormOpen(false);
                setEditingTaskId("");
              }}
            >
              取消
            </button>
          </div>
        </form>
      ) : null}
      <div className="tableWrap">
        <table>
          <thead>
            <tr>
              <th>任务</th>
              <th>采集源</th>
              <th>规则</th>
              <th>计划</th>
              <th>状态</th>
              <th>最近结果</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {store.collectionTasks.map((task) => (
              <tr key={task.id}>
                <td>{task.name}</td>
                <td>{nameOf(store.collectionSources, task.sourceId)}</td>
                <td>{nameOf(store.collectionRules, task.ruleId)}</td>
                <td>{collectionScheduleLabel(task.schedule)}</td>
                <td>
                  {task.enabled ? "启用" : "停用"} · {task.status}
                </td>
                <td>
                  <div className="stackedCell">
                    <span>{formatDateTime(task.lastRunAt)}</span>
                    <span>{task.lastResult || "未执行"}</span>
                  </div>
                </td>
                <td>
                  <div className="rowActions">
                    <button
                      className="inlineButton"
                      onClick={() => runCollectionTask(task)}
                    >
                      手动刷新
                    </button>
                    <button
                      className="inlineButton"
                      onClick={() => toggleTask(task.id)}
                    >
                      {task.enabled ? "停用" : "启用"}
                    </button>
                    <button
                      className="inlineButton"
                      onClick={() => {
                        setEditingTaskId(task.id);
                        setTaskFormOpen(true);
                      }}
                    >
                      修改
                    </button>
                    <button
                      className="dangerButton inlineDanger"
                      onClick={() => deleteTask(task.id)}
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function Topics({ store, mutate, view }: Ctx & { view: TopicSubKey }) {
  const [editingTopicId, setEditingTopicId] = React.useState("");
  const [topicFormOpen, setTopicFormOpen] = React.useState(view === "new");
  const editingTopic = store.topics.find((item) => item.id === editingTopicId);

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = formValues(form);
    const topicId = editingTopic?.id || uid("topic");
    const payload = {
      id: topicId,
      title: data.title,
      business_line_id: data.businessLineId,
      content_type: data.contentType,
      keywords: values(data.keywords),
      priority: data.priority,
      status: data.status || editingTopic?.status || "pending",
    };
    const remote = editingTopic
      ? updateRemote("topics", topicId, payload)
      : createRemote("topics", payload);
    void remote.catch(() => undefined);
    mutate(
      (draft) => {
        upsertById(draft.topics, {
          id: topicId,
          title: data.title,
          businessLineId: data.businessLineId,
          contentType: data.contentType,
          keywords: data.keywords,
          priority: data.priority as Topic["priority"],
          status: (data.status ||
            editingTopic?.status ||
            "pending") as Topic["status"],
          createdAt: editingTopic?.createdAt || now(),
        });
      },
      editingTopic ? "选题已更新" : "选题已加入选题池",
    );
    setEditingTopicId("");
    setTopicFormOpen(false);
    form.reset();
  }

  function openNewTopicForm() {
    setEditingTopicId("");
    setTopicFormOpen(true);
  }

  function openEditTopicForm(topicId: string) {
    setEditingTopicId(topicId);
    setTopicFormOpen(true);
  }

  function closeTopicForm() {
    setEditingTopicId("");
    setTopicFormOpen(false);
  }

  function deleteTopic(topicId: string) {
    if (!window.confirm("确定删除这个选题吗？")) return;
    void deleteRemote("topics", topicId).catch(() => undefined);
    mutate((draft) => {
      draft.topics = draft.topics.filter((item) => item.id !== topicId);
    }, "选题已删除");
    if (editingTopicId === topicId) closeTopicForm();
  }

  function convert(topic: Topic) {
    mutate((draft) => {
      draft.tasks.unshift({
        id: uid("task"),
        title: topic.title,
        topicId: topic.id,
        businessLineId: topic.businessLineId,
        contentType: topic.contentType,
        status: "鑽夌",
        targetPlatformIds: [],
        targetAccountIds: [],
        direction: `围绕关键词：${topic.keywords}`,
        outline: "",
        dueDate: "",
        createdAt: now(),
      });
      const found = draft.topics.find((item) => item.id === topic.id);
      if (found) found.status = "converted";
    }, "已从选题创建内容任务");
  }
  return (
    <section className="grid collectionWorkspace">
      {view === "new" || topicFormOpen || editingTopic ? (
        <Panel title={editingTopic ? "修改选题" : "新建选题"}>
          <form
            className="form topicForm"
            key={editingTopic?.id || "new-topic"}
            onSubmit={submit}
          >
            <label>
              选题标题
              <input
                name="title"
                required
                defaultValue={editingTopic?.title || ""}
              />
            </label>
            <label>
              业务线
              <Select
                name="businessLineId"
                rows={store.businessLines}
                value={editingTopic?.businessLineId || undefined}
              />
            </label>
            <label>
              内容类型
              <select
                name="contentType"
                defaultValue={editingTopic?.contentType || contentTypes[0]}
              >
                {contentTypes.map((item) => (
                  <option key={item}>{item}</option>
                ))}
              </select>
            </label>
            <label>
              关键词
              <input
                name="keywords"
                defaultValue={editingTopic?.keywords || ""}
              />
            </label>
            <label>
              优先级
              <select
                name="priority"
                defaultValue={editingTopic?.priority || "medium"}
              >
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="low">low</option>
              </select>
            </label>
            <label>
              状态
              <select
                name="status"
                defaultValue={editingTopic?.status || "pending"}
              >
                <option value="pending">待处理</option>
                <option value="approved">已确认</option>
                <option value="converted">已转任务</option>
              </select>
            </label>
            <div className="rowActions">
              <button type="submit">
                <Plus size={16} />
                保存选题
              </button>
              <button
                type="button"
                className="inlineButton"
                onClick={closeTopicForm}
              >
                取消
              </button>
            </div>
          </form>
        </Panel>
      ) : null}
      {view === "list" ? (
        <Panel title="选题列表">
          <div className="listToolbar">
            <div className="filterBar single">
              <button
                className="inlineButton"
                onClick={() =>
                  downloadText(
                    `mediapilotx-topics-${new Date().toISOString().slice(0, 10)}.json`,
                    JSON.stringify(store.topics, null, 2),
                    "application/json;charset=utf-8",
                  )
                }
              >
                导出选题列表
              </button>
            </div>
            <button
              type="button"
              className="primaryButton"
              onClick={openNewTopicForm}
            >
              <Plus size={16} />
              新增选题
            </button>
          </div>
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>选题</th>
                  <th>业务线</th>
                  <th>内容类型</th>
                  <th>关键词</th>
                  <th>优先级</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {store.topics.map((topic) => (
                  <tr key={topic.id}>
                    <td>{topic.title}</td>
                    <td>{nameOf(store.businessLines, topic.businessLineId)}</td>
                    <td>{topic.contentType}</td>
                    <td>{topic.keywords || "暂无"}</td>
                    <td>{topic.priority}</td>
                    <td>{topicStatusLabel(topic.status)}</td>
                    <td>
                      <div className="rowActions">
                        <button
                          className="inlineButton"
                          onClick={() => openEditTopicForm(topic.id)}
                        >
                          修改
                        </button>
                        <button
                          className="inlineButton"
                          onClick={() => convert(topic)}
                        >
                          转内容任务
                        </button>
                        <button
                          className="dangerButton inlineDanger"
                          onClick={() => deleteTopic(topic.id)}
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!store.topics.length ? (
            <p className="muted">暂无选题，可以点击右上角新增选题。</p>
          ) : null}
        </Panel>
      ) : null}
    </section>
  );
}

function MasterArticlesV2({
  store,
  mutate,
  setMasterSub,
  editingMasterArticleId,
  setEditingMasterArticleId,
  view,
}: Ctx & { view: MasterSubKey }) {
  const [viewingArticleId, setViewingArticleId] = React.useState("");
  const editingArticle = store.masterArticles.find(
    (item) => item.id === editingMasterArticleId,
  );
  const viewingArticle = store.masterArticles.find(
    (item) => item.id === viewingArticleId,
  );

  async function generate(task: ContentTask) {
    const remoteArticle = await postRemote<RemoteRow>(
      `/contentTasks/${task.id}/generate-master`,
    ).catch(() => null);
    if (remoteArticle) {
      const mapped = mapMasterArticle(remoteArticle);
      mutate((draft) => {
        upsertById(draft.masterArticles, mapped);
        const found = draft.tasks.find((item) => item.id === task.id);
        if (found) found.status = "待生成平台版本";
      }, "已从 API 生成母版文章");
      setViewingArticleId(mapped.id);
      return;
    }
    const articleId = uid("ma");
    mutate((draft) => {
      const article: MasterArticle = {
        id: articleId,
        taskId: task.id,
        title: task.title,
        outline: task.outline,
        body: makeMasterArticle(task),
        status: "草稿",
        aiGenerated: true,
        createdAt: now(),
      };
      upsertById(draft.masterArticles, article);
      draft.aiLogs.unshift(
        aiLog(task.id, "master_article", task.direction, article.body),
      );
      const found = draft.tasks.find((item) => item.id === task.id);
      if (found) found.status = "待生成平台版本";
    }, "已生成母版文章，并记录 AI 日志");
    setViewingArticleId(articleId);
  }

  function saveArticle(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = formValues(form);
    const articleId = editingArticle?.id || uid("ma");
    const body = cleanMarkdownContent(data.body);
    const row: MasterArticle = {
      id: articleId,
      taskId: data.taskId,
      title: data.title,
      outline: data.outline,
      body,
      status: data.status || "草稿",
      aiGenerated: editingArticle?.aiGenerated || false,
      createdAt: editingArticle?.createdAt || now(),
    };
    const payload = {
      id: articleId,
      content_task_id: row.taskId,
      title: row.title,
      outline: row.outline,
      body_markdown: row.body,
      body_html: markdownToHtml(row.body),
      status: row.status,
      word_count: row.body.length,
      ai_generated: row.aiGenerated,
    };
    const remote = editingArticle
      ? updateRemote("masterArticles", articleId, payload)
      : createRemote("masterArticles", payload);
    void remote.catch(() => undefined);
    mutate(
      (draft) => {
        upsertById(draft.masterArticles, row);
      },
      editingArticle ? "母版文章已更新" : "母版文章已新增",
    );
    setEditingMasterArticleId("");
    setViewingArticleId(articleId);
    form.reset();
  }

  function deleteArticle(articleId: string) {
    if (!window.confirm("确定删除这篇母版文章吗？")) return;
    void deleteRemote("masterArticles", articleId).catch(() => undefined);
    mutate((draft) => {
      draft.masterArticles = draft.masterArticles.filter(
        (item) => item.id !== articleId,
      );
      draft.platformVersions = draft.platformVersions.filter(
        (item) => item.masterArticleId !== articleId,
      );
    }, "母版文章已删除");
    if (editingMasterArticleId === articleId) setEditingMasterArticleId("");
    if (viewingArticleId === articleId) setViewingArticleId("");
  }

  return (
    <section className="grid collectionWorkspace">
      {view === "new" ? (
        <Panel title={editingArticle ? "编辑母版文章" : "新增母版文章"}>
          <form
            className="form"
            key={editingArticle?.id || "new-master"}
            onSubmit={saveArticle}
          >
            <label>
              关联任务
              <select name="taskId" defaultValue={editingArticle?.taskId || ""}>
                <option value="">不关联任务</option>
                {store.tasks.map((task) => (
                  <option key={task.id} value={task.id}>
                    {task.title}
                  </option>
                ))}
              </select>
            </label>
            <label>
              标题
              <input
                name="title"
                required
                defaultValue={editingArticle?.title || ""}
              />
            </label>
            <label>
              大纲
              <textarea
                name="outline"
                rows={4}
                defaultValue={editingArticle?.outline || ""}
              />
            </label>
            <label>
              Markdown 正文
              <textarea
                name="body"
                rows={14}
                required
                defaultValue={editingArticle?.body || ""}
              />
            </label>
            <label>
              状态
              <select
                name="status"
                defaultValue={editingArticle?.status || "草稿"}
              >
                <option>草稿</option>
                <option>待审核</option>
                <option>待生成平台版本</option>
                <option>已归档</option>
              </select>
            </label>
            <div className="rowActions">
              <button type="submit">
                <Plus size={16} />
                {editingArticle ? "保存修改" : "新增母版"}
              </button>
              {editingArticle ? (
                <button
                  type="button"
                  className="inlineButton"
                  onClick={() => setEditingMasterArticleId("")}
                >
                  取消编辑
                </button>
              ) : null}
            </div>
          </form>
        </Panel>
      ) : null}

      {view === "generate" ? (
        <Panel title="从任务生成母版">
          <div className="list">
            {store.tasks.map((task) => (
              <article className="card" key={task.id}>
                <strong>{task.title}</strong>
                <span>
                  {task.status} 路{" "}
                  {nameOf(store.businessLines, task.businessLineId)}
                </span>
                <button className="inlineButton" onClick={() => generate(task)}>
                  生成母版文章
                </button>
              </article>
            ))}
          </div>
        </Panel>
      ) : null}

      {view === "list" ? (
        <Panel title="母版文章列表">
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>标题</th>
                  <th>来源任务</th>
                  <th>状态</th>
                  <th>来源</th>
                  <th>字数</th>
                  <th>创建时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {store.masterArticles.map((article) => (
                  <tr
                    key={article.id}
                    className={
                      viewingArticleId === article.id ? "selectedRow" : ""
                    }
                  >
                    <td>
                      <button
                        className="linkButton"
                        onClick={() => setViewingArticleId(article.id)}
                      >
                        {article.title}
                      </button>
                    </td>
                    <td>
                      {article.taskId
                        ? taskTitle(store, article.taskId)
                        : "手动新增"}
                    </td>
                    <td>{article.status}</td>
                    <td>{article.aiGenerated ? "AI生成" : "人工"}</td>
                    <td>{article.body.length}</td>
                    <td>{formatDateTime(article.createdAt)}</td>
                    <td>
                      <div className="rowActions">
                        <button
                          className="inlineButton"
                          onClick={() => {
                            setEditingMasterArticleId(article.id);
                            setMasterSub("new");
                          }}
                        >
                          编辑
                        </button>
                        <button
                          className="dangerButton inlineDanger"
                          onClick={() => deleteArticle(article.id)}
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!store.masterArticles.length ? (
            <p className="muted">
              暂无母版文章，可以手动新增，或从内容任务生成。
            </p>
          ) : null}
        </Panel>
      ) : null}

      {view === "list" && viewingArticle ? (
        <Panel title="HTML 阅读预览">
          <article className="articleReader">
            <header>
              <h2>{viewingArticle.title}</h2>
              <span>
                {viewingArticle.status} 路{" "}
                {viewingArticle.aiGenerated ? "AI生成" : "人工"} ·{" "}
                {formatDateTime(viewingArticle.createdAt)}
              </span>
            </header>
            <div
              className="markdownPreview readonly"
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(viewingArticle.body),
              }}
            />
          </article>
        </Panel>
      ) : null}
    </section>
  );
}

function MasterArticles({ store, mutate }: Ctx) {
  async function generate(task: ContentTask) {
    const remoteArticle = await postRemote<RemoteRow>(
      `/contentTasks/${task.id}/generate-master`,
    ).catch(() => null);
    if (remoteArticle) {
      mutate((draft) => {
        upsertById(draft.masterArticles, mapMasterArticle(remoteArticle));
        const found = draft.tasks.find((item) => item.id === task.id);
        if (found) found.status = "待生成平台版本";
      }, "已从 API 生成母版文章");
      return;
    }
    mutate((draft) => {
      const article: MasterArticle = {
        id: uid("ma"),
        taskId: task.id,
        title: task.title,
        outline: task.outline,
        body: makeMasterArticle(task),
        status: "草稿",
        aiGenerated: true,
        createdAt: now(),
      };
      upsertById(draft.masterArticles, article);
      draft.aiLogs.unshift(
        aiLog(task.id, "master_article", task.direction, article.body),
      );
      const found = draft.tasks.find((item) => item.id === task.id);
      if (found) found.status = "待生成平台版本";
    }, "已生成母版文章，并记录 AI 日志");
  }
  return (
    <section className="grid two">
      <Panel title="从内容任务生成母版">
        <div className="list">
          {store.tasks.map((task) => (
            <article className="card" key={task.id}>
              <strong>{task.title}</strong>
              <span>
                {task.status} 路{" "}
                {nameOf(store.businessLines, task.businessLineId)}
              </span>
              <button className="inlineButton" onClick={() => generate(task)}>
                生成母版文章
              </button>
            </article>
          ))}
        </div>
      </Panel>
      <Panel title="母版文章">
        <CardList
          rows={store.masterArticles}
          render={(article) => (
            <>
              <strong>{article.title}</strong>
              <span>
                {article.status} · {article.aiGenerated ? "AI生成" : "人工"}
              </span>
              <small>{article.body.slice(0, 160)}...</small>
            </>
          )}
        />
      </Panel>
    </section>
  );
}

function PlatformVersions({
  store,
  mutate,
  view,
}: Ctx & { view: PlatformVersionSubKey }) {
  const [viewingPlatformVersionId, setViewingPlatformVersionId] =
    React.useState("");
  const [editingPlatformVersionId, setEditingPlatformVersionId] =
    React.useState("");
  const [viewingAccountVersionId, setViewingAccountVersionId] =
    React.useState("");
  const [editingAccountVersionId, setEditingAccountVersionId] =
    React.useState("");
  const viewingPlatformVersion = store.platformVersions.find(
    (item) => item.id === viewingPlatformVersionId,
  );
  const editingPlatformVersion = store.platformVersions.find(
    (item) => item.id === editingPlatformVersionId,
  );
  const viewingAccountVersion = store.accountVersions.find(
    (item) => item.id === viewingAccountVersionId,
  );
  const editingAccountVersion = store.accountVersions.find(
    (item) => item.id === editingAccountVersionId,
  );

  async function generatePlatformVersions(article: MasterArticle) {
    const task = store.tasks.find((item) => item.id === article.taskId);
    const targetPlatforms = task?.targetPlatformIds.length
      ? task.targetPlatformIds
      : store.platforms.map((item) => item.id);
    const remoteVersions = await postRemote<RemoteRow[]>(
      `/contentTasks/${article.taskId}/generate-platform-versions`,
    ).catch(() => null);
    if (remoteVersions?.length) {
      mutate((draft) => {
        for (const row of remoteVersions)
          upsertById(draft.platformVersions, mapPlatformVersion(row));
        const found = draft.tasks.find((item) => item.id === article.taskId);
        if (found) found.status = "待生成账号版本";
      }, "已从 API 生成平台版本");
      return;
    }
    mutate((draft) => {
      for (const platformId of targetPlatforms) {
        const platform = draft.platforms.find((item) => item.id === platformId);
        if (!platform) continue;
        const version: PlatformVersion = {
          id: uid("pv"),
          taskId: article.taskId,
          masterArticleId: article.id,
          platformId,
          title: `${article.title} · ${platform.name}版`,
          body: rewriteForPlatform(article.body, platform.name),
          summary: summarize(article.body),
          tags: extractKeywords(article.body),
          status: "草稿",
          createdAt: now(),
        };
        upsertById(draft.platformVersions, version);
        draft.aiLogs.unshift(
          aiLog(article.taskId, "platform_version", article.body, version.body),
        );
      }
    }, "已批量生成平台版本");
  }
  async function generateAccountVersions(version: PlatformVersion) {
    const accounts = store.accounts.filter(
      (account) => account.platformId === version.platformId,
    );
    const remoteVersions = await postRemote<RemoteRow[]>(
      `/platformVersions/${version.id}/generate-account-versions`,
    ).catch(() => null);
    if (remoteVersions?.length) {
      mutate((draft) => {
        for (const row of remoteVersions)
          upsertById(draft.accountVersions, mapAccountVersion(row));
      }, "已从 API 生成账号版本");
      return;
    }
    mutate((draft) => {
      for (const account of accounts) {
        const accountVersion: AccountVersion = {
          id: uid("av"),
          platformVersionId: version.id,
          accountId: account.id,
          title: `${version.title} · ${account.name}`,
          body: rewriteForAccount(version.body, account),
          coverText: version.title.slice(0, 24),
          hashtags: version.tags,
          cta: account.cta,
          status: "待发布",
          similarityScore: Number((0.42 + Math.random() * 0.18).toFixed(2)),
          createdAt: now(),
        };
        upsertById(draft.accountVersions, accountVersion);
        draft.aiLogs.unshift(
          aiLog(
            version.taskId,
            "account_version",
            version.body,
            accountVersion.body,
          ),
        );
      }
    }, "已按账号人设生成账号版本");
  }

  function savePlatformVersion(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editingPlatformVersion) return;
    const data = formValues(event.currentTarget);
    mutate(
      (draft) => {
        const found = draft.platformVersions.find(
          (item) => item.id === editingPlatformVersion.id,
        );
        if (!found) return;
        found.title = data.title;
        found.platformId = data.platformId;
        found.summary = data.summary;
        found.tags = data.tags;
        found.status = data.status;
        found.body = cleanMarkdownContent(data.body);
      },
      "平台版本已更新",
    );
    setEditingPlatformVersionId("");
  }

  function deletePlatformVersion(version: PlatformVersion) {
    if (!window.confirm(`确定删除平台版本「${version.title}」吗？`)) return;
    mutate((draft) => {
      draft.platformVersions = draft.platformVersions.filter(
        (item) => item.id !== version.id,
      );
      draft.accountVersions = draft.accountVersions.filter(
        (item) => item.platformVersionId !== version.id,
      );
    }, "平台版本已删除");
    if (viewingPlatformVersionId === version.id) setViewingPlatformVersionId("");
    if (editingPlatformVersionId === version.id) setEditingPlatformVersionId("");
  }

  function saveAccountVersion(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editingAccountVersion) return;
    const data = formValues(event.currentTarget);
    mutate(
      (draft) => {
        const found = draft.accountVersions.find(
          (item) => item.id === editingAccountVersion.id,
        );
        if (!found) return;
        found.title = data.title;
        found.accountId = data.accountId;
        found.coverText = data.coverText;
        found.hashtags = data.hashtags;
        found.cta = data.cta;
        found.status = data.status;
        found.body = cleanMarkdownContent(data.body);
      },
      "账号版本已更新",
    );
    setEditingAccountVersionId("");
  }

  function deleteAccountVersion(version: AccountVersion) {
    if (!window.confirm(`确定删除账号版本「${version.title}」吗？`)) return;
    mutate((draft) => {
      draft.accountVersions = draft.accountVersions.filter(
        (item) => item.id !== version.id,
      );
    }, "账号版本已删除");
    if (viewingAccountVersionId === version.id) setViewingAccountVersionId("");
    if (editingAccountVersionId === version.id) setEditingAccountVersionId("");
  }

  return (
    <section className="grid collectionWorkspace">
      {view === "masterToPlatform" ? (
        <Panel title="母版转平台版本">
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>母版文章</th>
                  <th>状态</th>
                  <th>来源任务</th>
                  <th>字数</th>
                  <th>已生成平台版本</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {store.masterArticles.map((article) => (
                  <tr key={article.id}>
                    <td>{article.title}</td>
                    <td>{article.status}</td>
                    <td>
                      {article.taskId ? taskTitle(store, article.taskId) : "手动新增"}
                    </td>
                    <td>{article.body.length}</td>
                    <td>
                      {
                        store.platformVersions.filter(
                          (version) => version.masterArticleId === article.id,
                        ).length
                      }
                    </td>
                    <td>
                      <button
                        className="inlineButton"
                        onClick={() => generatePlatformVersions(article)}
                      >
                        生成平台版本
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}
      {view === "versions" ? (
        <Panel title="平台版本与账号版本">
          <div className="stackPanel">
            <div>
              <h3>平台版本</h3>
              <div className="tableWrap">
                <table>
                  <thead>
                    <tr>
                      <th>标题</th>
                      <th>平台</th>
                      <th>状态</th>
                      <th>标签</th>
                      <th>账号版本</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {store.platformVersions.map((version) => (
                      <tr key={version.id}>
                        <td>{version.title}</td>
                        <td>{nameOf(store.platforms, version.platformId)}</td>
                        <td>{version.status}</td>
                        <td>{version.tags}</td>
                        <td>
                          {
                            store.accountVersions.filter(
                              (item) => item.platformVersionId === version.id,
                            ).length
                          }
                        </td>
                        <td>
                          <div className="rowActions">
                            <button
                              className="inlineButton"
                              onClick={() => setViewingPlatformVersionId(version.id)}
                            >
                              查看
                            </button>
                            <button
                              className="inlineButton"
                              onClick={() => setEditingPlatformVersionId(version.id)}
                            >
                              编辑
                            </button>
                            <button
                              className="inlineButton"
                              onClick={() => generateAccountVersions(version)}
                            >
                              生成账号版本
                            </button>
                            <button
                              className="dangerButton inlineDanger"
                              onClick={() => deletePlatformVersion(version)}
                            >
                              删除
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3>账号版本</h3>
              <div className="tableWrap">
                <table>
                  <thead>
                    <tr>
                      <th>标题</th>
                      <th>账号</th>
                      <th>来源平台版本</th>
                      <th>状态</th>
                      <th>相似度</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {store.accountVersions.map((version) => (
                      <tr key={version.id}>
                        <td>{version.title}</td>
                        <td>{nameOf(store.accounts, version.accountId)}</td>
                        <td>
                          {store.platformVersions.find(
                            (item) => item.id === version.platformVersionId,
                          )?.title || "未关联"}
                        </td>
                        <td>{version.status}</td>
                        <td>{version.similarityScore}</td>
                        <td>
                          <div className="rowActions">
                            <button
                              className="inlineButton"
                              onClick={() => setViewingAccountVersionId(version.id)}
                            >
                              查看
                            </button>
                            <button
                              className="inlineButton"
                              onClick={() => setEditingAccountVersionId(version.id)}
                            >
                              编辑
                            </button>
                            <button
                              className="dangerButton inlineDanger"
                              onClick={() => deleteAccountVersion(version)}
                            >
                              删除
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Panel>
      ) : null}
      {viewingPlatformVersion ? (
        <VersionDetailModal
          title="查看平台版本"
          meta={`${nameOf(store.platforms, viewingPlatformVersion.platformId)} · ${viewingPlatformVersion.status}`}
          heading={viewingPlatformVersion.title}
          body={viewingPlatformVersion.body}
          extra={[
            ["摘要", viewingPlatformVersion.summary],
            ["标签", viewingPlatformVersion.tags],
          ]}
          onClose={() => setViewingPlatformVersionId("")}
        />
      ) : null}
      {viewingAccountVersion ? (
        <VersionDetailModal
          title="查看账号版本"
          meta={`${nameOf(store.accounts, viewingAccountVersion.accountId)} · ${viewingAccountVersion.status}`}
          heading={viewingAccountVersion.title}
          body={viewingAccountVersion.body}
          extra={[
            ["封面文案", viewingAccountVersion.coverText],
            ["话题", viewingAccountVersion.hashtags],
            ["CTA", viewingAccountVersion.cta],
          ]}
          onClose={() => setViewingAccountVersionId("")}
        />
      ) : null}
      {editingPlatformVersion ? (
        <PlatformVersionEditModal
          store={store}
          version={editingPlatformVersion}
          onSubmit={savePlatformVersion}
          onClose={() => setEditingPlatformVersionId("")}
        />
      ) : null}
      {editingAccountVersion ? (
        <AccountVersionEditModal
          store={store}
          version={editingAccountVersion}
          onSubmit={saveAccountVersion}
          onClose={() => setEditingAccountVersionId("")}
        />
      ) : null}
    </section>
  );
}

function VersionDetailModal({
  title,
  meta,
  heading,
  body,
  extra,
  onClose,
}: {
  title: string;
  meta: string;
  heading: string;
  body: string;
  extra: Array<[string, string]>;
  onClose: () => void;
}) {
  return (
    <div className="modalOverlay">
      <div className="modalPanel imageProjectModal">
        <div className="modalHeader">
          <div>
            <strong>{title}</strong>
            <span>{meta}</span>
          </div>
          <button type="button" className="ghost" onClick={onClose}>
            关闭
          </button>
        </div>
        <article className="articleReader">
          <header>
            <h2>{heading}</h2>
            {extra.map(([label, value]) => (
              <span key={label}>
                {label}：{value || "未设置"}
              </span>
            ))}
          </header>
          <div
            className="markdownPreview readonly"
            dangerouslySetInnerHTML={{ __html: markdownToHtml(body) }}
          />
        </article>
      </div>
    </div>
  );
}

function PlatformVersionEditModal({
  store,
  version,
  onSubmit,
  onClose,
}: {
  store: Store;
  version: PlatformVersion;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
}) {
  return (
    <div className="modalOverlay">
      <form className="modalPanel imageProjectModal" onSubmit={onSubmit}>
        <div className="modalHeader">
          <div>
            <strong>编辑平台版本</strong>
            <span>{version.title}</span>
          </div>
          <button type="button" className="ghost" onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="form modelForm">
          <label>
            标题
            <input name="title" required defaultValue={version.title} />
          </label>
          <label>
            平台
            <Select
              name="platformId"
              rows={store.platforms}
              value={version.platformId}
            />
          </label>
          <label>
            状态
            <input name="status" defaultValue={version.status} />
          </label>
          <label>
            标签
            <input name="tags" defaultValue={version.tags} />
          </label>
          <label className="span2">
            摘要
            <textarea name="summary" rows={3} defaultValue={version.summary} />
          </label>
          <label className="span2">
            Markdown 正文
            <textarea name="body" rows={14} required defaultValue={version.body} />
          </label>
        </div>
        <div className="modalActions">
          <button type="button" className="ghost" onClick={onClose}>
            取消
          </button>
          <button type="submit" className="primaryButton">
            保存平台版本
          </button>
        </div>
      </form>
    </div>
  );
}

function AccountVersionEditModal({
  store,
  version,
  onSubmit,
  onClose,
}: {
  store: Store;
  version: AccountVersion;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
}) {
  return (
    <div className="modalOverlay">
      <form className="modalPanel imageProjectModal" onSubmit={onSubmit}>
        <div className="modalHeader">
          <div>
            <strong>编辑账号版本</strong>
            <span>{version.title}</span>
          </div>
          <button type="button" className="ghost" onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="form modelForm">
          <label>
            标题
            <input name="title" required defaultValue={version.title} />
          </label>
          <label>
            账号
            <Select
              name="accountId"
              rows={store.accounts}
              value={version.accountId}
            />
          </label>
          <label>
            状态
            <input name="status" defaultValue={version.status} />
          </label>
          <label>
            封面文案
            <input name="coverText" defaultValue={version.coverText} />
          </label>
          <label>
            话题
            <input name="hashtags" defaultValue={version.hashtags} />
          </label>
          <label>
            CTA
            <input name="cta" defaultValue={version.cta} />
          </label>
          <label className="span2">
            Markdown 正文
            <textarea name="body" rows={14} required defaultValue={version.body} />
          </label>
        </div>
        <div className="modalActions">
          <button type="button" className="ghost" onClick={onClose}>
            取消
          </button>
          <button type="submit" className="primaryButton">
            保存账号版本
          </button>
        </div>
      </form>
    </div>
  );
}

function ImageTextV2({ store, mutate, view }: Ctx & { view: ImageTextSubKey }) {
  const pageSize = 8;
  const [versionPage, setVersionPage] = React.useState(1);
  const [projectPage, setProjectPage] = React.useState(1);
  const [selectedVersionId, setSelectedVersionId] = React.useState("");
  const [selectedProjectId, setSelectedProjectId] = React.useState("");
  const [variantDialogProjectId, setVariantDialogProjectId] =
    React.useState("");
  const [variantGenerating, setVariantGenerating] = React.useState(false);
  const [variantMessage, setVariantMessage] = React.useState("");
  const pagedVersions = paginateRows(
    store.accountVersions,
    versionPage,
    pageSize,
  );
  const rootProjects = store.imageTextProjects.filter(
    (project) => !project.parentProjectId,
  );
  const pagedProjects = paginateRows(
    rootProjects,
    projectPage,
    pageSize,
  );
  const selectedVersion = store.accountVersions.find(
    (item) => item.id === selectedVersionId,
  );
  const selectedProject = store.imageTextProjects.find(
    (item) => item.id === selectedProjectId,
  );
  const variantDialogProject = store.imageTextProjects.find(
    (item) => item.id === variantDialogProjectId,
  );
  const selectedProjectVariants = selectedProject
    ? store.imageTextProjects.filter(
        (item) => item.parentProjectId === selectedProject.id,
      )
    : [];

  function createProject(accountVersion: AccountVersion) {
    mutate((draft) => {
      const account = draft.accounts.find(
        (item) => item.id === accountVersion.accountId,
      );
      const platform = draft.platforms.find(
        (item) => item.id === account?.platformId,
      );
      const pages = makeImagePages(accountVersion.body, platform?.pages || 6);
      draft.imageTextProjects.unshift({
        id: uid("imgtxt"),
        accountVersionId: accountVersion.id,
        title: accountVersion.title,
        ratio: platform?.ratio || "3:4",
        pages,
        imagePrompts: pages.map((page, index) =>
          makeImagePrompt(page, index, platform?.name || ""),
        ),
        coverTitle:
          accountVersion.coverText || accountVersion.title.slice(0, 24),
        publishTitle:
          accountVersion.coverText || accountVersion.title.slice(0, 32),
        publishContent: makeImageTextPublishContent(accountVersion),
        designStyle:
          platform?.code === "xiaohongshu"
            ? "小红书知识卡片，强标题，清单式排版"
            : "商务内容长图，清晰层级，适合阅读",
        assetIds: draft.assets
          .filter(
            (asset) =>
              !platform ||
              !asset.platformId ||
              asset.platformId === platform.id,
          )
          .slice(0, 6)
          .map((asset) => asset.id),
        status: "待生成图片",
        createdAt: now(),
      });
    }, "已生成图文项目、分页文案和图片 Prompt");
    setSelectedVersionId(accountVersion.id);
  }

  function createProjectFromArticle(article: MasterArticle) {
    mutate((draft) => {
      const accountVersion = makeDraftAccountVersionFromArticle(draft, article);
      if (!accountVersion) return;
      upsertById(draft.accountVersions, accountVersion);
      const account = draft.accounts.find(
        (item) => item.id === accountVersion.accountId,
      );
      const platform = draft.platforms.find(
        (item) => item.id === account?.platformId,
      );
      const pages = makeImagePages(accountVersion.body, platform?.pages || 6);
      draft.imageTextProjects.unshift({
        id: uid("imgtxt"),
        accountVersionId: accountVersion.id,
        title: accountVersion.title,
        ratio: platform?.ratio || "3:4",
        pages,
        imagePrompts: pages.map((page, index) =>
          makeImagePrompt(page, index, platform?.name || ""),
        ),
        coverTitle:
          accountVersion.coverText || accountVersion.title.slice(0, 24),
        publishTitle:
          accountVersion.coverText || accountVersion.title.slice(0, 32),
        publishContent: makeImageTextPublishContent(accountVersion),
        designStyle:
          platform?.code === "xiaohongshu"
            ? "小红书知识卡片，强标题，清单式排版"
            : "商务内容长图，清晰层级，适合阅读",
        assetIds: draft.assets
          .filter(
            (asset) =>
              !platform ||
              !asset.platformId ||
              asset.platformId === platform.id,
          )
          .slice(0, 6)
          .map((asset) => asset.id),
        status: "待生成图片",
        createdAt: now(),
      });
    }, "已从母版文章创建图文项目");
  }

  function updateProject(projectId: string, patch: Partial<ImageTextProject>) {
    mutate((draft) => {
      const project = draft.imageTextProjects.find(
        (item) => item.id === projectId,
      );
      if (project) Object.assign(project, patch);
    }, "图文项目已更新");
  }

  function saveGeneratedImages(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedProject) return;
    const data = new FormData(event.currentTarget);
    const paths = String(data.get("generatedImagePaths") || "")
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);
    updateProject(selectedProject.id, {
      generatedImagePaths: paths,
      status: paths.length ? "图片已生成" : "待生成图片",
    });
  }

  function exportProject(project: ImageTextProject) {
    downloadText(
      `${safeFileName(project.title)}-图文脚本.md`,
      buildImageTextPackage(store, project),
      "text/markdown;charset=utf-8",
    );
    updateProject(project.id, { status: "已导出脚本" });
  }

  async function generateChildProjects(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!variantDialogProject) return;
    const form = new FormData(event.currentTarget);
    const count = Math.min(Math.max(Number(form.get("count") || 1), 1), 100);
    const quality = Math.min(Math.max(Number(form.get("quality") || 82), 30), 100);
    const width = Math.max(Number(form.get("width") || 0), 0);
    const brightness = Number(form.get("brightness") || 0);
    const saturation = Number(form.get("saturation") || 0);
    const grayscale = String(form.get("grayscale") || "") === "on";
    const watermark = String(form.get("watermark") || "").trim();
    const logoText = String(form.get("logoText") || "").trim();
    const strength = String(form.get("strength") || "medium");
    const batchId = uid("variant_batch");
    const baseTitle =
      variantDialogProject.publishTitle ||
      variantDialogProject.coverTitle ||
      variantDialogProject.title;
    const baseContent =
      variantDialogProject.publishContent ||
      makeImageTextPublishContent(
        findAccountVersion(store, variantDialogProject.accountVersionId),
      );
    const imageSources =
      variantDialogProject.generatedImagePaths?.length
        ? variantDialogProject.generatedImagePaths
        : variantDialogProject.assetIds
            ?.map((id) => {
              const asset = store.assets.find((item) => item.id === id);
              return asset?.archiveDir && asset.fileName
                ? `/demo/image-text/${asset.fileName}`
                : asset?.path || asset?.storageKey || "";
            })
            .filter(Boolean) || [];
    if (!imageSources.length) {
      setVariantMessage("请先为母图文项目登记成品图片");
      return;
    }
    setVariantGenerating(true);
    setVariantMessage(`正在生成 0/${count}`);
    const generated: ImageTextProject[] = [];
    try {
      for (let index = 1; index <= count; index += 1) {
        setVariantMessage(`正在生成 ${index}/${count}`);
        const rewritten = makeLocalImageTextVariant(
          baseTitle,
          baseContent,
          index,
          strength,
        );
        const images: string[] = [];
        const hashes: string[] = [];
        const archivePaths: string[] = [];
        let failedImages = 0;
        for (let pageIndex = 0; pageIndex < imageSources.length; pageIndex += 1) {
          const archivePath = buildVariantAttachmentPath(
            store.appSettings,
            variantDialogProject,
            index,
            pageIndex + 1,
          );
          try {
            const result = await makeImageVariant(imageSources[pageIndex], {
              quality,
              width,
              brightness,
              saturation,
              grayscale,
              watermark,
              logoText,
              variantIndex: index,
              pageIndex: pageIndex + 1,
            });
            const uploaded = await uploadGeneratedImage({
              file: result.blob,
              storageKey: archivePath,
              projectId: variantDialogProject.id,
              variantIndex: index,
              pageIndex: pageIndex + 1,
              source: imageSources[pageIndex],
            });
            images.push(uploaded?.path || archivePath);
            hashes.push(result.hash);
          } catch {
            failedImages += 1;
            images.push(archivePath);
            hashes.push("未计算");
          }
          archivePaths.push(archivePath);
        }
        generated.push({
          ...variantDialogProject,
          id: uid("imgtxt_variant"),
          parentProjectId: variantDialogProject.id,
          variantIndex: index,
          variantBatchId: batchId,
          title: `${variantDialogProject.title} · 子项目 ${index}`,
          publishTitle: rewritten.title,
          publishContent: rewritten.content,
          generatedImagePaths: images,
          imageHashes: hashes,
          imageArchivePaths: archivePaths,
          status: failedImages ? "子项目已生成（部分图片未处理）" : "子项目已生成",
          variantSettings: JSON.stringify({
            quality,
            width,
            brightness,
            saturation,
            grayscale,
            watermark,
            logoText,
            strength,
          }),
          createdAt: now(),
        });
      }
      mutate((draft) => {
        draft.imageTextProjects.unshift(...generated.reverse());
      }, `已生成 ${generated.length} 个子图文项目`);
      setVariantDialogProjectId("");
      setVariantMessage("");
    } catch (error) {
      setVariantMessage(
        `生成失败：${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setVariantGenerating(false);
    }
  }

  return (
    <section className="grid collectionWorkspace">
      {view === "versions" ? (
        <Panel title="账号版本转图文">
          <div className="tableWrap modelTable">
            <table>
              <thead>
                <tr>
                  <th>标题</th>
                  <th>账号</th>
                  <th>状态</th>
                  <th>封面文案</th>
                  <th>创建时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {pagedVersions.rows.map((version) => (
                  <tr
                    key={version.id}
                    className={
                      selectedVersionId === version.id ? "selectedRow" : ""
                    }
                  >
                    <td>
                      <button
                        className="linkButton"
                        onClick={() => setSelectedVersionId(version.id)}
                      >
                        {version.title}
                      </button>
                    </td>
                    <td>{nameOf(store.accounts, version.accountId)}</td>
                    <td>{version.status}</td>
                    <td>{version.coverText || version.body.slice(0, 42)}</td>
                    <td>{formatDateTime(version.createdAt)}</td>
                    <td>
                      <button
                        className="inlineButton"
                        onClick={() => createProject(version)}
                      >
                        生成图文项目
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {store.accountVersions.length ? (
            <Pagination
              page={versionPage}
              totalPages={pagedVersions.totalPages}
              onPage={setVersionPage}
            />
          ) : (
            <div className="emptyAction">
              <p className="muted">
                暂无账号版本。可以先在平台版本里生成账号版本，也可以直接从母版文章创建图文项目。
              </p>
              {store.masterArticles.length ? (
                store.masterArticles.map((article) => (
                  <article className="card subtle" key={article.id}>
                    <strong>{article.title}</strong>
                    <small>{article.body.slice(0, 120)}...</small>
                    <button
                      className="inlineButton"
                      onClick={() => createProjectFromArticle(article)}
                    >
                      直接生成图文项目
                    </button>
                  </article>
                ))
              ) : (
                <p className="muted">
                  还没有母版文章，请先生成一篇内容。
                </p>
              )}
            </div>
          )}
          {selectedVersion ? (
            <article className="collectionDetail">
              <div className="detailHeader">
                <div>
                  <strong>{selectedVersion.title}</strong>
                  <span>
                    {nameOf(store.accounts, selectedVersion.accountId)} 路{" "}
                    {selectedVersion.status}
                  </span>
                </div>
              </div>
              <section className="detailMeta">
                <span>相似度：{selectedVersion.similarityScore}</span>
                <span>标签：{selectedVersion.hashtags || "暂无"}</span>
                <span>CTA：{selectedVersion.cta || "暂无"}</span>
              </section>
              <div
                className="markdownPreview readonly"
                dangerouslySetInnerHTML={{
                  __html: markdownToHtml(selectedVersion.body),
                }}
              />
            </article>
          ) : null}
        </Panel>
      ) : null}

      {view === "projects" ? (
        <Panel title="图文项目">
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>标题</th>
                  <th>比例</th>
                  <th>页数</th>
                  <th>子项目</th>
                  <th>状态</th>
                  <th>创建时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {pagedProjects.rows.map((project) => (
                  <tr
                    key={project.id}
                    className={
                      selectedProjectId === project.id ? "selectedRow" : ""
                    }
                  >
                    <td>
                      <button
                        className="linkButton"
                        onClick={() => setSelectedProjectId(project.id)}
                      >
                        {project.title}
                      </button>
                    </td>
                    <td>{project.ratio}</td>
                    <td>{project.pages.length}</td>
                    <td>
                      {(() => {
                        const childCount = store.imageTextProjects.filter(
                          (item) => item.parentProjectId === project.id,
                        ).length;
                        return childCount ? (
                          <button
                            className="linkButton"
                            onClick={() => setSelectedProjectId(project.id)}
                          >
                            {childCount}
                          </button>
                        ) : (
                          0
                        );
                      })()}
                    </td>
                    <td>{project.status}</td>
                    <td>{formatDateTime(project.createdAt)}</td>
                    <td>
                      <div className="rowActions">
                        <button
                          className="inlineButton"
                          onClick={() => exportProject(project)}
                        >
                          导出脚本
                        </button>
                        <button
                          className="inlineButton"
                          onClick={() => setVariantDialogProjectId(project.id)}
                        >
                          生成子项目
                        </button>
                        <button
                          className="inlineButton"
                          onClick={() =>
                            updateProject(project.id, { status: "图片制作中" })
                          }
                        >
                          制作中
                        </button>
                        <button
                          className="inlineButton"
                          onClick={() =>
                            updateProject(project.id, { status: "待发布" })
                          }
                        >
                          待发布
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rootProjects.length ? (
            <Pagination
              page={projectPage}
              totalPages={pagedProjects.totalPages}
              onPage={setProjectPage}
            />
          ) : (
            <p className="muted">暂无图文项目。</p>
          )}
          {selectedProject ? (
            <div className="modalOverlay">
              <article className="modalPanel imageProjectModal collectionDetail">
                <div className="detailHeader">
                  <div>
                    <strong>{selectedProject.title}</strong>
                    <span>
                      {selectedProject.ratio} 路 {selectedProject.status} 路{" "}
                      {selectedProject.pages.length} 页
                    </span>
                  </div>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => setSelectedProjectId("")}
                  >
                    关闭
                  </button>
                </div>
                <label>
                  封面标题
                  <input
                    value={
                      selectedProject.coverTitle ||
                      selectedProject.title.slice(0, 24)
                    }
                    onChange={(event) =>
                      updateProject(selectedProject.id, {
                        coverTitle: event.currentTarget.value,
                      })
                    }
                  />
                </label>
                <section className="publishPreview">
                  <div className="publishPreviewHeader">
                    <div>
                      <strong>图文成品</strong>
                      <span>
                        标题 + 正文 +{" "}
                        {selectedProject.generatedImagePaths?.length || 0}
                        张图片
                      </span>
                    </div>
                  </div>
                  <label>
                    发布标题
                    <input
                      value={
                        selectedProject.publishTitle ||
                        selectedProject.coverTitle ||
                        selectedProject.title
                      }
                      onChange={(event) =>
                        updateProject(selectedProject.id, {
                          publishTitle: event.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    发布正文
                    <textarea
                      rows={7}
                      value={
                        selectedProject.publishContent ||
                        makeImageTextPublishContent(
                          findAccountVersion(
                            store,
                            selectedProject.accountVersionId,
                          ),
                        )
                      }
                      onChange={(event) =>
                        updateProject(selectedProject.id, {
                          publishContent: event.currentTarget.value,
                        })
                      }
                    />
                  </label>
                </section>
                <label>
                  设计风格
                  <input
                    value={selectedProject.designStyle || ""}
                    onChange={(event) =>
                      updateProject(selectedProject.id, {
                        designStyle: event.currentTarget.value,
                      })
                    }
                  />
                </label>
                <div className="pageGrid">
                  {selectedProject.pages.map((page, index) => (
                    <article
                      className="pageCard"
                      key={`${selectedProject.id}_${index}`}
                    >
                      <b>{index + 1}</b>
                      {selectedProject.generatedImagePaths?.[index] ? (
                        <div className="generatedImagePreview">
                          {isPreviewableImagePath(
                            selectedProject.generatedImagePaths[index],
                          ) ? (
                            <img
                              src={selectedProject.generatedImagePaths[index]}
                              alt={`第 ${index + 1} 页成图`}
                            />
                          ) : (
                            <span>
                              {selectedProject.generatedImagePaths[index]}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="generatedImageEmpty">待生成图片</div>
                      )}
                      <span>{page}</span>
                      <small>
                        {selectedProject.imagePrompts[index] ||
                          makeImagePrompt(page, index, "")}
                      </small>
                    </article>
                  ))}
                </div>
                <small>
                  引用素材：
                  {selectedProject.assetIds
                    ?.map((id) => {
                      const asset = store.assets.find((item) => item.id === id);
                      return asset?.archiveDir
                        ? attachmentPath(
                            store.appSettings,
                            asset.archiveDir,
                            asset.fileName,
                          )
                        : asset?.path ||
                            asset?.storageKey ||
                            nameOf(store.assets, id);
                    })
                    .join("；") || "暂无"}
                </small>
                <form
                  key={`${selectedProject.id}-generated-images`}
                  className="form generatedImageForm"
                  onSubmit={saveGeneratedImages}
                >
                  <label>
                    已生成图片路径/链接
                    <textarea
                      name="generatedImagePaths"
                      rows={4}
                      placeholder="每行一张图片，顺序对应第 1 页、第 2 页、第 3 页..."
                      defaultValue={(
                        selectedProject.generatedImagePaths || []
                      ).join("\n")}
                    />
                  </label>
                  <div className="rowActions">
                    <button type="submit" className="inlineButton">
                      保存成图
                    </button>
                    <button
                      type="button"
                      className="inlineButton"
                      onClick={() =>
                        updateProject(selectedProject.id, {
                          status: "图片已生成",
                        })
                      }
                    >
                      仅标记已生成
                    </button>
                  </div>
                </form>
                {!selectedProject.parentProjectId ? (
                  <section className="variantList">
                    <div className="publishPreviewHeader">
                      <div>
                        <strong>子图文项目</strong>
                        <span>{selectedProjectVariants.length} 个变体</span>
                      </div>
                    </div>
                    {selectedProjectVariants.length ? (
                      <div className="tableWrap">
                        <table>
                          <thead>
                            <tr>
                              <th>标题</th>
                              <th>图片</th>
                              <th>Hash</th>
                              <th>状态</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedProjectVariants.map((variant) => (
                              <tr key={variant.id}>
                                <td>
                                  <button
                                    className="linkButton"
                                    onClick={() => setSelectedProjectId(variant.id)}
                                  >
                                    {variant.publishTitle || variant.title}
                                  </button>
                                </td>
                                <td>{variant.generatedImagePaths?.length || 0}</td>
                                <td>{variant.imageHashes?.[0]?.slice(0, 12) || "未计算"}</td>
                                <td>{variant.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="muted">还没有生成子项目。</p>
                    )}
                  </section>
                ) : null}
                <div className="rowActions">
                  <button
                    className="inlineButton"
                    onClick={() => exportProject(selectedProject)}
                  >
                    导出图文脚本
                  </button>
                  {!selectedProject.parentProjectId ? (
                    <button
                      className="inlineButton"
                      onClick={() => setVariantDialogProjectId(selectedProject.id)}
                    >
                      生成子项目
                    </button>
                  ) : null}
                  <button
                    className="inlineButton"
                    onClick={() =>
                      updateProject(selectedProject.id, {
                        status: "图片制作中",
                      })
                    }
                  >
                    标记制作中
                  </button>
                  <button
                    className="inlineButton"
                    onClick={() =>
                      updateProject(selectedProject.id, { status: "待发布" })
                    }
                  >
                    标记待发布
                  </button>
                </div>
              </article>
            </div>
          ) : null}
          {variantDialogProject ? (
            <div className="modalOverlay">
              <form
                className="modalPanel modelModal"
                onSubmit={generateChildProjects}
              >
                <div className="modalHeader">
                  <div>
                    <strong>生成子图文项目</strong>
                    <span>{variantDialogProject.title}</span>
                  </div>
                  <button
                    type="button"
                    className="ghost"
                    disabled={variantGenerating}
                    onClick={() => setVariantDialogProjectId("")}
                  >
                    关闭
                  </button>
                </div>
                <div className="form modelForm">
                  <label>
                    生成数量
                    <input name="count" type="number" min="1" max="100" defaultValue="10" />
                  </label>
                  <label>
                    文案改写强度
                    <select name="strength" defaultValue="medium">
                      <option value="light">轻度</option>
                      <option value="medium">中度</option>
                      <option value="strong">强度</option>
                    </select>
                  </label>
                  <label>
                    图片质量
                    <input name="quality" type="number" min="30" max="100" defaultValue="82" />
                  </label>
                  <label>
                    输出宽度
                    <input name="width" type="number" min="0" placeholder="0 表示保持原宽度" />
                  </label>
                  <label>
                    亮度调整
                    <input name="brightness" type="number" min="-40" max="40" defaultValue="0" />
                  </label>
                  <label>
                    饱和度调整
                    <input name="saturation" type="number" min="-40" max="40" defaultValue="0" />
                  </label>
                  <label>
                    水印文字
                    <input name="watermark" placeholder="可选，例如 MediaPilotX" />
                  </label>
                  <label>
                    Logo 文案
                    <input name="logoText" placeholder="可选，例如 QTDDP" />
                  </label>
                  <label className="inlineCheck">
                    <input name="grayscale" type="checkbox" />
                    灰度处理
                  </label>
                </div>
                {variantMessage ? <p className="muted">{variantMessage}</p> : null}
                <div className="modalActions">
                  <button
                    type="button"
                    className="ghost"
                    disabled={variantGenerating}
                    onClick={() => setVariantDialogProjectId("")}
                  >
                    取消
                  </button>
                  <button type="submit" className="primaryButton" disabled={variantGenerating}>
                    {variantGenerating ? "生成中..." : "开始生成"}
                  </button>
                </div>
              </form>
            </div>
          ) : null}
        </Panel>
      ) : null}
    </section>
  );
}

function VideoV2({ store, mutate, view }: Ctx & { view: VideoSubKey }) {
  function createProject(accountVersion: AccountVersion) {
    mutate((draft) => {
      const account = draft.accounts.find(
        (item) => item.id === accountVersion.accountId,
      );
      const platform = account
        ? draft.platforms.find((item) => item.id === account.platformId)
        : null;
      draft.videoProjects.unshift({
        id: uid("video"),
        accountVersionId: accountVersion.id,
        title: accountVersion.title,
        script: makeVideoScript(accountVersion.body),
        storyboard: makeStoryboard(accountVersion.body),
        subtitles: makeSubtitles(accountVersion.body),
        voice: "zh-CN-business-neutral",
        duration: platform?.code === "douyin" ? "45s" : "60s",
        aspectRatio: platform?.ratio || "9:16",
        editNotes: makeEditNotes(accountVersion.body, platform?.name || ""),
        assetIds: draft.assets
          .filter((asset) =>
            ["瑙嗛", "图片", "BGM", "音频"].includes(asset.type),
          )
          .slice(0, 8)
          .map((asset) => asset.id),
        status: "脚本已生成",
        createdAt: now(),
      });
    }, "已生成视频脚本、分镜、字幕和混剪说明");
  }

  function createProjectFromArticle(article: MasterArticle) {
    mutate((draft) => {
      const accountVersion = makeDraftAccountVersionFromArticle(draft, article);
      if (!accountVersion) return;
      upsertById(draft.accountVersions, accountVersion);
      const account = draft.accounts.find(
        (item) => item.id === accountVersion.accountId,
      );
      const platform = account
        ? draft.platforms.find((item) => item.id === account.platformId)
        : null;
      draft.videoProjects.unshift({
        id: uid("video"),
        accountVersionId: accountVersion.id,
        title: accountVersion.title,
        script: makeVideoScript(accountVersion.body),
        storyboard: makeStoryboard(accountVersion.body),
        subtitles: makeSubtitles(accountVersion.body),
        voice: "zh-CN-business-neutral",
        duration: platform?.code === "douyin" ? "45s" : "60s",
        aspectRatio: platform?.ratio || "9:16",
        editNotes: makeEditNotes(accountVersion.body, platform?.name || ""),
        assetIds: draft.assets
          .filter((asset) =>
            ["瑙嗛", "图片", "BGM", "音频"].includes(asset.type),
          )
          .slice(0, 8)
          .map((asset) => asset.id),
        status: "脚本已生成",
        createdAt: now(),
      });
    }, "已从母版文章创建视频工程");
  }

  function updateProject(projectId: string, patch: Partial<VideoProject>) {
    mutate((draft) => {
      const project = draft.videoProjects.find((item) => item.id === projectId);
      if (project) Object.assign(project, patch);
    }, "视频工程已更新");
  }

  function exportProject(project: VideoProject) {
    downloadText(
      `${safeFileName(project.title)}-视频混剪脚本.md`,
      buildVideoPackage(store, project),
      "text/markdown;charset=utf-8",
    );
    updateProject(project.id, { status: "已导出脚本" });
  }

  return (
    <section className="grid collectionWorkspace">
      {view === "versions" ? (
        <Panel title="账号版本转视频">
          <div className="list">
            {store.accountVersions.length ? (
              store.accountVersions.map((version) => (
                <article className="card" key={version.id}>
                  <strong>{version.title}</strong>
                  <span>
                    {nameOf(store.accounts, version.accountId)} 路{" "}
                    {version.status}
                  </span>
                  <small>{version.body.slice(0, 100)}</small>
                  <button
                    className="inlineButton"
                    onClick={() => createProject(version)}
                  >
                    生成视频工程
                  </button>
                </article>
              ))
            ) : (
              <div className="emptyAction">
                <p className="muted">
                  暂无账号版本。可以先在“平台版本”里生成账号版本，也可以直接从母版文章创建视频工程。
                </p>
                {store.masterArticles.length ? (
                  store.masterArticles.map((article) => (
                    <article className="card subtle" key={article.id}>
                      <strong>{article.title}</strong>
                      <small>{article.body.slice(0, 120)}...</small>
                      <button
                        className="inlineButton"
                        onClick={() => createProjectFromArticle(article)}
                      >
                        直接生成视频工程
                      </button>
                    </article>
                  ))
                ) : (
                  <p className="muted">
                    还没有母版文章，请先到“母版文章”生成一篇内容。
                  </p>
                )}
              </div>
            )}
          </div>
        </Panel>
      ) : null}

      {view === "projects" ? (
        <Panel title="视频工程">
          <div className="list">
            {store.videoProjects.length ? (
              store.videoProjects.map((project) => (
                <article className="card" key={project.id}>
                  <strong>{project.title}</strong>
                  <span>
                    {project.aspectRatio || "9:16"} 路{" "}
                    {project.duration || "60s"} 路 {project.voice} 路{" "}
                    {project.status}
                  </span>
                  <label>
                    配音
                    <input
                      value={project.voice}
                      onChange={(event) =>
                        updateProject(project.id, {
                          voice: event.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    时长
                    <input
                      value={project.duration || ""}
                      onChange={(event) =>
                        updateProject(project.id, {
                          duration: event.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <label>
                    混剪说明
                    <textarea
                      rows={4}
                      value={project.editNotes || ""}
                      onChange={(event) =>
                        updateProject(project.id, {
                          editNotes: event.currentTarget.value,
                        })
                      }
                    />
                  </label>
                  <div className="timeline">
                    {storyboardSegments(project.storyboard).map(
                      (segment, index) => (
                        <article
                          className="timelineStep"
                          key={`${project.id}_${index}`}
                        >
                          <b>{String(index + 1).padStart(2, "0")}</b>
                          <span>{segment}</span>
                        </article>
                      ),
                    )}
                  </div>
                  <small>
                    引用素材：{" "}
                    {project.assetIds
                      ?.map((id) => nameOf(store.assets, id))
                      .join("、") || "暂无"}
                  </small>
                  <details>
                    <summary>字幕预览</summary>
                    <pre>{project.subtitles}</pre>
                  </details>
                  <div className="rowActions">
                    <button
                      className="inlineButton"
                      onClick={() => exportProject(project)}
                    >
                      导出混剪脚本
                    </button>
                    <button
                      className="inlineButton"
                      onClick={() =>
                        updateProject(project.id, { status: "剪辑中" })
                      }
                    >
                      标记剪辑中
                    </button>
                    <button
                      className="inlineButton"
                      onClick={() =>
                        updateProject(project.id, { status: "待发布" })
                      }
                    >
                      标记待发布
                    </button>
                  </div>
                </article>
              ))
            ) : (
              <p className="muted">暂无视频工程。</p>
            )}
          </div>
        </Panel>
      ) : null}
    </section>
  );
}

function Assets({ store, mutate, view }: Ctx & { view: AssetSubKey }) {
  const [duplicateMatches, setDuplicateMatches] = React.useState<
    DuplicateMatch[]
  >([]);
  const [knowledgeTree, setKnowledgeTree] = React.useState<KnowledgeTreeNode[]>([]);
  const [selectedKnowledgePath, setSelectedKnowledgePath] = React.useState("");
  const [selectedKnowledge, setSelectedKnowledge] =
    React.useState<KnowledgeArticle | null>(null);
  const [knowledgeQuery, setKnowledgeQuery] = React.useState("");
  const [knowledgePublishFilter, setKnowledgePublishFilter] = React.useState("");
  const [knowledgeStatus, setKnowledgeStatus] = React.useState("");
  const [publishDialogOpen, setPublishDialogOpen] = React.useState(false);
  const [editingKnowledgeRecord, setEditingKnowledgeRecord] =
    React.useState<KnowledgePublishRecord | null>(null);

  const allKnowledgeFiles = React.useMemo(
    () => flattenKnowledgeTree(knowledgeTree).filter((node) => node.kind === "file"),
    [knowledgeTree],
  );
  const filteredKnowledgeTree = React.useMemo(
    () => filterKnowledgeTree(knowledgeTree, knowledgeQuery, "", knowledgePublishFilter),
    [knowledgeTree, knowledgeQuery, knowledgePublishFilter],
  );

  React.useEffect(() => {
    if (view !== "knowledge") return;
    void refreshCloudKnowledge();
  }, [view]);

  async function refreshCloudKnowledge() {
    try {
      setKnowledgeStatus("正在读取 Cloudflare 知识库...");
      const result = await fetchKnowledgeTree();
      if (!result) {
        setKnowledgeStatus("未配置云端 API，可选择本地目录读取。");
        return;
      }
      const tree = result.tree.map(remoteKnowledgeNodeToLocal);
      setKnowledgeTree(tree);
      const firstFile = flattenKnowledgeTree(tree).find((node) => node.kind === "file");
      if (firstFile) await openKnowledgeNode(firstFile);
      setKnowledgeStatus(`云端知识库已读取 ${flattenKnowledgeTree(tree).filter((node) => node.kind === "file").length} 篇 Markdown`);
    } catch (error) {
      setKnowledgeStatus(error instanceof Error ? error.message : "读取云端知识库失败");
    }
  }

  async function openKnowledgeNode(node: KnowledgeTreeNode) {
    if (node.kind !== "file") return;
    const remote = node.file ? null : await fetchKnowledgeFile(node.path);
    const content = node.file ? await node.file.text() : remote?.content || "";
    if (!content) {
      setKnowledgeStatus("未读取到知识库正文。");
      return;
    }
    const parsed = parseKnowledgeMarkdown(content, node.title);
    setSelectedKnowledgePath(node.path);
    setSelectedKnowledge({
      title: parsed.title,
      path: node.path,
      content: parsed.content,
      updatedAt: node.updatedAt || node.file?.lastModified || Date.now(),
      tags: parsed.tags.length ? parsed.tags : node.tags || [],
      nodeId: remote?.nodeId || node.nodeId || "",
      publishStatus: remote?.publishStatus || node.publishStatus || "unpublished",
      publishedAt: remote?.publishedAt || node.publishedAt || "",
      publishNote: remote?.publishNote || node.publishNote || "",
      publishRecords: remote?.publishRecords || [],
    });
    setEditingKnowledgeRecord(null);
  }

  async function changeKnowledgePublishStatus(
    publishStatus: string,
    publishNote = selectedKnowledge?.publishNote || "",
  ) {
    if (!selectedKnowledge?.nodeId) return;
    const result = await updateKnowledgePublishStatus({
      nodeId: selectedKnowledge.nodeId,
      publishStatus,
      publishNote,
    });
    if (!result) return;
    setSelectedKnowledge({
      ...selectedKnowledge,
      publishStatus: result.publishStatus,
      publishedAt: result.publishedAt,
      publishNote: result.publishNote,
    });
    setKnowledgeTree((tree) =>
      updateKnowledgeTreePublishStatus(tree, selectedKnowledge.nodeId, result),
    );
  }

  async function saveKnowledgePublishRecord(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedKnowledge?.nodeId) return;
    const form = new FormData(event.currentTarget);
    const payload = {
      platformName: String(form.get("platformName") || ""),
      siteName: String(form.get("siteName") || ""),
      accountName: String(form.get("accountName") || ""),
      publishUrl: String(form.get("publishUrl") || ""),
      publishStatus: String(form.get("publishStatus") || "pending"),
      publishedAt: String(form.get("publishedAt") || ""),
      publishNote: String(form.get("publishNote") || ""),
    };
    const result = editingKnowledgeRecord
      ? await updateKnowledgePublishRecord(editingKnowledgeRecord.id, payload)
      : await createKnowledgePublishRecord(selectedKnowledge.nodeId, payload);
    if (!result?.record) return;
    const records = editingKnowledgeRecord
      ? selectedKnowledge.publishRecords.map((record) =>
          record.id === result.record.id ? result.record : record,
        )
      : [result.record, ...selectedKnowledge.publishRecords];
    setSelectedKnowledge({ ...selectedKnowledge, publishRecords: records });
    setEditingKnowledgeRecord(null);
    event.currentTarget.reset();
  }

  async function removeKnowledgePublishRecord(recordId: string) {
    if (!selectedKnowledge) return;
    await deleteKnowledgePublishRecord(recordId);
    setSelectedKnowledge({
      ...selectedKnowledge,
      publishRecords: selectedKnowledge.publishRecords.filter(
        (record) => record.id !== recordId,
      ),
    });
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = formValues(form);
    const fileInput = form.elements.namedItem(
      "file",
    ) as HTMLInputElement | null;
    const file = fileInput?.files?.[0] || null;
    const assetId = uid("asset");
    const contentHash = file ? await fileSha256(file) : "";
    const localMatches = localAssetDuplicates(
      store,
      contentHash,
      data.platformId,
    );
    const remoteMatches = contentHash
      ? await checkDuplicates({
          kind: "asset",
          contentHash,
          platformId: data.platformId,
        }).catch(() => null)
      : null;
    const matches = remoteMatches?.matches?.length
      ? remoteMatches.matches
      : localMatches;
    const localArchive = buildArchiveName({
      root: "materials",
      businessLineName: nameOf(store.businessLines, data.businessLineId),
      platformName: data.platformId
        ? nameOf(store.platforms, data.platformId)
        : "all-platforms",
      type: data.type,
      title: data.name || file?.name || "asset",
      idValue: assetId,
      extension: file ? fileExtension(file.name) : "link",
    });
    setDuplicateMatches(matches);
    const remoteAsset = file
      ? await uploadRemoteAsset<RemoteRow>({
          id: assetId,
          file,
          name: data.name || file.name,
          assetType: data.type,
          businessLineId: data.businessLineId,
          platformId: data.platformId,
          tags: data.tags,
          contentHash,
        }).catch(() => null)
      : await createRemote<RemoteRow>("materialAssets", {
          id: assetId,
          name: data.name,
          asset_type: data.type,
          source_url: data.path,
          business_line_id: data.businessLineId,
          platform_id: data.platformId,
          tags: values(data.tags),
          content_hash: contentHash,
          duplicate_status: matches.length ? "duplicate" : "clear",
          duplicate_notes: matches.map((match) => match.title).join("; "),
          archive_dir: localArchive.archiveDir,
          file_name: localArchive.fileName,
          status: "ready",
        }).catch(() => null);
    mutate(
      (draft) => {
        const asset = remoteAsset
          ? mapMaterialAsset(remoteAsset)
          : {
              id: assetId,
              name: data.name || file?.name || "未命名素材",
              type: data.type,
              path: file ? file.name : data.path,
              tags: data.tags,
              businessLineId: data.businessLineId,
              platformId: data.platformId,
              mimeType: file?.type || "",
              size: file?.size || 0,
              storageKey: "",
              status: "local",
              contentHash,
              duplicateStatus: matches.length ? "duplicate" : "clear",
              duplicateNotes: matches.map((match) => match.title).join("; "),
              archiveDir: localArchive.archiveDir,
              fileName: localArchive.fileName,
              createdAt: now(),
            };
        upsertById(draft.assets, asset);
      },
      file ? "素材已上传登记" : "素材已登记",
    );
    form.reset();
  }
  if (view === "knowledge") {
    return (
      <section className="knowledgeWorkspace">
        <section className="panel">
          <div className="knowledgeFilters">
            <input
              value={knowledgeQuery}
              onChange={(event) => setKnowledgeQuery(event.target.value)}
              placeholder="搜索知识标题"
            />
            <select
              value={knowledgePublishFilter}
              onChange={(event) => setKnowledgePublishFilter(event.target.value)}
            >
              <option value="">全部发布状态</option>
              {knowledgePublishStatuses.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          <p className="muted">当前来源：Cloudflare Web 知识库。</p>
          {knowledgeStatus ? <p className="muted">{knowledgeStatus}</p> : null}
          <div className="knowledgeLayout">
            <div className="knowledgeTreePanel">
              {filteredKnowledgeTree.length ? (
                <div className="knowledgeTree">
                  {filteredKnowledgeTree.map((node) => (
                    <KnowledgeTreeItem
                      key={node.id}
                      node={node}
                      selectedPath={selectedKnowledgePath}
                      onOpen={openKnowledgeNode}
                    />
                  ))}
                </div>
              ) : (
                <p className="muted">请选择包含 Markdown 文件的知识库目录。</p>
              )}
            </div>
            <article className="knowledgeReader">
              {selectedKnowledge ? (
                <>
                  <div className="knowledgeArticleHeader">
                    <div>
                      <h2>{selectedKnowledge.title}</h2>
                      <div className="tagList">
                        <span className={`publishBadge status-${selectedKnowledge.publishStatus}`}>
                          {knowledgePublishStatusLabel(selectedKnowledge.publishStatus)}
                        </span>
                        {selectedKnowledge.tags.length ? (
                          selectedKnowledge.tags.map((tag) => <span key={tag}>{tag}</span>)
                        ) : (
                          <span>未设置标签</span>
                        )}
                      </div>
                      <div className="detailMeta">
                        <span>创建/更新时间：{formatDate(new Date(selectedKnowledge.updatedAt).toISOString())}</span>
                        {selectedKnowledge.publishedAt ? (
                          <span>发布时间：{formatDate(selectedKnowledge.publishedAt)}</span>
                        ) : null}
                        <span>{selectedKnowledge.path}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="inlineButton"
                      onClick={() => setPublishDialogOpen(true)}
                    >
                      发布管理
                    </button>
                  </div>
                  {publishDialogOpen ? (
                    <div className="modalOverlay">
                      <div className="modalPanel knowledgePublishModal">
                        <div className="modalHeader">
                          <div>
                            <strong>发布管理</strong>
                            <span>{selectedKnowledge.title}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setPublishDialogOpen(false);
                              setEditingKnowledgeRecord(null);
                            }}
                          >
                            关闭
                          </button>
                        </div>
                        <div className="knowledgePublishPanel">
                          <div className="formGrid two">
                            <label>
                              发布状态
                              <select
                                value={selectedKnowledge.publishStatus}
                                onChange={(event) => void changeKnowledgePublishStatus(event.target.value)}
                              >
                                {knowledgePublishStatuses.map((status) => (
                                  <option key={status.value} value={status.value}>
                                    {status.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label>
                              发布备注
                              <input
                                value={selectedKnowledge.publishNote}
                                onChange={(event) =>
                                  setSelectedKnowledge({
                                    ...selectedKnowledge,
                                    publishNote: event.target.value,
                                  })
                                }
                                onBlur={(event) =>
                                  void changeKnowledgePublishStatus(
                                    selectedKnowledge.publishStatus,
                                    event.target.value,
                                  )
                                }
                                placeholder="发布平台、账号、链接或说明"
                              />
                            </label>
                          </div>
                          <div className="sectionHeader compact">
                            <strong>发布记录</strong>
                          </div>
                          <form
                            key={editingKnowledgeRecord?.id || "new-record"}
                            className="knowledgePublishForm"
                            onSubmit={saveKnowledgePublishRecord}
                          >
                            <input name="platformName" placeholder="平台，如公众号" defaultValue={editingKnowledgeRecord?.platformName || ""} />
                            <input name="siteName" placeholder="网站/渠道" defaultValue={editingKnowledgeRecord?.siteName || ""} />
                            <input name="accountName" placeholder="账号" defaultValue={editingKnowledgeRecord?.accountName || ""} />
                            <select name="publishStatus" defaultValue={editingKnowledgeRecord?.publishStatus || "pending"}>
                              {knowledgeRecordStatuses.map((status) => (
                                <option key={status.value} value={status.value}>
                                  {status.label}
                                </option>
                              ))}
                            </select>
                            <input name="publishedAt" type="datetime-local" defaultValue={toDateTimeLocal(editingKnowledgeRecord?.publishedAt || "")} />
                            <input name="publishUrl" placeholder="发布链接" defaultValue={editingKnowledgeRecord?.publishUrl || ""} />
                            <input name="publishNote" placeholder="备注" defaultValue={editingKnowledgeRecord?.publishNote || ""} />
                            <button type="submit" className="primaryButton">
                              {editingKnowledgeRecord ? "保存记录" : "新增记录"}
                            </button>
                            {editingKnowledgeRecord ? (
                              <button type="button" className="ghost" onClick={() => setEditingKnowledgeRecord(null)}>
                                取消
                              </button>
                            ) : null}
                          </form>
                          <div className="tableWrap">
                            <table>
                              <thead>
                                <tr>
                                  <th>平台</th>
                                  <th>网站/渠道</th>
                                  <th>账号</th>
                                  <th>状态</th>
                                  <th>发布时间</th>
                                  <th>链接</th>
                                  <th>操作</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedKnowledge.publishRecords.length ? (
                                  selectedKnowledge.publishRecords.map((record) => (
                                    <tr key={record.id}>
                                      <td>{record.platformName || "-"}</td>
                                      <td>{record.siteName || "-"}</td>
                                      <td>{record.accountName || "-"}</td>
                                      <td>
                                        <span className={`publishBadge status-${record.publishStatus}`}>
                                          {knowledgeRecordStatusLabel(record.publishStatus)}
                                        </span>
                                      </td>
                                      <td>{record.publishedAt ? formatDate(record.publishedAt) : "-"}</td>
                                      <td>
                                        {record.publishUrl ? (
                                          <a href={record.publishUrl} target="_blank" rel="noreferrer">打开</a>
                                        ) : (
                                          "-"
                                        )}
                                      </td>
                                      <td>
                                        <button type="button" className="ghost" onClick={() => setEditingKnowledgeRecord(record)}>修改</button>
                                        <button type="button" className="dangerButton inlineDanger" onClick={() => void removeKnowledgePublishRecord(record.id)}>删除</button>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={7} className="muted">暂无发布记录。</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div
                    className="markdownPreview readonly knowledgeMarkdown"
                    dangerouslySetInnerHTML={{
                      __html: markdownToHtml(selectedKnowledge.content),
                    }}
                  />
                </>
              ) : (
                <p className="muted">请选择左侧 Markdown 文件查看内容。</p>
              )}
            </article>
          </div>
        </section>
      </section>
    );
  }

  return (
    <section className="grid two">
      <Panel title="登记素材">
        <form className="form" onSubmit={submit}>
          <label>
            素材名称
            <input name="name" required />
          </label>
          <label>
            类型
            <select name="type">
              <option>图片</option>
              <option>视频</option>
              <option>音频</option>
              <option>BGM</option>
              <option>字幕文件</option>
            </select>
          </label>
          <label>
            上传文件
            <input name="file" type="file" />
          </label>
          <label>
            本地或云端路径
            <input name="path" placeholder="不上传文件时可填写外部路径" />
          </label>
          <label>
            业务线 <Select name="businessLineId" rows={store.businessLines} />
          </label>
          <label>
            关联平台
            <select name="platformId">
              <option value="">不关联平台</option>
              {store.platforms.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            标签
            <input name="tags" />
          </label>
          <DuplicateNotice matches={duplicateMatches} />
          <button type="submit">
            <Plus size={16} />
            保存素材
          </button>
        </form>
      </Panel>
      <Panel title="素材库">
        <CardList
          rows={store.assets}
          render={(asset) => (
            <>
              <strong>{asset.name}</strong>
              <span>
                {asset.type} 路{" "}
                {nameOf(store.businessLines, asset.businessLineId)} 路{" "}
                {asset.status}
              </span>
              <small>
                {asset.duplicateStatus === "duplicate"
                  ? `查重：重复素材 ${asset.duplicateNotes}`
                  : "查重：未发现同平台重复"}
              </small>
              <small>
                归档：{" "}
                {asset.archiveDir
                  ? attachmentPath(
                      store.appSettings,
                      asset.archiveDir,
                      asset.fileName,
                    )
                  : asset.storageKey || asset.path || "未归档"}
              </small>
              <small>
                {formatBytes(asset.size)} 路 {asset.mimeType || "未知类型"}
              </small>
            </>
          )}
        />
      </Panel>
    </section>
  );
}

function KnowledgeTreeItem({
  node,
  selectedPath,
  onOpen,
}: {
  node: KnowledgeTreeNode;
  selectedPath: string;
  onOpen: (node: KnowledgeTreeNode) => void;
}) {
  const [open, setOpen] = React.useState(false);
  if (node.kind === "folder") {
    return (
      <div className="knowledgeTreeNode">
        <button type="button" className="knowledgeFolder" onClick={() => setOpen(!open)}>
          <span className="treeToggle">{open ? "▾" : "▸"}</span>
          <span className="treeIcon treeFolderIcon" aria-hidden="true" />
          <span className="treeLabel">{node.title}</span>
        </button>
        {open ? (
          <div className="knowledgeTreeChildren">
            {node.children.map((child) => (
              <KnowledgeTreeItem
                key={child.id}
                node={child}
                selectedPath={selectedPath}
                onOpen={onOpen}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={`knowledgeFile ${selectedPath === node.path ? "active" : ""}`}
      onClick={() => onOpen(node)}
    >
      <span className="treeToggle treeSpacer">·</span>
      <span className="treeIcon treeFileIcon" aria-hidden="true" />
      <span className="treeText">
        <span className="treeTitleLine">
          <span className={`publishBadge mini status-${node.publishStatus || "unpublished"}`}>
            {knowledgePublishStatusLabel(node.publishStatus)}
          </span>
          <span className="treeLabel">{node.title}</span>
        </span>
        <span className="treeMeta">
          {node.tags?.length ? <small>{node.tags.join(" / ")}</small> : null}
        </span>
      </span>
    </button>
  );
}

function Publishing({ store, mutate }: Ctx) {
  const [preflightMatches, setPreflightMatches] = React.useState<
    DuplicateMatch[]
  >([]);
  async function createPlan(version: AccountVersion) {
    const account = store.accounts.find(
      (item) => item.id === version.accountId,
    );
    if (!account) return;
    const matches = await publishPreflight(store, version, account.platformId);
    setPreflightMatches(matches);
    if (matches.length) return;
    const scheduledAt = nextDateTime();
    const planId = uid("pub");
    void createRemote("publishPlans", {
      id: planId,
      account_version_id: version.id,
      account_id: account.id,
      platform_id: account.platformId,
      scheduled_at: scheduledAt,
      status: "scheduled",
    }).catch(() => undefined);
    mutate((draft) => {
      draft.publishPlans.unshift({
        id: planId,
        accountVersionId: version.id,
        accountId: account.id,
        platformId: account.platformId,
        scheduledAt,
        actualPublishedAt: "",
        status: "scheduled",
        publishUrl: "",
        remark: "",
        createdAt: now(),
      });
    }, "发布计划已创建");
  }
  function markPublished(plan: PublishPlan) {
    const url = window.prompt("请输入发布链接", plan.publishUrl || "https://");
    if (!url) return;
    void postRemote(`/publishPlans/${plan.id}/mark-published`, {
      publish_url: url,
      actual_published_at: now(),
    }).catch(() => undefined);
    mutate((draft) => {
      const found = draft.publishPlans.find((item) => item.id === plan.id);
      if (found) {
        found.status = "published";
        found.publishUrl = url;
        found.actualPublishedAt = now();
      }
    }, "已回填发布链接");
  }
  async function exportPackage(plan: PublishPlan) {
    const version = store.accountVersions.find(
      (item) => item.id === plan.accountVersionId,
    );
    const matches = version
      ? await publishPreflight(store, version, plan.platformId)
      : [];
    setPreflightMatches(matches);
    if (matches.length) return;
    const localPackage = buildLocalExportPackage(store, plan);
    const remote = await postRemote<{ package?: RemoteRow }>(
      "/export-packages",
      {
        publish_plan_id: plan.id,
        account_version_id: plan.accountVersionId,
        account_id: plan.accountId,
        platform_id: plan.platformId,
      },
    ).catch(() => null);
    mutate((draft) => {
      const pack = remote?.package
        ? mapExportPackage(remote.package)
        : localPackage;
      upsertById(draft.exportPackages, pack);
      draft.jobs.unshift({
        id: uid("job"),
        type: "export_package",
        status: "done",
        payload: JSON.stringify({
          publishPlanId: plan.id,
          exportPackageId: pack.id,
        }),
        createdAt: now(),
      });
    }, "发布包已生成，可下载 Markdown/TXT/JSON");
  }
  return (
    <section className="grid two">
      <Panel title="从账号版本创建排期">
        <DuplicateNotice matches={preflightMatches} />
        <div className="list">
          {store.accountVersions.map((version) => (
            <article className="card" key={version.id}>
              <strong>{version.title}</strong>
              <span>
                {nameOf(store.accounts, version.accountId)} 路 {version.status}
              </span>
              <button
                className="inlineButton"
                onClick={() => createPlan(version)}
              >
                创建发布计划
              </button>
            </article>
          ))}
        </div>
      </Panel>
      <Panel title="发布排期与回填">
        <div className="list">
          {store.publishPlans.map((plan) => (
            <article className="card" key={plan.id}>
              <strong>{nameOf(store.accounts, plan.accountId)}</strong>
              <span>
                {nameOf(store.platforms, plan.platformId)} 路 {plan.status} 路{" "}
                {formatDate(plan.scheduledAt)}
              </span>
                <small>{plan.publishUrl || "待人工发布后回填链接"}</small>
              <div className="rowActions">
                <button
                  className="inlineButton"
                  onClick={() => exportPackage(plan)}
                >
                  导出发布包
                </button>
                <button
                  className="inlineButton"
                  onClick={() => markPublished(plan)}
                >
                  回填发布链接
                </button>
              </div>
            </article>
          ))}
        </div>
      </Panel>
      <Panel title="发布包下载">
        <div className="list">
          {store.exportPackages.map((pack) => (
            <article className="card" key={pack.id}>
              <strong>{pack.title}</strong>
              <span>
                {nameOf(store.accounts, pack.accountId)} 路{" "}
                {nameOf(store.platforms, pack.platformId)} 路 {pack.status}
              </span>
              <small>发布计划：{pack.publishPlanId}</small>
              <div className="rowActions">
                <button
                  className="inlineButton"
                  onClick={() =>
                    downloadText(
                      pack.fileName
                        ? pack.fileName.replace(/\.json$/i, ".md")
                        : `${safeFileName(pack.title)}.md`,
                      pack.markdown,
                      "text/markdown;charset=utf-8",
                    )
                  }
                >
                  Markdown
                </button>
                <button
                  className="inlineButton"
                  onClick={() =>
                    downloadText(
                      pack.fileName
                        ? pack.fileName.replace(/\.json$/i, ".txt")
                        : `${safeFileName(pack.title)}.txt`,
                      pack.text,
                      "text/plain;charset=utf-8",
                    )
                  }
                >
                  TXT
                </button>
                <button
                  className="inlineButton"
                  onClick={() =>
                    downloadText(
                      pack.fileName || `${safeFileName(pack.title)}.json`,
                      pack.json,
                      "application/json;charset=utf-8",
                    )
                  }
                >
                  JSON
                </button>
              </div>
            </article>
          ))}
        </div>
      </Panel>
    </section>
  );
}

function AnalyticsV2({ store, mutate }: Ctx) {
  const totals = store.metrics.reduce(
    (sum, item) => ({
      views: sum.views + item.views,
      likes: sum.likes + item.likes,
      favorites: sum.favorites + item.favorites,
      comments: sum.comments + item.comments,
      shares: sum.shares + item.shares,
      followersGain: sum.followersGain + item.followersGain,
    }),
    {
      views: 0,
      likes: 0,
      favorites: 0,
      comments: 0,
      shares: 0,
      followersGain: 0,
    },
  );
  const ranked = [...store.metrics]
    .sort((a, b) => engagementScore(b) - engagementScore(a))
    .slice(0, 8);

  function addMetric(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    mutate((draft) => {
      draft.metrics.unshift({
        id: uid("metric"),
        publishPlanId: data.publishPlanId,
        views: num(data.views),
        likes: num(data.likes),
        favorites: num(data.favorites),
        comments: num(data.comments),
        shares: num(data.shares),
        followersGain: num(data.followersGain),
        createdAt: now(),
      });
    }, "发布数据已录入");
    event.currentTarget.reset();
  }

  function quickMetric(plan: PublishPlan) {
    mutate((draft) => {
      draft.metrics.unshift({
        id: uid("metric"),
        publishPlanId: plan.id,
        views: rand(800, 6000),
        likes: rand(20, 300),
        favorites: rand(10, 180),
        comments: rand(2, 60),
        shares: rand(1, 40),
        followersGain: rand(0, 30),
        createdAt: now(),
      });
    }, "已录入一条模拟复盘数据");
  }

  function exportReport() {
    downloadText(
      `mediapilotx-数据复盘-${new Date().toISOString().slice(0, 10)}.md`,
      buildAnalyticsReport(store),
      "text/markdown;charset=utf-8",
    );
  }

  return (
    <section className="grid two">
      <Panel title="发布数据录入">
        <form className="form" onSubmit={addMetric}>
          <label>
            发布计划
            <select name="publishPlanId" required>
              {store.publishPlans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {metricPlanTitle(store, plan.id)}
                </option>
              ))}
            </select>
          </label>
          <label>
            阅读/播放
            <input name="views" type="number" min="0" required />
          </label>
          <label>
            鐐硅禐
            <input name="likes" type="number" min="0" defaultValue="0" />
          </label>
          <label>
            收藏
            <input name="favorites" type="number" min="0" defaultValue="0" />
          </label>
          <label>
            璇勮
            <input name="comments" type="number" min="0" defaultValue="0" />
          </label>
          <label>
              转发
            <input name="shares" type="number" min="0" defaultValue="0" />
          </label>
          <label>
            涨粉
            <input
              name="followersGain"
              type="number"
              min="0"
              defaultValue="0"
            />
          </label>
          <button type="submit">
            <Plus size={16} />
            保存数据
          </button>
        </form>
        <div className="list spaced">
          {store.publishPlans.slice(0, 5).map((plan) => (
            <article className="card" key={plan.id}>
              <strong>{metricPlanTitle(store, plan.id)}</strong>
              <span>
                {plan.status} · {formatDate(plan.scheduledAt)}
              </span>
              <button
                className="inlineButton"
                onClick={() => quickMetric(plan)}
              >
                快速模拟数据
              </button>
            </article>
          ))}
        </div>
      </Panel>

      <Panel title="复盘总览">
        <section className="metrics embedded">
          <Metric label="阅读/播放" value={String(totals.views)} tone="blue" />
          <Metric
            label="互动"
            value={String(
              totals.likes + totals.favorites + totals.comments + totals.shares,
            )}
            tone="green"
          />
          <Metric
            label="涨粉"
            value={String(totals.followersGain)}
            tone="orange"
          />
          <Metric
            label="互动率"
            value={`${engagementRate(totals)}%`}
            tone="dark"
          />
        </section>
        <button className="inlineButton" onClick={exportReport}>
          导出复盘报告
        </button>
      </Panel>

      <Panel title="内容表现排行">
        <div className="tableWrap">
          <table>
            <thead>
              <tr>
                <th>内容</th>
                <th>账号</th>
                <th>播放</th>
                <th>互动率</th>
                <th>涨粉</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((metric) => (
                <tr key={metric.id}>
                  <td>{metricPlanTitle(store, metric.publishPlanId)}</td>
                  <td>{metricAccountName(store, metric.publishPlanId)}</td>
                  <td>{metric.views}</td>
                  <td>{engagementRate(metric)}%</td>
                  <td>{metric.followersGain}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="数据明细">
        <div className="tableWrap">
          <table>
            <thead>
              <tr>
                <th>发布计划</th>
                <th>阅读/播放</th>
                <th>点赞</th>
                <th>收藏</th>
                <th>璇勮</th>
                    <th>转发</th>
                <th>涨粉</th>
              </tr>
            </thead>
            <tbody>
              {store.metrics.map((metric) => (
                <tr key={metric.id}>
                  <td>{metricPlanTitle(store, metric.publishPlanId)}</td>
                  <td>{metric.views}</td>
                  <td>{metric.likes}</td>
                  <td>{metric.favorites}</td>
                  <td>{metric.comments}</td>
                  <td>{metric.shares}</td>
                  <td>{metric.followersGain}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </section>
  );
}

function Analytics({ store, mutate }: Ctx) {
  function addMetric(plan: PublishPlan) {
    mutate((draft) => {
      draft.metrics.unshift({
        id: uid("metric"),
        publishPlanId: plan.id,
        views: rand(800, 6000),
        likes: rand(20, 300),
        favorites: rand(10, 180),
        comments: rand(2, 60),
        shares: rand(1, 40),
        followersGain: rand(0, 30),
        createdAt: now(),
      });
    }, "已录入一条复盘数据");
  }
  return (
    <section className="grid two">
      <Panel title="发布数据录入">
        <div className="list">
          {store.publishPlans.map((plan) => (
            <article className="card" key={plan.id}>
              <strong>{nameOf(store.accounts, plan.accountId)}</strong>
              <span>{plan.status}</span>
              <button className="inlineButton" onClick={() => addMetric(plan)}>
                快速录入模拟数据
              </button>
            </article>
          ))}
        </div>
      </Panel>
      <Panel title="内容表现">
        <div className="tableWrap">
          <table>
            <thead>
              <tr>
                <th>发布计划</th>
                <th>阅读/播放</th>
                <th>点赞</th>
                <th>收藏</th>
                <th>评论</th>
                <th>涨粉</th>
              </tr>
            </thead>
            <tbody>
              {store.metrics.map((metric) => (
                <tr key={metric.id}>
                  <td>{metric.publishPlanId}</td>
                  <td>{metric.views}</td>
                  <td>{metric.likes}</td>
                  <td>{metric.favorites}</td>
                  <td>{metric.comments}</td>
                  <td>{metric.followersGain}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </section>
  );
}

function AIConfigV2({
  store,
  mutate,
  mode = "prompts",
}: Ctx & { mode?: "prompts" | "models" }) {
  const [previewPromptId, setPreviewPromptId] = React.useState(
    store.promptTemplates[0]?.id || "",
  );
  const [editingModelId, setEditingModelId] = React.useState("");
  const [modelFormOpen, setModelFormOpen] = React.useState(false);
  const [testingModelId, setTestingModelId] = React.useState("");
  const [modelTestResults, setModelTestResults] = React.useState<
    Record<string, { ok: boolean; message: string }>
  >({});
  const previewPrompt =
    store.promptTemplates.find((item) => item.id === previewPromptId) ||
    store.promptTemplates[0];
  const editingModel = store.aiModels.find(
    (item) => item.id === editingModelId,
  );

  function addPrompt(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    const promptId = uid("pt");
    void createRemote("promptTemplates", {
      id: promptId,
      name: data.name,
      scene: data.scene,
      prompt_body: data.promptBody,
      version: data.version || "1.0",
      enabled: true,
    }).catch(() => undefined);
    mutate((draft) => {
      draft.promptTemplates.unshift({
        id: promptId,
        name: data.name,
        scene: data.scene,
        promptBody: data.promptBody,
        version: data.version || "1.0",
        enabled: true,
        createdAt: now(),
      });
    }, "Prompt 模板已保存");
    setPreviewPromptId(promptId);
    event.currentTarget.reset();
  }

  function addModel(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    const modelId = editingModel?.id || uid("model");
    mutate(
      (draft) => {
        upsertById(draft.aiModels, {
          id: modelId,
          provider: data.provider,
          name: data.name,
          code: data.code,
          taskType: data.taskType,
          baseUrl: data.baseUrl,
          apiKeyName: data.apiKeyName,
          apiKey: data.apiKey,
          notes: data.notes,
          enabled: editingModel?.enabled ?? true,
          createdAt: editingModel?.createdAt || now(),
        });
      },
      editingModel ? "AI 模型配置已更新" : "AI 模型已添加",
    );
    setEditingModelId("");
    setModelFormOpen(false);
    event.currentTarget.reset();
  }

  function openNewModelForm() {
    setEditingModelId("");
    setModelFormOpen(true);
  }

  function closeModelForm() {
    setEditingModelId("");
    setModelFormOpen(false);
  }

  function togglePrompt(promptId: string) {
    mutate((draft) => {
      const prompt = draft.promptTemplates.find((item) => item.id === promptId);
      if (prompt) prompt.enabled = !prompt.enabled;
    }, "Prompt 状态已更新");
  }

  function toggleModel(modelId: string) {
    mutate((draft) => {
      const model = draft.aiModels.find((item) => item.id === modelId);
      if (model) model.enabled = !model.enabled;
    }, "模型状态已更新");
  }

  async function testModel(model: AiModel) {
    if (!model.apiKey) {
      setModelTestResults((current) => ({
        ...current,
        [model.id]: { ok: false, message: "请先填写 API Key" },
      }));
      return;
    }
    if (!model.baseUrl) {
      setModelTestResults((current) => ({
        ...current,
        [model.id]: { ok: false, message: "请先填写 API Base URL" },
      }));
      return;
    }
    setTestingModelId(model.id);
    setModelTestResults((current) => ({
      ...current,
      [model.id]: { ok: false, message: "测试中..." },
    }));
    try {
      const result = await postRemote<{
        ok: boolean;
        error?: string;
        content?: string;
        latencyMs?: number;
      }>("/ai/test-model", {
        provider: model.provider,
        name: model.name,
        code: model.code,
        baseUrl: model.baseUrl,
        apiKey: model.apiKey,
      });
      if (!result) {
        throw new Error("当前没有可用 API 代理，请先启动本地 API 服务");
      }
      setModelTestResults((current) => ({
        ...current,
        [model.id]: {
          ok: Boolean(result.ok),
          message: result.ok
            ? `测试通过：${result.content || "连接成功"} · ${result.latencyMs || 0}ms`
            : `测试失败：${result.error || "模型返回异常"}`,
        },
      }));
    } catch (error) {
      setModelTestResults((current) => ({
        ...current,
        [model.id]: {
          ok: false,
          message: error instanceof Error ? error.message : "测试失败",
        },
      }));
    } finally {
      setTestingModelId("");
    }
  }

  function exportConfig() {
    downloadText(
      `mediapilotx-ai-config-${new Date().toISOString().slice(0, 10)}.json`,
      JSON.stringify(
        { models: store.aiModels, promptTemplates: store.promptTemplates },
        null,
        2,
      ),
      "application/json;charset=utf-8",
    );
  }

  return (
    <section className={mode === "models" ? "grid" : "grid two"}>
      {mode === "prompts" ? (
        <Panel title="Prompt 模板">
          <form className="form" onSubmit={addPrompt}>
            <label>
              模板名称
              <input name="name" required />
            </label>
            <label>
              场景
              <select name="scene">
                <option>平台改写</option>
                <option>账号差异化改写</option>
                <option>图文Prompt</option>
                <option>视频脚本</option>
                <option>标题优化</option>
                <option>查重改写</option>
              </select>
            </label>
            <label>
              版本
              <input name="version" defaultValue="1.0" />
            </label>
            <label>
              模板正文
              <textarea
                name="promptBody"
                rows={8}
                required
                placeholder="{{business_line}} {{platform}} {{account_persona}} {{source_content}}"
              />
            </label>
            <button type="submit">
              <Plus size={16} />
              保存模板
            </button>
          </form>
        </Panel>
      ) : null}

      {mode === "models" && (modelFormOpen || editingModel) ? (
        <div className="modalOverlay">
          <form
            className="modalPanel modelModal"
            key={editingModel?.id || "new-model"}
            onSubmit={addModel}
          >
            <div className="modalHeader">
              <div>
                <strong>{editingModel ? "修改模型" : "新增模型"}</strong>
                <span>填写供应商、模型代码、调用地址和 API Key。</span>
              </div>
              <button type="button" className="ghost" onClick={closeModelForm}>
                关闭
              </button>
            </div>
            <div className="form modelForm">
              <label>
                供应商 <input
                  name="provider"
                  required
                  placeholder="OpenAI / DeepSeek / 通义千问"
                  defaultValue={editingModel?.provider || ""}
                />
              </label>
              <label>
                模型名称
                <input
                  name="name"
                  required
                  placeholder="内容生成模型"
                  defaultValue={editingModel?.name || ""}
                />
              </label>
              <label>
                模型代码
                <input
                  name="code"
                  required
                  placeholder="qwen-plus / deepseek-chat"
                  defaultValue={editingModel?.code || ""}
                />
              </label>
              <label>
                任务类型
                <select
                  name="taskType"
                  defaultValue={editingModel?.taskType || "内容生成"}
                >
                  <option>内容生成</option>
                  <option>改写</option>
                  <option>长文分析</option>
                  <option>图文Prompt</option>
                  <option>视频脚本</option>
                  <option>审核查重</option>
                </select>
              </label>
              <label>
                API Base URL
                <input
                  name="baseUrl"
                  placeholder="https://api.example.com/v1"
                  defaultValue={editingModel?.baseUrl || ""}
                />
              </label>
              <label>
                Key 名称
                <input
                  name="apiKeyName"
                  placeholder="DASHSCOPE_API_KEY"
                  defaultValue={editingModel?.apiKeyName || ""}
                />
              </label>
              <label>
                API Key
                <input
                  name="apiKey"
                  type="password"
                  placeholder="由你修改填入，暂只保存在本地配置"
                  defaultValue={editingModel?.apiKey || ""}
                />
              </label>
              <label>
                备注
                <textarea
                  name="notes"
                  rows={3}
                  placeholder="控制台地址、Endpoint ID、调用方式等"
                  defaultValue={editingModel?.notes || ""}
                />
              </label>
            </div>
            <div className="modalActions">
              <button type="submit" className="primaryButton">
                <Plus size={16} />
                {editingModel ? "保存模型" : "添加模型"}
              </button>
              <button
                type="button"
                className="inlineButton"
                onClick={closeModelForm}
              >
                取消
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {mode === "prompts" ? (
        <Panel title="模板预览">
          <label>
            选择模板
            <select
              value={previewPrompt?.id || ""}
              onChange={(event) =>
                setPreviewPromptId(event.currentTarget.value)
              }
            >
              {store.promptTemplates.map((prompt) => (
                <option key={prompt.id} value={prompt.id}>
                  {prompt.name}
                </option>
              ))}
            </select>
          </label>
          <article className="card">
            <strong>{previewPrompt?.name || "暂无模板"}</strong>
            <span>
              {previewPrompt?.scene || ""} 路 v{previewPrompt?.version || ""}
            </span>
            <pre>{renderPromptPreview(previewPrompt?.promptBody || "")}</pre>
          </article>
        </Panel>
      ) : null}

      <Panel title={mode === "models" ? "模型列表" : "Prompt 模板与 AI 日志"}>
        {mode === "models" ? (
          <>
            <div className="listToolbar">
              <div>
                <strong>AI 模型列表</strong>
                <p className="muted">
                  维护供应商、模型代码、Base URL 与 API Key。
                </p>
              </div>
              <div className="toolbarActions">
                <button
                  type="button"
                  className="primaryButton"
                  onClick={openNewModelForm}
                >
                  <Plus size={16} />
                  新增模型
                </button>
                <button className="inlineButton" onClick={exportConfig}>
                  导出 AI 配置
                </button>
              </div>
            </div>
            <div className="tableWrap modelTable">
              <table>
                <thead>
                  <tr>
                    <th>模型</th>
                    <th>供应商</th>
                    <th>模型代码</th>
                    <th>任务类型</th>
                    <th>Key</th>
                    <th>状态</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {store.aiModels.map((model) => (
                    <tr key={model.id}>
                      <td>
                        <div className="stackedCell">
                          <strong>{model.name}</strong>
                          <span>{model.notes || "暂无备注"}</span>
                        </div>
                      </td>
                      <td>{model.provider}</td>
                      <td>{model.code}</td>
                      <td>{model.taskType}</td>
                      <td>
                        <div className="stackedCell compact">
                          <span>{model.apiKeyName || "未配置 Key 名称"}</span>
                          <span
                            className={
                              model.apiKey
                                ? "statusBadge success"
                                : "statusBadge"
                            }
                          >
                            {model.apiKey ? "Key 已填写" : "Key 待填写"}
                          </span>
                          {modelTestResults[model.id] ? (
                            <span
                              className={
                                modelTestResults[model.id].ok
                                  ? "statusBadge success"
                                  : "statusBadge danger"
                              }
                            >
                              {modelTestResults[model.id].message}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>{model.enabled ? "启用" : "停用"}</td>
                      <td>
                        <div className="rowActions">
                          <button
                            className="inlineButton"
                            onClick={() => {
                              setEditingModelId(model.id);
                              setModelFormOpen(true);
                            }}
                          >
                            修改
                          </button>
                          <button
                            className="inlineButton"
                            disabled={testingModelId === model.id}
                            onClick={() => testModel(model)}
                          >
                            {testingModelId === model.id ? "测试中" : "测试"}
                          </button>
                          <button
                            className="inlineButton"
                            onClick={() => toggleModel(model.id)}
                          >
                            {model.enabled ? "停用" : "启用"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
        {mode === "prompts" ? (
          <div className="list">
            {mode === "prompts"
              ? store.promptTemplates.map((prompt) => (
                  <article className="card subtle" key={prompt.id}>
                    <strong>{prompt.name}</strong>
                    <span>
                      {prompt.scene} 路 v{prompt.version} 路{" "}
                      {prompt.enabled ? "启用" : "停用"}
                    </span>
                    <small>{prompt.promptBody}</small>
                    <button
                      className="inlineButton"
                      onClick={() => togglePrompt(prompt.id)}
                    >
                      {prompt.enabled ? "停用" : "启用"}
                    </button>
                  </article>
                ))
              : null}
            {mode === "prompts"
              ? store.aiLogs.map((log) => (
                  <article className="card" key={log.id}>
                    <strong>{log.objectType}</strong>
                    <span>
                      {log.status} 路 楼{log.cost.toFixed(2)}
                    </span>
                    <small>{log.output.slice(0, 120)}...</small>
                  </article>
                ))
              : null}
          </div>
        ) : null}
      </Panel>
    </section>
  );
}

function AIConfig({ store, mutate }: Ctx) {
  function addPrompt(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    const promptId = uid("pt");
    void createRemote("promptTemplates", {
      id: promptId,
      name: data.name,
      scene: data.scene,
      prompt_body: data.promptBody,
      version: "1.0",
      enabled: true,
    }).catch(() => undefined);
    mutate((draft) => {
      draft.promptTemplates.unshift({
        id: promptId,
        name: data.name,
        scene: data.scene,
        promptBody: data.promptBody,
        version: "1.0",
        enabled: true,
        createdAt: now(),
      });
    }, "Prompt 模板已保存");
    event.currentTarget.reset();
  }
  return (
    <section className="grid two">
      <Panel title="模型与 Prompt 配置">
        <form className="form" onSubmit={addPrompt}>
          <label>
            模板名称
            <input name="name" required />
          </label>
          <label>
            鍦烘櫙
            <select name="scene">
              <option>平台改写</option>
              <option>账号差异化改写</option>
              <option>视频脚本</option>
              <option>图文Prompt</option>
            </select>
          </label>
          <label>
            模板正文
            <textarea
              name="promptBody"
              rows={8}
              required
              placeholder="{{business_line}} {{platform}} {{account_persona}}"
            />
          </label>
          <button type="submit">
            <Plus size={16} />
            保存模板
          </button>
        </form>
      </Panel>
      <Panel title="AI 日志与成本">
        <div className="list">
          {store.aiModels.map((model) => (
            <article className="card" key={model.id}>
              <strong>{model.name}</strong>
              <span>
                {model.provider} 路 {model.code} 路 {model.taskType}
              </span>
            </article>
          ))}
          {store.promptTemplates.map((prompt) => (
            <article className="card subtle" key={prompt.id}>
              <strong>{prompt.name}</strong>
              <span>
                {prompt.scene} 路 v{prompt.version}
              </span>
              <small>{prompt.promptBody}</small>
            </article>
          ))}
          {store.aiLogs.map((log) => (
            <article className="card" key={log.id}>
              <strong>{log.objectType}</strong>
              <span>
                {log.status} 路 楼{log.cost.toFixed(2)}
              </span>
              <small>{log.output.slice(0, 120)}...</small>
            </article>
          ))}
        </div>
      </Panel>
    </section>
  );
}

function AccountsV3({ store, mutate, view }: Ctx & { view: AccountSubKey }) {
  const [editingPlatformId, setEditingPlatformId] = React.useState("");
  const [platformFormOpen, setPlatformFormOpen] = React.useState(false);
  const [editingAccountId, setEditingAccountId] = React.useState("");
  const [accountFormOpen, setAccountFormOpen] = React.useState(false);
  const [platformFilter, setPlatformFilter] = React.useState("");
  const [businessFilter, setBusinessFilter] = React.useState("");
  const editingPlatform = store.platforms.find(
    (item) => item.id === editingPlatformId,
  );
  const editingAccount = store.accounts.find(
    (item) => item.id === editingAccountId,
  );
  const filteredAccounts = store.accounts.filter(
    (account) =>
      (!platformFilter || account.platformId === platformFilter) &&
      (!businessFilter || account.businessLineIds.includes(businessFilter)),
  );

  async function savePlatform(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = formValues(form);
    const logoInput = form.elements.namedItem(
      "logoFile",
    ) as HTMLInputElement | null;
    const logoUrl =
      (await fileDataUrl(logoInput?.files?.[0])) ||
      editingPlatform?.logoUrl ||
      "";
    const platformId = editingPlatform?.id || uid("pf");
    const payload = {
      id: platformId,
      name: data.name,
      code: data.code,
      logo_url: logoUrl,
      default_image_ratio: data.ratio || "3:4",
      default_image_pages: Number(data.pages || 1),
      publish_mode: data.publishMode || "manual",
      enabled: data.enabled !== "off",
    };
    const remote = editingPlatform
      ? updateRemote("platforms", platformId, payload)
      : createRemote("platforms", payload);
    void remote.catch(() => undefined);
    mutate(
      (draft) => {
        const row: Platform = {
          id: platformId,
          name: data.name,
          code: data.code,
          logoUrl,
          ratio: data.ratio || "3:4",
          pages: Number(data.pages || 1),
          publishMode: (data.publishMode ||
            "manual") as Platform["publishMode"],
          enabled: data.enabled !== "off",
          createdAt: editingPlatform?.createdAt || now(),
        };
        upsertById(draft.platforms, row);
      },
      editingPlatform ? "平台资料已更新" : "平台已新增",
    );
    setEditingPlatformId("");
    setPlatformFormOpen(false);
    form.reset();
  }

  function openNewPlatformForm() {
    setEditingPlatformId("");
    setPlatformFormOpen(true);
  }

  function editPlatform(platformId: string) {
    setEditingPlatformId(platformId);
    setPlatformFormOpen(true);
  }

  function closePlatformForm() {
    setEditingPlatformId("");
    setPlatformFormOpen(false);
  }

  function deletePlatform(platformId: string) {
    if (!window.confirm("删除平台会同时移除本地关联账号，确定继续吗？")) return;
    void deleteRemote("platforms", platformId).catch(() => undefined);
    mutate((draft) => {
      draft.platforms = draft.platforms.filter(
        (item) => item.id !== platformId,
      );
      draft.accounts = draft.accounts.filter(
        (item) => item.platformId !== platformId,
      );
    }, "平台已删除");
    if (editingPlatformId === platformId) {
      setEditingPlatformId("");
      setPlatformFormOpen(false);
    }
  }

  async function saveAccount(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = formValues(form);
    const accountId = editingAccount?.id || uid("acc");
    const payload = {
      id: accountId,
      platform_id: data.platformId,
      name: data.name,
      business_line_ids: values(data.businessLineIds),
      persona: data.persona,
      content_style: data.style,
      target_customer: data.targetCustomer,
      common_cta: data.cta,
      publish_frequency: data.frequency,
      bind_mode: data.bindMode || "manual",
      bind_status: data.bindStatus || "unbound",
      auth_subject: data.authSubject,
      credential_ref: data.credentialRef,
      token_expires_at: data.tokenExpiresAt,
      enabled: data.enabled !== "off",
    };
    const remote = editingAccount
      ? updateRemote("accounts", accountId, payload)
      : createRemote("accounts", payload);
    void remote.catch(() => undefined);
    mutate(
      (draft) => {
        const row: MediaAccount = {
          id: accountId,
          platformId: data.platformId,
          name: data.name,
          businessLineIds: values(data.businessLineIds),
          persona: data.persona,
          style: data.style,
          targetCustomer: data.targetCustomer,
          cta: data.cta,
          frequency: data.frequency,
          bindMode: bindMode(data.bindMode),
          bindStatus: bindStatus(data.bindStatus),
          authSubject: data.authSubject,
          credentialRef: data.credentialRef,
          tokenExpiresAt: data.tokenExpiresAt,
          enabled: data.enabled !== "off",
          createdAt: editingAccount?.createdAt || now(),
        };
        upsertById(draft.accounts, row);
      },
      editingAccount ? "账号资料已更新" : "账号已新增",
    );
    setEditingAccountId("");
    setAccountFormOpen(false);
    form.reset();
  }

  function deleteAccount(accountId: string) {
    if (!window.confirm("确定删除这个账号吗？")) return;
    void deleteRemote("accounts", accountId).catch(() => undefined);
    mutate((draft) => {
      draft.accounts = draft.accounts.filter((item) => item.id !== accountId);
    }, "账号已删除");
    if (editingAccountId === accountId) {
      setEditingAccountId("");
      setAccountFormOpen(false);
    }
  }

  function openNewAccountForm() {
    setEditingAccountId("");
    setAccountFormOpen(true);
  }

  function openEditAccountForm(accountId: string) {
    setEditingAccountId(accountId);
    setAccountFormOpen(true);
  }

  function closeAccountForm() {
    setEditingAccountId("");
    setAccountFormOpen(false);
  }

  function updateBinding(accountId: string, status: AccountBindStatus) {
    const source = store.accounts.find((item) => item.id === accountId);
    const expiresAt =
      status === "bound"
        ? source?.tokenExpiresAt || nextMonthDate()
        : source?.tokenExpiresAt || "";
    void updateRemote("accounts", accountId, {
      bind_mode: source?.bindMode || "manual",
      bind_status: status,
      token_expires_at: expiresAt,
    }).catch(() => undefined);
    mutate(
      (draft) => {
        const account = draft.accounts.find((item) => item.id === accountId);
        if (!account) return;
        account.bindStatus = status;
        account.bindMode = account.bindMode || "manual";
        account.tokenExpiresAt = expiresAt;
      },
      status === "bound" ? "账号已标记为绑定" : "账号绑定状态已更新",
    );
  }

  function exportMatrix() {
    downloadText(
      `mediapilotx-account-matrix-${new Date().toISOString().slice(0, 10)}.json`,
      JSON.stringify(
        {
          platforms: store.platforms,
          accounts: store.accounts,
          businessLines: store.businessLines,
        },
        null,
        2,
      ),
      "application/json;charset=utf-8",
    );
  }

  if (view === "platforms") {
    return (
      <section className="grid collectionWorkspace">
        {platformFormOpen || editingPlatform ? (
          <Panel title={editingPlatform ? "修改平台" : "新增平台"}>
            <form
              className="form platformForm"
              key={editingPlatform?.id || "new-platform"}
              onSubmit={savePlatform}
            >
              <label>
                骞冲彴鍚嶇О
                <input
                  name="name"
                  required
                  defaultValue={editingPlatform?.name || ""}
                />
              </label>
              <label>
                骞冲彴缂栫爜
                <input
                  name="code"
                  required
                  defaultValue={editingPlatform?.code || ""}
                />
              </label>
              <label>
                骞冲彴 Logo
                <input name="logoFile" type="file" accept="image/*" />
              </label>
              {editingPlatform?.logoUrl ? (
                <img
                  className="logoPreview"
                  src={editingPlatform.logoUrl}
                  alt={editingPlatform.name}
                />
              ) : null}
              <label>
                默认比例
                <input
                  name="ratio"
                  placeholder="3:4"
                  defaultValue={editingPlatform?.ratio || "3:4"}
                />
              </label>
              <label>
                默认页数
                <input
                  name="pages"
                  type="number"
                  min="1"
                  defaultValue={editingPlatform?.pages || 1}
                />
              </label>
              <label>
                发布方式
                <select
                  name="publishMode"
                  defaultValue={editingPlatform?.publishMode || "manual"}
                >
                  <option value="manual">人工发布</option>
                  <option value="draft">草稿箱</option>
                  <option value="api">API 发布</option>
                </select>
              </label>
              <label>
                状态 <select
                  name="enabled"
                  defaultValue={
                    editingPlatform?.enabled === false ? "off" : "on"
                  }
                >
                  <option value="on">启用</option>
                  <option value="off">停用</option>
                </select>
              </label>
              <div className="rowActions">
                <button type="submit">
                  <Plus size={16} />
                  {editingPlatform ? "保存修改" : "新增平台"}
                </button>
                <button
                  type="button"
                  className="inlineButton"
                  onClick={closePlatformForm}
                >
                  取消
                </button>
              </div>
            </form>
          </Panel>
        ) : null}

        <Panel title="平台列表">
          <div className="listToolbar">
            <div className="filterBar single">
              <button
                className="inlineButton"
                onClick={() =>
                  downloadText(
                    `mediapilotx-platforms-${new Date().toISOString().slice(0, 10)}.json`,
                    JSON.stringify(store.platforms, null, 2),
                    "application/json;charset=utf-8",
                  )
                }
              >
                导出平台列表
              </button>
            </div>
            <button
              type="button"
              className="primaryButton"
              onClick={openNewPlatformForm}
            >
              <Plus size={16} />
              新增平台
            </button>
          </div>
          <div className="tableWrap">
            <table className="platformTable">
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>平台</th>
                  <th>编码</th>
                  <th>默认规则</th>
                  <th>发布方式</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {store.platforms.map((platform) => (
                  <tr key={platform.id}>
                    <td className="logoCell">
                      {platform.logoUrl ? (
                        <img
                          className="platformLogo"
                          src={platform.logoUrl}
                          alt={platform.name}
                        />
                      ) : (
                        <span className="logoFallback">
                          {platform.name.slice(0, 1)}
                        </span>
                      )}
                    </td>
                    <td>{platform.name}</td>
                    <td>{platform.code}</td>
                    <td>
                      {platform.ratio} · {platform.pages} 页
                    </td>
                    <td>{platformPublishModeLabel(platform.publishMode)}</td>
                    <td>{platform.enabled ? "启用" : "停用"}</td>
                    <td>
                      <div className="rowActions">
                        <button
                          className="inlineButton"
                          onClick={() => editPlatform(platform.id)}
                        >
                          修改
                        </button>
                        <button
                          className="dangerButton inlineDanger"
                          onClick={() => deletePlatform(platform.id)}
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </section>
    );
  }

  return (
    <section className="grid collectionWorkspace">
      {accountFormOpen || editingAccount ? (
        <Panel title={editingAccount ? "修改账号" : "新增账号"}>
          <form
            className="form accountForm"
            key={editingAccount?.id || "new-account"}
            onSubmit={saveAccount}
          >
            <label>
              平台
              <Select
                name="platformId"
                rows={store.platforms}
                value={editingAccount?.platformId || undefined}
              />
            </label>
            <label>
              账号名称
              <input
                name="name"
                required
                defaultValue={editingAccount?.name || ""}
              />
            </label>
            <label>
              业务线 <MultiSelect
                name="businessLineIds"
                rows={store.businessLines}
                value={(editingAccount?.businessLineIds || []).join(",")}
              />
            </label>
            <label>
              人设
              <textarea
                name="persona"
                rows={3}
                defaultValue={editingAccount?.persona || ""}
              />
            </label>
            <label>
              内容风格
              <input name="style" defaultValue={editingAccount?.style || ""} />
            </label>
            <label>
              目标客户
              <input
                name="targetCustomer"
                defaultValue={editingAccount?.targetCustomer || ""}
              />
            </label>
            <label>
              常用 CTA
              <input name="cta" defaultValue={editingAccount?.cta || ""} />
            </label>
            <label>
              发布频率
              <input
                name="frequency"
                placeholder="每周3篇 / 每日1条"
                defaultValue={editingAccount?.frequency || ""}
              />
            </label>
            <label>
              绑定方式
              <select
                name="bindMode"
                defaultValue={editingAccount?.bindMode || "manual"}
              >
                <option value="manual">手工登记</option>
                <option value="oauth">开放平台授权</option>
                <option value="qr">扫码登录</option>
                <option value="cookie">会话托管</option>
                <option value="api">API Token</option>
              </select>
            </label>
            <label>
              绑定状态
              <select
                name="bindStatus"
                defaultValue={editingAccount?.bindStatus || "unbound"}
              >
                <option value="unbound">未绑定</option>
                <option value="pending">待确认</option>
                <option value="bound">已绑定</option>
                <option value="expired">已过期</option>
              </select>
            </label>
            <label>
              授权主体
              <input
                name="authSubject"
                defaultValue={editingAccount?.authSubject || ""}
                placeholder="OpenID / UnionID / 店铺ID / 主页ID"
              />
            </label>
            <label>
              凭据引用
              <input
                name="credentialRef"
                defaultValue={editingAccount?.credentialRef || ""}
                placeholder="只填托管编号或密钥名称，不填密码"
              />
            </label>
            <label>
              过期时间
              <input
                name="tokenExpiresAt"
                type="date"
                defaultValue={editingAccount?.tokenExpiresAt || ""}
              />
            </label>
            <label>
              状态 <select
                name="enabled"
                defaultValue={editingAccount?.enabled === false ? "off" : "on"}
              >
                <option value="on">启用</option>
                <option value="off">停用</option>
              </select>
            </label>
            <div className="rowActions">
              <button type="submit">
                <Plus size={16} />
                {editingAccount ? "保存修改" : "新增账号"}
              </button>
              <button
                type="button"
                className="inlineButton"
                onClick={closeAccountForm}
              >
                取消
              </button>
            </div>
          </form>
        </Panel>
      ) : null}

      <Panel title="账号列表">
        <div className="listToolbar">
          <div className="filterBar">
            <select
              value={platformFilter}
              onChange={(event) => setPlatformFilter(event.currentTarget.value)}
            >
              <option value="">全部平台</option>
              {store.platforms.map((platform) => (
                <option key={platform.id} value={platform.id}>
                  {platform.name}
                </option>
              ))}
            </select>
            <select
              value={businessFilter}
              onChange={(event) => setBusinessFilter(event.currentTarget.value)}
            >
              <option value="">全部业务线</option>
              {store.businessLines.map((line) => (
                <option key={line.id} value={line.id}>
                  {line.name}
                </option>
              ))}
            </select>
            <button className="inlineButton" onClick={exportMatrix}>
              导出账号矩阵
            </button>
          </div>
          <button
            type="button"
            className="primaryButton"
            onClick={openNewAccountForm}
          >
            <Plus size={16} />
            新增账号
          </button>
        </div>
        <div className="tableWrap">
          <table className="accountTable">
            <thead>
              <tr>
                <th>账号</th>
                <th>平台</th>
                <th>业务线</th>
                <th>频率</th>
                <th>绑定</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map((account) => (
                <tr key={account.id}>
                  <td>{account.name}</td>
                  <td>
                    <PlatformIdentity
                      platform={store.platforms.find(
                        (platform) => platform.id === account.platformId,
                      )}
                    />
                  </td>
                  <td>
                    {account.businessLineIds
                      .map((id) => nameOf(store.businessLines, id))
                      .join("、")}
                  </td>
                  <td>{account.frequency}</td>
                  <td>{bindingLabel(account)}</td>
                    <td>{account.enabled ? "启用" : "停用"}</td>
                  <td>
                    <div className="rowActions">
                      <button
                        className="inlineButton"
                        onClick={() => openEditAccountForm(account.id)}
                      >
                        修改
                      </button>
                      <button
                        className="inlineButton"
                        onClick={() => updateBinding(account.id, "bound")}
                      >
                        标记已绑定
                      </button>
                      <button
                        className="inlineButton"
                        onClick={() => updateBinding(account.id, "expired")}
                      >
                        标记失效
                      </button>
                      <button
                        className="dangerButton inlineDanger"
                        onClick={() => deleteAccount(account.id)}
                      >
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </section>
  );
}

function PlatformIdentity({ platform }: { platform?: Platform }) {
  if (!platform) {
    return (
      <span className="platformIdentity">
        <span className="logoFallback small">?</span>
                        <span>未设置</span>
      </span>
    );
  }
  return (
    <span className="platformIdentity">
      {platform.logoUrl ? (
        <img
          className="platformLogo small"
          src={platform.logoUrl}
          alt={platform.name}
        />
      ) : (
        <span className="logoFallback small">{platform.name.slice(0, 1)}</span>
      )}
      <span>{platform.name}</span>
    </span>
  );
}

function AccountsV2({ store, mutate }: Ctx) {
  const [platformFilter, setPlatformFilter] = React.useState("");
  const [businessFilter, setBusinessFilter] = React.useState("");
  const filteredAccounts = store.accounts.filter(
    (account) =>
      (!platformFilter || account.platformId === platformFilter) &&
      (!businessFilter || account.businessLineIds.includes(businessFilter)),
  );

  function addPlatform(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    const platformId = uid("pf");
    void createRemote("platforms", {
      id: platformId,
      name: data.name,
      code: data.code,
      default_image_ratio: data.ratio || "3:4",
      default_image_pages: Number(data.pages || 1),
      publish_mode: "manual",
      enabled: true,
    }).catch(() => undefined);
    mutate((draft) => {
      draft.platforms.unshift({
        id: platformId,
        name: data.name,
        code: data.code,
        ratio: data.ratio || "3:4",
        pages: Number(data.pages || 1),
        publishMode: "manual",
        enabled: true,
        createdAt: now(),
      });
    }, "平台已保存");
    event.currentTarget.reset();
  }

  function addAccount(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    const accountId = uid("acc");
    void createRemote("accounts", {
      id: accountId,
      platform_id: data.platformId,
      name: data.name,
      business_line_ids: values(data.businessLineIds),
      persona: data.persona,
      content_style: data.style,
      target_customer: data.targetCustomer,
      common_cta: data.cta,
      publish_frequency: data.frequency,
      bind_mode: data.bindMode || "manual",
      bind_status: data.bindStatus || "unbound",
      auth_subject: data.authSubject,
      credential_ref: data.credentialRef,
      token_expires_at: data.tokenExpiresAt,
      enabled: true,
    }).catch(() => undefined);
    mutate((draft) => {
      draft.accounts.unshift({
        id: accountId,
        platformId: data.platformId,
        name: data.name,
        businessLineIds: values(data.businessLineIds),
        persona: data.persona,
        style: data.style,
        targetCustomer: data.targetCustomer,
        cta: data.cta,
        frequency: data.frequency,
        bindMode: bindMode(data.bindMode),
        bindStatus: bindStatus(data.bindStatus),
        authSubject: data.authSubject,
        credentialRef: data.credentialRef,
        tokenExpiresAt: data.tokenExpiresAt,
        enabled: true,
        createdAt: now(),
      });
    }, "账号已保存");
    event.currentTarget.reset();
  }

  function toggleAccount(accountId: string) {
    mutate((draft) => {
      const account = draft.accounts.find((item) => item.id === accountId);
      if (account) account.enabled = !account.enabled;
    }, "账号状态已更新");
  }

  function togglePlatform(platformId: string) {
    mutate((draft) => {
      const platform = draft.platforms.find((item) => item.id === platformId);
      if (platform) platform.enabled = !platform.enabled;
    }, "平台状态已更新");
  }

  function updateBinding(accountId: string, status: AccountBindStatus) {
    const source = store.accounts.find((item) => item.id === accountId);
    const expiresAt =
      status === "bound"
        ? source?.tokenExpiresAt || nextMonthDate()
        : source?.tokenExpiresAt || "";
    void updateRemote("accounts", accountId, {
      bind_mode: source?.bindMode || "manual",
      bind_status: status,
      token_expires_at: expiresAt,
    }).catch(() => undefined);
    mutate(
      (draft) => {
        const account = draft.accounts.find((item) => item.id === accountId);
        if (!account) return;
        account.bindStatus = status;
        account.bindMode = account.bindMode || "manual";
        account.tokenExpiresAt = expiresAt;
      },
      status === "bound" ? "账号已标记为绑定" : "账号绑定状态已更新",
    );
  }

  function exportMatrix() {
    downloadText(
      `mediapilotx-account-matrix-${new Date().toISOString().slice(0, 10)}.json`,
      JSON.stringify(
        {
          platforms: store.platforms,
          accounts: store.accounts,
          businessLines: store.businessLines,
        },
        null,
        2,
      ),
      "application/json;charset=utf-8",
    );
  }

  return (
    <section className="grid two">
      <Panel title="骞冲彴绠＄悊">
        <form className="form" onSubmit={addPlatform}>
          <label>
            骞冲彴鍚嶇О
            <input name="name" required />
          </label>
          <label>
            骞冲彴缂栫爜
            <input name="code" required />
          </label>
          <label>
            默认比例
            <input name="ratio" placeholder="3:4" />
          </label>
          <label>
            默认页数
            <input name="pages" type="number" min="1" defaultValue="1" />
          </label>
          <button type="submit">
            <Plus size={16} />
            保存平台
          </button>
        </form>
        <div className="cards compact">
          {store.platforms.map((platform) => (
            <article className="card" key={platform.id}>
              <strong>{platform.name}</strong>
              <span>
                    {platform.code} · {platform.ratio} · {platform.pages}页 ·{" "}
                    {platform.enabled ? "启用" : "停用"}
              </span>
              <button
                className="inlineButton"
                onClick={() => togglePlatform(platform.id)}
              >
                    {platform.enabled ? "停用" : "启用"}
              </button>
            </article>
          ))}
        </div>
      </Panel>

      <Panel title="账号新增">
        <form className="form" onSubmit={addAccount}>
          <label>
            骞冲彴
            <Select name="platformId" rows={store.platforms} />
          </label>
          <label>
            账号名称
            <input name="name" required />
          </label>
          <label>
            业务线
            <MultiSelect name="businessLineIds" rows={store.businessLines} />
          </label>
          <label>
            浜鸿
            <textarea name="persona" rows={3} />
          </label>
          <label>
            内容风格
            <input name="style" />
          </label>
          <label>
            鐩爣瀹㈡埛
            <input name="targetCustomer" />
          </label>
          <label>
              常用 CTA
            <input name="cta" />
          </label>
          <label>
            发布频率
            <input name="frequency" placeholder="每周3篇 / 每日1条" />
          </label>
          <label>
            绑定方式
            <select name="bindMode" defaultValue="manual">
              <option value="manual">手工登记</option>
              <option value="oauth">开放平台授权</option>
              <option value="qr">扫码登录</option>
              <option value="cookie">会话托管</option>
              <option value="api">API Token</option>
            </select>
          </label>
          <label>
            绑定状态
            <select name="bindStatus" defaultValue="unbound">
              <option value="unbound">未绑定</option>
              <option value="pending">待确认</option>
              <option value="bound">已绑定</option>
              <option value="expired">已过期</option>
            </select>
          </label>
          <label>
            授权主体
            <input
              name="authSubject"
              placeholder="OpenID / UnionID / 店铺ID / 主页ID"
            />
          </label>
          <label>
            凭据引用
            <input
              name="credentialRef"
              placeholder="只填托管编号或密钥名称，不填密码"
            />
          </label>
          <label>
            过期时间
            <input name="tokenExpiresAt" type="date" />
          </label>
          <button type="submit">
            <Plus size={16} />
            保存账号
          </button>
        </form>
      </Panel>

      <Panel title="账号矩阵">
        <div className="filterBar">
          <select
            value={platformFilter}
            onChange={(event) => setPlatformFilter(event.currentTarget.value)}
          >
            <option value="">全部平台</option>
            {store.platforms.map((platform) => (
              <option key={platform.id} value={platform.id}>
                {platform.name}
              </option>
            ))}
          </select>
          <select
            value={businessFilter}
            onChange={(event) => setBusinessFilter(event.currentTarget.value)}
          >
              <option value="">全部业务线</option>
            {store.businessLines.map((line) => (
              <option key={line.id} value={line.id}>
                {line.name}
              </option>
            ))}
          </select>
          <button className="inlineButton" onClick={exportMatrix}>
            导出账号矩阵
          </button>
        </div>
        <div className="tableWrap">
          <table>
            <thead>
              <tr>
                <th>账号</th>
                <th>平台</th>
                  <th>业务线</th>
                <th>频率</th>
                <th>绑定</th>
                  <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map((account) => (
                <tr key={account.id}>
                  <td>{account.name}</td>
                  <td>{nameOf(store.platforms, account.platformId)}</td>
                  <td>
                    {account.businessLineIds
                      .map((id) => nameOf(store.businessLines, id))
                      .join("、")}
                  </td>
                  <td>{account.frequency}</td>
                  <td>{bindingLabel(account)}</td>
                    <td>{account.enabled ? "启用" : "停用"}</td>
                  <td>
                    <button
                      className="inlineButton"
                      onClick={() => toggleAccount(account.id)}
                    >
                          {account.enabled ? "停用" : "启用"}
                    </button>
                    <button
                      className="inlineButton"
                      onClick={() => updateBinding(account.id, "bound")}
                    >
                      标记已绑定
                    </button>
                    <button
                      className="inlineButton"
                      onClick={() => updateBinding(account.id, "expired")}
                    >
                      标记失效
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="账号绑定说明">
        <div className="cards compact">
          <article className="card">
            <strong>手工登记</strong>
            <span>
              只管理账号档案、业务分组和发布限制，适合当前本地运营阶段。
            </span>
          </article>
          <article className="card">
            <strong>开放平台授权</strong>
            <span>
              适合公众号等提供官方 API 的平台，后续可接 OAuth 和自动发布。
            </span>
          </article>
          <article className="card">
            <strong>扫码登录 / 会话托管</strong>
            <span>
              适合暂未开放 API 的平台，后续更适合接桌面端或安全托管服务。
            </span>
          </article>
          <article className="card">
            <strong>API Token</strong>
            <span>
              适合已有开放平台令牌的账号，系统只记录凭据引用，不保存明文密码。
            </span>
          </article>
        </div>
      </Panel>

      <Panel title="覆盖概览">
        <section className="metrics embedded">
          <Metric
            label="平台"
            value={String(store.platforms.length)}
            tone="blue"
          />
          <Metric
            label="账号"
            value={String(store.accounts.length)}
            tone="green"
          />
          <Metric
            label="启用账号"
            value={String(
              store.accounts.filter((account) => account.enabled).length,
            )}
            tone="orange"
          />
          <Metric
            label="业务线覆盖"
            value={`${businessCoverage(store)}%`}
            tone="dark"
          />
        </section>
        <CardList
          rows={store.accounts}
          render={(account) => (
            <>
              <strong>{account.name}</strong>
              <span>
                {nameOf(store.platforms, account.platformId)} 路{" "}
                {account.frequency}
              </span>
              <small>{account.persona}</small>
            </>
          )}
        />
      </Panel>
    </section>
  );
}

function Accounts({ store, mutate }: Ctx) {
  function addPlatform(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    const platformId = uid("pf");
    void createRemote("platforms", {
      id: platformId,
      name: data.name,
      code: data.code,
      default_image_ratio: data.ratio || "3:4",
      default_image_pages: Number(data.pages || 1),
      publish_mode: "manual",
      enabled: true,
    }).catch(() => undefined);
    mutate((draft) => {
      draft.platforms.unshift({
        id: platformId,
        name: data.name,
        code: data.code,
        ratio: data.ratio || "3:4",
        pages: Number(data.pages || 1),
        publishMode: "manual",
        enabled: true,
        createdAt: now(),
      });
    }, "平台已保存");
    event.currentTarget.reset();
  }
  function addAccount(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    const accountId = uid("acc");
    void createRemote("accounts", {
      id: accountId,
      platform_id: data.platformId,
      name: data.name,
      business_line_ids: values(data.businessLineIds),
      persona: data.persona,
      content_style: data.style,
      target_customer: data.targetCustomer,
      common_cta: data.cta,
      publish_frequency: data.frequency,
      enabled: true,
    }).catch(() => undefined);
    mutate((draft) => {
      draft.accounts.unshift({
        id: accountId,
        platformId: data.platformId,
        name: data.name,
        businessLineIds: values(data.businessLineIds),
        persona: data.persona,
        style: data.style,
        targetCustomer: data.targetCustomer,
        cta: data.cta,
        frequency: data.frequency,
        enabled: true,
        createdAt: now(),
      });
    }, "账号已保存");
    event.currentTarget.reset();
  }
  return (
    <section className="grid two">
      <Panel title="骞冲彴绠＄悊">
        <form className="form" onSubmit={addPlatform}>
          <label>
            骞冲彴鍚嶇О
            <input name="name" required />
          </label>
          <label>
            骞冲彴缂栫爜
            <input name="code" required />
          </label>
          <label>
            默认比例
            <input name="ratio" placeholder="3:4" />
          </label>
          <label>
            默认页数
            <input name="pages" type="number" min="1" />
          </label>
          <button type="submit">
            <Plus size={16} />
            保存平台
          </button>
        </form>
        <div className="cards compact">
          {store.platforms.map((platform) => (
            <article className="card" key={platform.id}>
              <strong>{platform.name}</strong>
              <span>
                {platform.code} · {platform.ratio} · {platform.pages}页
              </span>
            </article>
          ))}
        </div>
      </Panel>
      <Panel title="账号管理与人设">
        <form className="form" onSubmit={addAccount}>
          <label>
            骞冲彴
            <Select name="platformId" rows={store.platforms} />
          </label>
          <label>
            账号名称
            <input name="name" required />
          </label>
          <label>
            业务线 <MultiSelect name="businessLineIds" rows={store.businessLines} />
          </label>
          <label>
            浜鸿
            <textarea name="persona" rows={3} />
          </label>
          <label>
            内容风格
            <input name="style" />
          </label>
          <label>
            鐩爣瀹㈡埛
            <input name="targetCustomer" />
          </label>
          <label>
            常用CTA
            <input name="cta" />
          </label>
          <label>
            发布频率
            <input name="frequency" />
          </label>
          <button type="submit">
            <Plus size={16} />
            保存账号
          </button>
        </form>
        <CardList
          rows={store.accounts}
          render={(account) => (
            <>
              <strong>{account.name}</strong>
              <span>
                {nameOf(store.platforms, account.platformId)} 路{" "}
                {account.frequency}
              </span>
              <small>{account.persona}</small>
            </>
          )}
        />
      </Panel>
    </section>
  );
}

function SettingsView({
  store,
  mutate,
  currentUser,
  view,
  ...ctx
}: Ctx & { view: SettingsSubKey }) {
  const [health, setHealth] = React.useState<ApiHealth | null>(null);
  const [healthError, setHealthError] = React.useState("");
  const [userDialogOpen, setUserDialogOpen] = React.useState(false);
  const [editingUserId, setEditingUserId] = React.useState("");
  const [roleDialogOpen, setRoleDialogOpen] = React.useState(false);
  const [editingRoleId, setEditingRoleId] = React.useState("");
  const [permissionRoleId, setPermissionRoleId] = React.useState(
    store.roles[0]?.id || "",
  );
  const editingUser = store.users.find((user) => user.id === editingUserId);
  const editingRole = store.roles.find((role) => role.id === editingRoleId);
  const permissionRole =
    store.roles.find((role) => role.id === permissionRoleId) || store.roles[0];
  const canManageSettings = ctx.can("settings", "manage");

  React.useEffect(() => {
    fetchHealth()
      .then((result) => {
        setHealth(result);
        setHealthError(
          result ? "" : "未配置 VITE_API_BASE_URL，当前使用本地演示数据。",
        );
      })
      .catch((error) =>
        setHealthError(error instanceof Error ? error.message : "API 检查失败"),
      );
  }, []);

  function reset() {
    if (!window.confirm("确认重置本地演示数据？")) return;
    mutate((draft) => {
      Object.assign(draft, seedStore());
    }, "本地演示数据已重置");
  }

  function saveUser(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    if (editingUser) {
      mutate((draft) => {
        const user = draft.users.find((item) => item.id === editingUser.id);
        if (!user) return;
        user.name = data.name;
        user.email = data.email;
        if (data.password) user.password = data.password;
        user.roleId = data.roleId;
        user.enabled = data.enabled === "true";
      }, "用户已更新");
    } else {
      const userId = uid("user");
      void createRemote("users", {
        id: userId,
        tenant_id: "tenant_default",
        role_id: data.roleId,
        name: data.name,
        email: data.email,
        enabled: true,
      }).catch(() => undefined);
      mutate((draft) => {
        draft.users.unshift({
          id: userId,
          name: data.name,
          email: data.email,
          password: data.password,
          roleId: data.roleId,
          enabled: data.enabled !== "false",
          lastLoginAt: "",
          createdAt: now(),
        });
      }, "用户已创建");
    }
    event.currentTarget.reset();
    setEditingUserId("");
    setUserDialogOpen(false);
  }

  function deleteUser(user: User) {
    if (user.id === currentUser.id) {
      window.alert("不能删除当前登录用户。");
      return;
    }
    if (!window.confirm(`确认删除用户「${user.name}」？`)) return;
    mutate((draft) => {
      draft.users = draft.users.filter((item) => item.id !== user.id);
    }, "用户已删除");
  }

  function saveRole(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    if (editingRole) {
      mutate((draft) => {
        const role = draft.roles.find((item) => item.id === editingRole.id);
        if (!role) return;
        role.name = data.name;
        role.code = data.code;
        role.description = data.description;
        role.enabled = data.enabled === "true";
      }, "角色已更新");
    } else {
      const roleId = uid("role");
      mutate((draft) => {
        draft.roles.unshift({
          id: roleId,
          name: data.name,
          code: data.code,
          description: data.description,
          permissions: ["dashboard:view"],
          enabled: data.enabled !== "false",
          createdAt: now(),
        });
      }, "角色已创建");
      setPermissionRoleId(roleId);
    }
    event.currentTarget.reset();
    setEditingRoleId("");
    setRoleDialogOpen(false);
  }

  function deleteRole(role: Role) {
    if (store.users.some((user) => user.roleId === role.id)) {
      window.alert("该角色已被用户使用，不能删除。请先调整用户角色。");
      return;
    }
    if (!window.confirm(`确认删除角色「${role.name}」？`)) return;
    mutate((draft) => {
      draft.roles = draft.roles.filter((item) => item.id !== role.id);
    }, "角色已删除");
    if (permissionRoleId === role.id) setPermissionRoleId(store.roles[0]?.id || "");
  }

  function toggleRolePermission(
    roleId: string,
    moduleKey: NavKey,
    action: PermissionAction,
    checked: boolean,
  ) {
    const permission = `${moduleKey}:${action}`;
    mutate((draft) => {
      const role = draft.roles.find((item) => item.id === roleId);
      if (!role) return;
      const nextPermissions = normalizePermissions(
        role.permissions.filter((item) => item !== "*"),
      );
      role.permissions = checked
        ? [...new Set([...nextPermissions, permission])]
        : nextPermissions.filter((item) => item !== permission);
    }, "角色权限已更新");
  }

  function saveAttachmentSettings(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = formValues(event.currentTarget);
    mutate((draft) => {
      draft.appSettings = {
        attachmentRoot: data.attachmentRoot || draft.appSettings.attachmentRoot,
        knowledgeRoot: data.knowledgeRoot || draft.appSettings.knowledgeRoot,
        cloudSyncPrefix:
          data.cloudSyncPrefix || draft.appSettings.cloudSyncPrefix,
        archiveMode: (data.archiveMode ||
          "local") as AppSettings["archiveMode"],
      };
    }, "附件存放设置已保存");
  }

  function exportSyncManifest() {
    const rows = store.assets.map((asset) => ({
      id: asset.id,
      name: asset.name,
      type: asset.type,
      sourcePath: asset.path,
      localTarget: asset.archiveDir
        ? attachmentPath(store.appSettings, asset.archiveDir, asset.fileName)
        : "",
      cloudTarget: asset.archiveDir
        ? cloudAttachmentPath(
            store.appSettings,
            asset.archiveDir,
            asset.fileName,
          )
        : "",
      contentHash: asset.contentHash,
      status: asset.status,
    }));
    downloadText(
      `mediapilotx-attachment-sync-${new Date().toISOString().slice(0, 10)}.json`,
      JSON.stringify(rows, null, 2),
      "application/json;charset=utf-8",
    );
  }

  return (
    <section className={`grid settingsView settings-${view}`}>
      <Panel title="系统状态">
        <div className="cards">
          <article className="card">
            <strong>当前用户</strong>
            <span>
              {currentUser.name} · {nameOf(store.roles, currentUser.roleId)}
            </span>
            <small>{currentUser.email}</small>
          </article>
          <article className="card">
            <strong>本地数据</strong>
            <span>localStorage 持久化，可离线演示 MVP 闭环。</span>
          </article>
          <article className="card">
            <strong>Cloudflare API</strong>
            <span>{apiBaseUrl || "未配置"}</span>
            <small>{health ? `在线：${health.at}` : healthError}</small>
          </article>
          <article className="card">
            <strong>异步任务</strong>
            <span>发布包导出、批量生成等任务已进入队列。</span>
          </article>
        </div>
        <button className="dangerButton" onClick={reset}>
          重置演示数据
        </button>
      </Panel>
      <Panel title="附件存放与同步">
        <form className="form" onSubmit={saveAttachmentSettings}>
          <label>
            本地附件根目录 <input
              name="attachmentRoot"
              defaultValue={store.appSettings.attachmentRoot}
              placeholder="D:\MediaPilotX\attachments"
            />
          </label>
          <label>
            本地知识库根目录
            <input
              name="knowledgeRoot"
              defaultValue={store.appSettings.knowledgeRoot}
              placeholder="D:\MediaPilotX\knowledge"
            />
          </label>
          <label>
            云端同步前缀
            <input
              name="cloudSyncPrefix"
              defaultValue={store.appSettings.cloudSyncPrefix}
              placeholder="r2://mediapilotx-media/attachments"
            />
          </label>
          <label>
            归档模式
            <select
              name="archiveMode"
              defaultValue={store.appSettings.archiveMode}
            >
              <option value="local">仅本地整理</option>
              <option value="cloud">云端优先</option>
              <option value="hybrid">本地+云端同步</option>
            </select>
          </label>
          <button type="submit">
            <Plus size={16} />
            保存附件设置
          </button>
        </form>
        <div className="list spaced">
          <article className="card">
            <strong>当前本地目录</strong>
            <span>{store.appSettings.attachmentRoot}</span>
            <small>素材会按业务线/平台/日期生成相对归档路径。</small>
          </article>
          <article className="card">
            <strong>云端同步目标</strong>
            <span>{store.appSettings.cloudSyncPrefix}</span>
            <small>未来上线时，可按同步清单把本地附件推到 R2 或服务器。</small>
          </article>
          <button className="inlineButton" onClick={exportSyncManifest}>
            导出附件同步清单
          </button>
        </div>
      </Panel>
      <Panel title="用户与角色">
        <div className="sectionHeader">
          <div>
            <strong>系统用户管理</strong>
            <span>维护本地系统登录用户与角色归属。</span>
          </div>
          {canManageSettings ? (
            <button
              onClick={() => {
                setEditingUserId("");
                setUserDialogOpen(true);
              }}
            >
              <Plus size={16} />
              新增用户
            </button>
          ) : null}
        </div>
        <div className="tableWrap">
          <table>
            <thead>
              <tr>
                <th>姓名</th>
                <th>邮箱</th>
                <th>角色</th>
                <th>状态</th>
                <th>最近登录</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {store.users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.name}</strong>
                  </td>
                  <td>{user.email}</td>
                  <td>{nameOf(store.roles, user.roleId)}</td>
                  <td>{user.enabled ? "启用" : "停用"}</td>
                  <td>{user.lastLoginAt ? formatDate(user.lastLoginAt) : "未登录"}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    {canManageSettings ? (
                    <div className="rowActions">
                      <button
                        className="inlineButton"
                        onClick={() => {
                          setEditingUserId(user.id);
                          setUserDialogOpen(true);
                        }}
                      >
                        修改
                      </button>
                      <button
                        className="dangerButton inlineDanger"
                        onClick={() => deleteUser(user)}
                      >
                        删除
                      </button>
                    </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="sectionHeader roleSectionHeader">
          <div>
            <strong>角色管理</strong>
            <span>角色决定用户能看到哪些菜单、能执行哪些操作。</span>
          </div>
          {canManageSettings ? (
            <button
              onClick={() => {
                setEditingRoleId("");
                setRoleDialogOpen(true);
              }}
            >
              <Plus size={16} />
              新增角色
            </button>
          ) : null}
        </div>
        <div className="list spaced roleList">
          {store.roles.map((role) => (
            <article className="card" key={role.id}>
              <strong>{role.name}</strong>
              <span>
                {role.code} · {role.description}
              </span>
              <small>权限：{role.permissions.join("、")}</small>
              {canManageSettings ? (
              <div className="rowActions">
                <button
                  className="inlineButton"
                  onClick={() => {
                    setEditingRoleId(role.id);
                    setRoleDialogOpen(true);
                  }}
                >
                  修改
                </button>
                <button
                  className="inlineButton"
                  onClick={() => setPermissionRoleId(role.id)}
                >
                  配置权限
                </button>
                <button
                  className="dangerButton inlineDanger"
                  onClick={() => deleteRole(role)}
                >
                  删除
                </button>
              </div>
              ) : null}
            </article>
          ))}
        </div>
        {permissionRole ? (
          <div className="permissionMatrix">
            <div className="sectionHeader">
              <div>
                <strong>权限配置</strong>
                <span>当前配置角色：{permissionRole.name}</span>
              </div>
              <select
                value={permissionRole.id}
                onChange={(event) => setPermissionRoleId(event.target.value)}
              >
                {store.roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="tableWrap">
              <table>
                <thead>
                  <tr>
                    <th>模块</th>
                    {permissionActions.map((action) => (
                      <th key={action.key}>{action.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {permissionModules.map((module) => (
                    <tr key={module.key}>
                      <td>{module.label}</td>
                      {permissionActions.map((action) => {
                        const supported = module.actions.includes(action.key);
                        const checked =
                          permissionRole.permissions.includes("*") ||
                          permissionRole.permissions.includes(`${module.key}:*`) ||
                          permissionRole.permissions.includes(`${module.key}:${action.key}`);
                        return (
                          <td key={action.key}>
                            {supported ? (
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={
                                  !canManageSettings ||
                                  permissionRole.permissions.includes("*")
                                }
                                onChange={(event) =>
                                  toggleRolePermission(
                                    permissionRole.id,
                                    module.key,
                                    action.key,
                                    event.target.checked,
                                  )
                                }
                              />
                            ) : (
                              <span className="muted">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        {userDialogOpen ? (
          <div className="modalOverlay">
            <form className="modalPanel modelModal" onSubmit={saveUser}>
              <div className="modalHeader">
                <div>
                  <strong>{editingUser ? "修改用户" : "新增用户"}</strong>
                  <span>用户创建后可在登录页选择进入系统。</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setEditingUserId("");
                    setUserDialogOpen(false);
                  }}
                >
                  关闭
                </button>
              </div>
              <div className="modelForm">
                <label>
                  姓名
                  <input
                    name="name"
                    required
                    placeholder="例如：运营A"
                    defaultValue={editingUser?.name || ""}
                  />
                </label>
                <label>
                  邮箱
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="operator@example.com"
                    defaultValue={editingUser?.email || ""}
                  />
                </label>
                <label>
                  角色
                  <select name="roleId" defaultValue={editingUser?.roleId || store.roles[0]?.id}>
                    {store.roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  密码
                  <input
                    name="password"
                    type="password"
                    required={!editingUser}
                    placeholder={editingUser ? "留空表示不修改密码" : "请输入登录密码"}
                  />
                </label>
                <label>
                  状态
                  <select name="enabled" defaultValue={editingUser?.enabled === false ? "false" : "true"}>
                    <option value="true">启用</option>
                    <option value="false">停用</option>
                  </select>
                </label>
              </div>
              <div className="modalActions">
                <button
                  type="button"
                  onClick={() => {
                    setEditingUserId("");
                    setUserDialogOpen(false);
                  }}
                >
                  取消
                </button>
                <button type="submit">
                  <Plus size={16} />
                  {editingUser ? "保存用户" : "创建用户"}
                </button>
              </div>
            </form>
          </div>
        ) : null}
        {roleDialogOpen ? (
          <div className="modalOverlay">
            <form className="modalPanel modelModal" onSubmit={saveRole}>
              <div className="modalHeader">
                <div>
                  <strong>{editingRole ? "修改角色" : "新增角色"}</strong>
                  <span>角色编码建议使用英文小写和下划线。</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setEditingRoleId("");
                    setRoleDialogOpen(false);
                  }}
                >
                  关闭
                </button>
              </div>
              <div className="modelForm">
                <label>
                  角色名称
                  <input name="name" required defaultValue={editingRole?.name || ""} />
                </label>
                <label>
                  角色编码
                  <input name="code" required defaultValue={editingRole?.code || ""} />
                </label>
                <label>
                  状态
                  <select name="enabled" defaultValue={editingRole?.enabled === false ? "false" : "true"}>
                    <option value="true">启用</option>
                    <option value="false">停用</option>
                  </select>
                </label>
                <label>
                  说明
                  <textarea
                    name="description"
                    rows={3}
                    defaultValue={editingRole?.description || ""}
                  />
                </label>
              </div>
              <div className="modalActions">
                <button
                  type="button"
                  onClick={() => {
                    setEditingRoleId("");
                    setRoleDialogOpen(false);
                  }}
                >
                  取消
                </button>
                <button type="submit">
                  <Plus size={16} />
                  {editingRole ? "保存角色" : "创建角色"}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </Panel>
      <Panel title="数据统计">
        <div className="metrics embedded">
          <Metric
            label="业务线"
            value={String(store.businessLines.length)}
            tone="green"
          />
          <Metric
            label="账号"
            value={String(store.accounts.length)}
            tone="orange"
          />
          <Metric label="任务" value={String(store.tasks.length)} tone="blue" />
          <Metric
            label="发布计划"
            value={String(store.publishPlans.length)}
            tone="dark"
          />
        </div>
      </Panel>
      <Panel title="AI配置">
        <AIConfigV2
          store={store}
          mutate={mutate}
          currentUser={currentUser}
          mode="prompts"
          {...ctx}
        />
      </Panel>
      <Panel title="AI模型列表">
        <AIConfigV2
          store={store}
          mutate={mutate}
          currentUser={currentUser}
          mode="models"
          {...ctx}
        />
      </Panel>
      <Panel title="AI提示词">
        <AiRewritePromptsPanel store={store} mutate={mutate} />
      </Panel>
    </section>
  );
}

function AiRewritePromptsPanel({
  store,
  mutate,
}: {
  store: Store;
  mutate: Ctx["mutate"];
}) {
  const [formOpen, setFormOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState("");
  const editingPrompt = store.aiRewritePrompts.find(
    (prompt) => prompt.id === editingId,
  );

  function openNew() {
    setEditingId("");
    setFormOpen(true);
  }

  function closeForm() {
    setEditingId("");
    setFormOpen(false);
  }

  function savePrompt(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = formValues(form);
    const promptId = editingPrompt?.id || uid("rewrite_prompt");
    mutate(
      (draft) => {
        upsertById(draft.aiRewritePrompts, {
          id: promptId,
          sequence: Number(data.sequence || editingPrompt?.sequence || 1),
          category: data.category,
          name: data.name,
          prompt: data.prompt,
          description: data.description,
          enabled: data.enabled !== "off",
          createdAt: editingPrompt?.createdAt || now(),
        });
        draft.aiRewritePrompts.sort((a, b) => a.sequence - b.sequence);
      },
      editingPrompt ? "AI提示词已更新" : "AI提示词已新增",
    );
    closeForm();
    form.reset();
  }

  function deletePrompt(prompt: AiRewritePrompt) {
    if (!window.confirm(`确定删除提示词「${prompt.name}」吗？`)) return;
    mutate((draft) => {
      draft.aiRewritePrompts = draft.aiRewritePrompts.filter(
        (item) => item.id !== prompt.id,
      );
    }, "AI提示词已删除");
  }

  function togglePrompt(promptId: string) {
    mutate((draft) => {
      const prompt = draft.aiRewritePrompts.find((item) => item.id === promptId);
      if (prompt) prompt.enabled = !prompt.enabled;
    }, "AI提示词状态已更新");
  }

  return (
    <div className="stackPanel">
      <div className="listToolbar">
        <div>
          <strong>AI提示词列表</strong>
          <p className="muted">
            用于采集内容洗稿、知识库改写和公众号文章生成。
          </p>
        </div>
        <button type="button" className="primaryButton" onClick={openNew}>
          <Plus size={16} />
          新增提示词
        </button>
      </div>
      {formOpen || editingPrompt ? (
        <div className="modalOverlay">
          <form className="modalPanel modelModal" onSubmit={savePrompt}>
            <div className="modalHeader">
              <div>
                <strong>{editingPrompt ? "修改提示词" : "新增提示词"}</strong>
                <span>维护分类、提示词正文和说明。</span>
              </div>
              <button type="button" className="ghost" onClick={closeForm}>
                关闭
              </button>
            </div>
            <div className="form modelForm">
              <label>
                序号
                <input
                  name="sequence"
                  type="number"
                  min="1"
                  required
                  defaultValue={
                    editingPrompt?.sequence ||
                    store.aiRewritePrompts.length + 1
                  }
                />
              </label>
              <label>
                分类
                <input
                  name="category"
                  required
                  placeholder="新闻洗稿 / 知识库改写 / 公众号长文"
                  defaultValue={editingPrompt?.category || ""}
                />
              </label>
              <label>
                提示词名称
                <input
                  name="name"
                  required
                  placeholder="公众号新闻深度改写"
                  defaultValue={editingPrompt?.name || ""}
                />
              </label>
              <label>
                状态
                <select
                  name="enabled"
                  defaultValue={editingPrompt?.enabled === false ? "off" : "on"}
                >
                  <option value="on">启用</option>
                  <option value="off">停用</option>
                </select>
              </label>
              <label className="span2">
                提示词
                <textarea
                  name="prompt"
                  rows={8}
                  required
                  defaultValue={editingPrompt?.prompt || ""}
                />
              </label>
              <label className="span2">
                说明
                <textarea
                  name="description"
                  rows={3}
                  defaultValue={editingPrompt?.description || ""}
                />
              </label>
            </div>
            <div className="modalActions">
              <button type="button" className="ghost" onClick={closeForm}>
                取消
              </button>
              <button type="submit" className="primaryButton">
                保存提示词
              </button>
            </div>
          </form>
        </div>
      ) : null}
      <div className="tableWrap">
        <table>
          <thead>
            <tr>
              <th>序号</th>
              <th>分类</th>
              <th>提示词</th>
              <th>说明</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {store.aiRewritePrompts.map((prompt) => (
              <tr key={prompt.id}>
                <td>{prompt.sequence}</td>
                <td>{prompt.category}</td>
                <td>
                  <div className="stackedCell">
                    <strong>{prompt.name}</strong>
                    <span>{prompt.prompt.slice(0, 120)}</span>
                  </div>
                </td>
                <td>{prompt.description || "暂无说明"}</td>
                <td>{prompt.enabled ? "启用" : "停用"}</td>
                <td>
                  <div className="rowActions">
                    <button
                      className="inlineButton"
                      onClick={() => {
                        setEditingId(prompt.id);
                        setFormOpen(true);
                      }}
                    >
                      修改
                    </button>
                    <button
                      className="inlineButton"
                      onClick={() => togglePrompt(prompt.id)}
                    >
                      {prompt.enabled ? "停用" : "启用"}
                    </button>
                    <button
                      className="dangerButton inlineDanger"
                      onClick={() => deletePrompt(prompt)}
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!store.aiRewritePrompts.length ? (
        <p className="muted">暂无提示词，可以点击右上角新增。</p>
      ) : null}
    </div>
  );
}

function DashboardGuide() {
  return (
    <section className="grid">
      <Panel title="系统使用文档">
        <SystemUsageGuide />
      </Panel>
    </section>
  );
}

function SystemUsageGuide() {
  const chain = [
    "采集内容",
    "选题池",
    "内容任务",
    "母版文章",
    "平台版本",
    "账号版本",
    "图文/视频项目",
    "发布排期",
  ];
  return (
    <div className="usageGuide">
      <section>
        <h3>一、内容生产链路</h3>
        <p>
          系统里的主要功能是一条完整的内容生产链路，不是彼此孤立的页面。简单理解是：先收集资料，再形成选题，然后进入任务、写作、改写、制作和发布安排。
        </p>
        <div className="productionChain">
          {chain.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </section>

      <section>
        <h3>二、模块之间如何转换</h3>
        <div className="guideGrid transitionGrid">
          <article>
            <strong>采集内容 → 选题</strong>
            <span>采集内容是资料库，可以是历史公众号文章、网页、RSS 新闻或手工录入内容。转选题时不是照搬全文，而是提炼出值得重新创作的主题。</span>
          </article>
          <article>
            <strong>选题 → 内容任务</strong>
            <span>选题回答“写什么”，内容任务回答“怎么生产”。任务里会明确业务线、内容类型、目标平台、目标账号、创作方向和截止时间。</span>
          </article>
          <article>
            <strong>内容任务 → 母版文章</strong>
            <span>母版文章是一篇完整的基础稿，先保证逻辑完整、内容准确、结构清晰，再用于后续平台改写。</span>
          </article>
          <article>
            <strong>母版文章 → 平台版本</strong>
            <span>不同平台表达方式不同。公众号偏长文，小红书偏图文卡片，视频号偏脚本和短句，所以需要把母版文章改写成平台版本。</span>
          </article>
          <article>
            <strong>平台版本 → 账号版本</strong>
            <span>同一平台下多个账号不能发布高度重复内容，所以平台版本还要按账号人设、客户群、CTA、案例和标题继续差异化。</span>
          </article>
          <article>
            <strong>账号版本 → 图文/视频项目</strong>
            <span>账号版本是文案，图文/视频项目是制作方案。图文项目包含标题、正文、图片、分页脚本和图片提示词；视频项目包含脚本、分镜、字幕和混剪说明。</span>
          </article>
          <article>
            <strong>图文/视频项目 → 发布排期</strong>
            <span>制作完成后，把最终内容安排到具体账号和发布时间，并导出发布包，方便人工发布和后续数据回填。</span>
          </article>
          <article>
            <strong>一句话总结</strong>
            <span>采集内容是资料，选题是主题，任务是生产安排，母版是基础稿，平台版本适配平台，账号版本做差异化，项目负责制作，排期负责发布。</span>
          </article>
        </div>
      </section>

      <section>
        <h3>三、推荐使用流程</h3>
        <ol>
          <li><strong>配置账号矩阵：</strong>先维护平台、账号、业务线、账号人设和目标客户。</li>
          <li><strong>导入或采集内容：</strong>从采集中心导入历史 MD/TXT/PDF，或配置采集源、采集规则和采集任务。</li>
          <li><strong>整理选题：</strong>从资料中提炼选题，也可以手工新增选题。</li>
          <li><strong>创建内容任务：</strong>把选题变成具体生产任务，明确平台、账号、方向和截止时间。</li>
          <li><strong>生成母版文章：</strong>从任务生成基础稿，必要时手工编辑。</li>
          <li><strong>生成平台与账号版本：</strong>先按平台改写，再按账号差异化。</li>
          <li><strong>生成图文或视频项目：</strong>把账号版本转换成可制作项目，补充图片、脚本、分镜或混剪说明。</li>
          <li><strong>进入发布排期：</strong>安排发布时间，导出发布包，人工发布后回填链接和数据。</li>
        </ol>
      </section>

      <section>
        <h3>四、图文子项目说明</h3>
        <ul>
          <li>一个父图文项目可以生成多个子图文项目，用于批量变体。</li>
          <li>每个子项目都会有独立标题、正文、图片列表、图片 hash 和归档路径。</li>
          <li>如果子项目数量大于 0，图文项目列表里的数字可以点击查看子项目明细。</li>
          <li>第一版使用浏览器 Canvas 处理图片；如果图片无法读取，会保留原图片路径并标记“部分图片未处理”。</li>
        </ul>
      </section>

      <section>
        <h3>五、本地附件与同步</h3>
        <ul>
          <li>当前系统以本地优先为主，素材记录电脑上的原文件路径。</li>
          <li>附件根目录在“系统设置 / 附件存放与同步”中配置。</li>
          <li>未来上线时，可以按同步清单把本地附件同步到 Cloudflare R2 或服务器。</li>
          <li>GitHub 同步不是实时同步，由你手工确认后再提交和推送。</li>
        </ul>
      </section>

      <section>
        <h3>六、当前版本注意事项</h3>
        <ul>
          <li>当前版本暂不做自动发布，发布包导出后仍需人工登录平台发布。</li>
          <li>用户与角色当前主要用于本地演示和权限规划，复杂多人协作后续再扩展。</li>
          <li>正式使用前，建议先确认平台、账号、AI 模型、附件根目录是否配置正确。</li>
        </ul>
      </section>
    </div>
  );
}
function TaskTable({
  store,
  actions,
  onEdit,
  onDelete,
}: {
  store: Store;
  actions?: boolean;
  onEdit?: (task: ContentTask) => void;
  onDelete?: (task: ContentTask) => void;
}) {
  function openTaskDetailTab(task: ContentTask) {
    openHtmlTab(`${task.title} - 内容任务`, buildTaskReaderHtml(store, task));
  }

  return (
    <div className="tableWrap">
      <table>
        <thead>
          <tr>
            <th>任务</th>
                  <th>业务线</th>
                  <th>状态</th>
            <th>鐩爣骞冲彴</th>
            <th>目标账号</th>
            <th>鎴</th>
            {actions ? <th>操作</th> : null}
          </tr>
        </thead>
        <tbody>
          {store.tasks.map((task) => (
            <tr key={task.id}>
              <td>
                <button
                  className="linkButton"
                  onClick={() => openTaskDetailTab(task)}
                >
                  {task.title}
                </button>
              </td>
              <td>{nameOf(store.businessLines, task.businessLineId)}</td>
              <td>
                <span className="pill">{task.status}</span>
              </td>
              <td>
                {task.targetPlatformIds
                  .map((id) => nameOf(store.platforms, id))
                  .join("、") || "未设置"}
              </td>
              <td>
                {task.targetAccountIds
                  .map((id) => nameOf(store.accounts, id))
                  .join("、") || "未设置"}
              </td>
              <td>{task.dueDate || "未设置"}</td>
              {actions ? (
                <td>
                  <div className="rowActions">
                    <button
                      className="inlineButton"
                      onClick={() => onEdit?.(task)}
                    >
                      修改
                    </button>
                    <button
                      className="dangerButton inlineDanger"
                      onClick={() => onDelete?.(task)}
                    >
                      删除
                    </button>
                  </div>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Panel({
  title,
  children,
}: React.PropsWithChildren<{ title: string }>) {
  return (
    <section className="panel">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function Step({ n, title, text }: { n: string; title: string; text: string }) {
  return (
    <div className="step">
      <b>{n}</b>
      <strong>{title}</strong>
      <span>{text}</span>
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: string;
}) {
  return (
    <article className={`metric ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function Select({
  name,
  rows,
  value,
}: {
  name: string;
  rows: Array<{ id: string; name: string }>;
  value?: string;
}) {
  return (
    <select name={name} defaultValue={value}>
      {rows.map((row) => (
        <option key={row.id} value={row.id}>
          {row.name}
        </option>
      ))}
    </select>
  );
}

function MultiSelect({
  name,
  rows,
  value = "",
}: {
  name: string;
  rows: Array<{ id: string; name: string }>;
  value?: string;
}) {
  return (
    <select
      name={name}
      multiple
      defaultValue={values(value)}
      size={Math.min(5, Math.max(3, rows.length))}
    >
      {rows.map((row) => (
        <option key={row.id} value={row.id}>
          {row.name}
        </option>
      ))}
    </select>
  );
}

function CardList<T extends { id: string }>({
  rows,
  render,
}: {
  rows: T[];
  render: (row: T) => React.ReactNode;
}) {
  return (
    <div className="list">
      {rows.length ? (
        rows.map((row) => (
          <article className="card" key={row.id}>
            {render(row)}
          </article>
        ))
      ) : (
        <p className="muted">暂无数据。</p>
      )}
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  onPage,
}: {
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="pagination">
      <button
        className="inlineButton"
        disabled={page <= 1}
        onClick={() => onPage(Math.max(1, page - 1))}
      >
        上一页
      </button>
      <span>
        第 {page} / {totalPages} 页
      </span>
      <button
        className="inlineButton"
        disabled={page >= totalPages}
        onClick={() => onPage(Math.min(totalPages, page + 1))}
      >
        下一页
      </button>
    </div>
  );
}

function ProgressRow({ label, percent }: { label: string; percent: number }) {
  return (
    <div className="completionRow">
      <span>{label}</span>
      <div className="bar">
        <i style={{ width: `${percent}%` }} />
      </div>
      <b>{percent}%</b>
    </div>
  );
}

function formValues(form: HTMLFormElement) {
  const data = new FormData(form);
  const result: Record<string, string> = {};
  for (const [key, value] of data.entries()) {
    if (result[key]) result[key] += `,${value}`;
    else result[key] = String(value);
  }
  return result;
}

function values(input = "") {
  return input
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function paginateRows<T>(rows: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return { rows: rows.slice(start, start + pageSize), totalPages };
}

async function fileSha256(file: File) {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

function localAssetDuplicates(
  store: Store,
  contentHash: string,
  platformId: string,
): DuplicateMatch[] {
  if (!contentHash) return [];
  return store.assets
    .filter(
      (asset) =>
        asset.contentHash === contentHash &&
        (!platformId || asset.platformId === platformId),
    )
    .slice(0, 8)
    .map((asset) => ({
      id: asset.id,
      title: asset.name,
      platformId: asset.platformId,
      score: 1,
      createdAt: asset.createdAt,
    }));
}

function localTextDuplicates(
  store: Store,
  textValue: string,
  platformId = "",
): DuplicateMatch[] {
  const candidates = [
    ...store.collectedItems.map((item) => ({
      id: item.id,
      title: item.title,
      body: item.rawContent,
      platformId: "",
      createdAt: item.createdAt,
    })),
    ...store.accountVersions.map((version) => {
      const account = store.accounts.find(
        (item) => item.id === version.accountId,
      );
      return {
        id: version.id,
        title: version.title,
        body: version.body,
        platformId: account?.platformId || "",
        createdAt: version.createdAt,
      };
    }),
  ];
  return candidates
    .filter(
      (item) =>
        !platformId || !item.platformId || item.platformId === platformId,
    )
    .map((item) => ({
      id: item.id,
      title: item.title,
      platformId: item.platformId,
      createdAt: item.createdAt,
      score: textSimilarity(item.body, textValue),
    }))
    .filter((item) => item.score >= 0.78)
    .sort((left, right) => right.score - left.score)
    .slice(0, 8);
}

type ParsedHistoryFile = {
  title: string;
  body: string;
  keywords: string;
  assetType: "markdown" | "text" | "pdf";
  mimeType: string;
  extension: "md" | "txt" | "pdf";
  warning?: string;
};

async function parseHistoryFile(file: File): Promise<ParsedHistoryFile> {
  const extension = historyFileExtension(file.name);
  if (extension === "pdf") {
    const extracted = await extractPdfTextBasic(file);
    const title = stripFileExtension(file.name);
    const warning = extracted.warning;
    const body =
      extracted.text ||
      `PDF 文件已入库：${file.name}\n\n当前浏览器端无法稳定读取该 PDF 的文本层。建议先将 PDF 转为 TXT/MD 后导入，或后续接入 OCR/PDF 解析服务。`;
    return {
      title,
      body: cleanMarkdownContent(body),
      keywords: extractKeywords(body),
      assetType: "pdf",
      mimeType: file.type || "application/pdf",
      extension: "pdf",
      warning,
    };
  }

  const raw = await file.text();
  if (extension === "txt") {
    const body = cleanMarkdownContent(raw);
    const firstLine = body.split(/\n+/).find(Boolean);
    return {
      title: firstLine?.slice(0, 80) || stripFileExtension(file.name),
      body,
      keywords: extractKeywords(body),
      assetType: "text",
      mimeType: file.type || "text/plain",
      extension: "txt",
    };
  }

  const parsed = parseMarkdownDocument(raw, file.name);
  return {
    ...parsed,
    assetType: "markdown",
    mimeType: file.type || "text/markdown",
    extension: "md",
  };
}

function isSupportedHistoryFile(file: File) {
  return /\.(md|txt|pdf)$/i.test(file.name);
}

function historyFileExtension(filename: string): "md" | "txt" | "pdf" {
  const match = filename.toLowerCase().match(/\.([a-z0-9]+)$/);
  const extension = match?.[1];
  if (extension === "pdf") return "pdf";
  if (extension === "txt") return "txt";
  return "md";
}

function historyFileAssetLabel(assetType: ParsedHistoryFile["assetType"]) {
  const labels: Record<ParsedHistoryFile["assetType"], string> = {
    markdown: "Markdown",
    text: "Text",
    pdf: "PDF",
  };
  return labels[assetType];
}

function stripFileExtension(filename: string) {
  return filename.replace(/\.[^.]+$/i, "");
}

async function extractPdfTextBasic(file: File) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  try {
    const pdf = await pdfjsLib.getDocument({ data: bytes.slice() }).promise;
    const pages: string[] = [];
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item) => ("str" in item ? item.str : ""))
        .join(" ");
      if (pageText.trim()) pages.push(pageText);
    }
    const text = cleanMarkdownContent(pages.join("\n\n"));
    if (text.length >= 20) {
      return {
        text,
        warning: undefined,
      };
    }
  } catch (error) {
    console.warn("pdf.js text extraction failed", error);
  }

  const raw = new TextDecoder("latin1").decode(bytes);
  const chunks: string[] = [];
  const literalMatches = raw.matchAll(/\((?:\\.|[^\\)]){2,}\)\s*Tj/g);
  for (const match of literalMatches) {
    chunks.push(decodePdfLiteral(match[0].replace(/\)\s*Tj$/, "").slice(1)));
  }
  const arrayMatches = raw.matchAll(
    /\[((?:\s*\((?:\\.|[^\\)]){1,}\)\s*)+)\]\s*TJ/g,
  );
  for (const match of arrayMatches) {
    const values = match[1].matchAll(/\((?:\\.|[^\\)]){1,}\)/g);
    chunks.push(
      Array.from(values)
        .map((value) => decodePdfLiteral(value[0].slice(1, -1)))
        .join(""),
    );
  }
  const text = cleanMarkdownContent(chunks.join("\n"));
  return {
    text: text.length >= 80 ? text : "",
    warning:
      text.length >= 80
        ? undefined
        : "PDF 未提取到足够文本，可能是扫描件或压缩文本层。",
  };
}

function decodePdfLiteral(value: string) {
  return value
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n")
    .replace(/\\t/g, " ")
    .replace(/\\\(/g, "(")
    .replace(/\\\)/g, ")")
    .replace(/\\\\/g, "\\")
    .replace(/\\([0-7]{1,3})/g, (_match, octal: string) =>
      String.fromCharCode(Number.parseInt(octal, 8)),
    );
}

function parseMarkdownDocument(raw: string, filename: string) {
  const withoutBom = raw.replace(/^\uFEFF/, "");
  const frontmatter = withoutBom.match(/^---\s*\n([\s\S]*?)\n---\s*\n?/);
  const meta = frontmatter ? parseFrontmatter(frontmatter[1]) : {};
  const body = cleanMarkdownContent(
    frontmatter ? withoutBom.slice(frontmatter[0].length) : withoutBom,
  );
  const heading = body.match(/^#\s+(.+)$/m)?.[1]?.trim();
  const title = meta.title || heading || stripFileExtension(filename);
  const keywords = meta.tags || meta.keywords || extractKeywords(body);
  return { title, body, keywords };
}

function cleanMarkdownContent(raw = "") {
  let value = raw
    .replace(/^\uFEFF/, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "\n")
    .replace(/<script[\s\S]*?<\/script>/gi, "\n")
    .replace(
      /<\/?(section|div|span|p|strong|em|br|img|a|svg|path|meta)[^>]*>/gi,
      "\n",
    )
    .replace(/!\[[^\]]*]\([^)]*\)/g, "\n")
    .replace(/\\_/g, "_")
    .replace(/\\\*/g, "*")
    .replace(/\\\[/g, "[")
    .replace(/\\\]/g, "]")
    .replace(/\\\{/g, "{")
    .replace(/\\\}/g, "}")
    .replace(/\\n/g, "\n");

  value = stripWechatStylePrelude(value);
  return value
    .split(/\n+/)
    .map((line) => line.trim())
    .filter((line) => line && !looksLikeCssLine(line))
    .join("\n\n")
    .trim();
}

function stripWechatStylePrelude(value: string) {
  const markers = [
    /^鍘熷垱\s+/m,
    /^原文地址[:：]/m,
    /^20\d{2}年/m,
    /^#\s+/m,
    /^\S.{4,80}\n=+$/m,
  ];
  const positions = markers
    .map((pattern) => value.search(pattern))
    .filter((position) => position > 0);
  if (!positions.length) return value;
  const firstContentAt = Math.min(...positions);
  const before = value.slice(0, firstContentAt);
  const cssSignals =
    (before.match(/[{};]/g) || []).length +
    (
      before.match(
        /\b(font|margin|padding|color|line-height|background|display|border)\b/g,
      ) || []
    ).length *
      4;
  return cssSignals > 20 ? value.slice(firstContentAt) : value;
}

function looksLikeCssLine(line: string) {
  if (/^[-_*]{3,}$/.test(line)) return true;
  const cssWords =
    line.match(
      /\b(font-family|font-size|margin|padding|outline|line-height|background|border|display|color|text-align|box-sizing)\b/g,
    ) || [];
  const braceCount = (line.match(/[{}]/g) || []).length;
  const semicolonCount = (line.match(/;/g) || []).length;
  return cssWords.length >= 2 || braceCount >= 2 || semicolonCount >= 4;
}

function parseFrontmatter(value: string) {
  const result: Record<string, string> = {};
  for (const line of value.split(/\r?\n/)) {
    const match = line.match(/^([A-Za-z0-9_-]+):\s*(.+)$/);
    if (!match) continue;
    const key = match[1].trim();
    const rawValue = match[2].trim().replace(/^["']|["']$/g, "");
    result[key] = rawValue
      .replace(/^\[|\]$/g, "")
      .split(",")
      .map((item) => item.trim().replace(/^["']|["']$/g, ""))
      .filter(Boolean)
      .join(",");
  }
  return result;
}

async function publishPreflight(
  store: Store,
  version: AccountVersion,
  platformId: string,
) {
  const localMatches = localTextDuplicates(
    store,
    version.body,
    platformId,
  ).filter((match) => match.id !== version.id && match.score >= 0.82);
  const remoteMatches = await checkDuplicates({
    kind: "text",
    text: version.body,
    platformId,
    accountId: version.accountId,
    excludeId: version.id,
  }).catch(() => null);
  const matches = remoteMatches?.matches?.length
    ? remoteMatches.matches
    : localMatches;
  return matches.filter(
    (match) => match.id !== version.id && match.score >= 0.82,
  );
}

function textSimilarity(left: string, right: string) {
  const a = new Set(textShingles(left));
  const b = new Set(textShingles(right));
  if (!a.size || !b.size) return 0;
  let intersection = 0;
  for (const token of a) if (b.has(token)) intersection += 1;
  return intersection / (a.size + b.size - intersection);
}

function textShingles(value: string) {
  const normalized = value
    .toLowerCase()
    .replace(/https?:\/\/\S+/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, "");
  if (!normalized) return [];
  if (normalized.length <= 3) return [normalized];
  const result: string[] = [];
  for (let index = 0; index <= normalized.length - 3; index += 1)
    result.push(normalized.slice(index, index + 3));
  return Array.from(new Set(result));
}

function DuplicateNotice({ matches }: { matches: DuplicateMatch[] }) {
  if (!matches.length) return null;
  return (
    <div className="duplicateNotice">
      <strong>可能重复</strong>
      {matches.slice(0, 4).map((match) => (
        <span key={match.id}>
          {match.title} · 相似度 {Math.round(match.score * 100)}%
        </span>
      ))}
    </div>
  );
}

function nameOf(rows: Array<{ id: string; name: string }>, id: string) {
  return rows.find((row) => row.id === id)?.name || "未设置";
}

function taskTitle(store: Store, id: string) {
  return store.tasks.find((row) => row.id === id)?.title || "未设置";
}

function collectionStatusLabel(status = "") {
  const labels: Record<string, string> = {
    new: "新采集",
    history: "历史内容",
    history_similar: "历史相似",
    link_collected: "链接采集",
    auto_collected: "任务采集",
    clear: "无重复",
    similar: "疑似相似",
  };
  return labels[status] || status || "未处理";
}

function collectionScheduleLabel(schedule: CollectionTask["schedule"]) {
  const labels: Record<CollectionTask["schedule"], string> = {
    manual: "手动",
    hourly: "每小时",
    daily: "每天",
    weekly: "每周",
  };
  return labels[schedule] || schedule;
}

function collectionLanguageLabel(language = "auto") {
  return collectionLanguages.find((item) => item.code === language)?.name || language || "自动识别";
}

function translationStatusLabel(
  status: CollectedItem["translationStatus"] = "none",
) {
  const labels: Record<string, string> = {
    none: "无需翻译",
    pending: "待翻译",
    translated: "已翻译",
    failed: "翻译失败",
  };
  return labels[status] || "无需翻译";
}

function inferCollectionLanguage(source: { name: string; url: string }) {
  const value = `${source.name} ${source.url}`.toLowerCase();
  if (/sat|dof|mexico|mexicano|gob\.mx/.test(value)) return "es";
  if (/mdic|camex|gov\.br/.test(value)) return "pt";
  if (/kgd|eec|eaeunion|ru\/|俄文|俄语/.test(value)) return "ru";
  if (/china|中文|公众号|wechat/.test(value)) return "zh-CN";
  return "en";
}

function filterPreviewItems(
  items: CollectedPreviewItem[],
  rule: CollectionRule,
) {
  if (!rule.enabled) return [];
  const includeKeywords = splitKeywords(rule.keywords);
  const excludeKeywords = splitKeywords(rule.excludeKeywords);
  return items.filter((item) => {
    const haystack = `${item.title} ${item.summary || ""} ${item.content || ""}`.toLowerCase();
    if (excludeKeywords.some((keyword) => haystack.includes(keyword))) return false;
    if (rule.mode === "all") return true;
    return includeKeywords.some((keyword) => haystack.includes(keyword));
  });
}

function splitKeywords(value: string) {
  return value
    .split(/[,，、\n]+/g)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
}

function collectedSummary(item: CollectedItem) {
  const summary = item.summary || "";
  return looksLikeCssLine(summary)
    ? summarize(cleanMarkdownContent(item.rawContent))
    : summary || summarize(cleanMarkdownContent(item.rawContent));
}

function inferTopicContentType(value: string) {
  if (/风险|预警|延误|查验|处罚|失败|异常|危机/.test(value)) return "风险提醒";
  if (/政策|法规|规则|公告|关税|税率|Section|301|232/i.test(value)) return "政策解读";
  if (/清关|海关|申报|报关|查验|放行/.test(value)) return "清关知识";
  if (/运输|物流|货代|仓库|派送|卡车|装车/.test(value)) return "物流知识";
  if (/案例|复盘|客户|项目/.test(value)) return "案例复盘";
  return "行业新闻";
}

function isPreviewableImagePath(value = "") {
  return (
    /^https?:\/\//i.test(value) ||
    /^data:image\//i.test(value) ||
    /^blob:/i.test(value) ||
    /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i.test(value)
  );
}
function openHtmlTab(title: string, html: string) {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, "_blank");
  if (tab) window.setTimeout(() => URL.revokeObjectURL(url), 30000);
  else URL.revokeObjectURL(url);
}

function buildCollectedReaderHtml(item: CollectedItem) {
  const body = cleanMarkdownContent(item.rawContent);
  return standaloneHtml({
    title: item.title,
    body: `
      <main class="reader">
        <header>
          <h1>${escapeHtml(item.title)}</h1>
          <p>${escapeHtml(item.url || "无原文链接")} 路 ${escapeHtml(collectionStatusLabel(item.status))} 路 ${escapeHtml(formatDateTime(item.createdAt))}</p>
        </header>
        <article class="content">${markdownToHtml(body)}</article>
      </main>`,
  });
}

function buildTaskReaderHtml(store: Store, task: ContentTask) {
  const topic = store.topics.find((item) => item.id === task.topicId);
  const masterArticles = store.masterArticles.filter(
    (item) => item.taskId === task.id,
  );
  const platforms =
    task.targetPlatformIds
      .map((id) => nameOf(store.platforms, id))
      .join("、") || "未设置";
  const accounts =
    task.targetAccountIds.map((id) => nameOf(store.accounts, id)).join("、") ||
    "未设置";
  return standaloneHtml({
    title: task.title,
    body: `
      <main class="reader">
        <header>
          <p>内容任务 路 ${escapeHtml(task.status)} 路 ${escapeHtml(formatDateTime(task.createdAt))}</p>
          <h1>${escapeHtml(task.title)}</h1>
        </header>
        <section class="metaGrid">
          <div><b>关联选题</b><span>${escapeHtml(topic?.title || "未关联")}</span></div>
    <div><b>业务线</b><span>${escapeHtml(nameOf(store.businessLines, task.businessLineId))}</span></div>
          <div><b>内容类型</b><span>${escapeHtml(task.contentType || "未设置")}</span></div>
          <div><b>截止时间</b><span>${escapeHtml(task.dueDate || "未设置")}</span></div>
          <div><b>鐩爣骞冲彴</b><span>${escapeHtml(platforms)}</span></div>
          <div><b>目标账号</b><span>${escapeHtml(accounts)}</span></div>
        </section>
        <section>
          <h2>创作方向</h2>
          ${markdownToHtml(task.direction || "暂无创作方向。")}
        </section>
        <section>
          <h2>内容大纲</h2>
          ${markdownToHtml(task.outline || "暂无内容大纲。")}
        </section>
        <section>
          <h2>关联母版文章</h2>
          ${
            masterArticles.length
              ? `<ul>${masterArticles
                  .map(
                    (article) =>
                      `<li>${escapeHtml(article.title)} 路 ${escapeHtml(article.status)}</li>`,
                  )
                  .join("")}</ul>`
    : "<p>暂无关联母版文章。</p>"
          }
        </section>
      </main>
    `,
  });
}

function standaloneHtml({ title, body }: { title: string; body: string }) {
  return `<!doctype html>
<html lang="zh-CN">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { margin: 0; background: #f4f6f8; color: #172026; font-family: Inter, "Microsoft YaHei", Arial, sans-serif; }
    main { width: min(1080px, calc(100vw - 32px)); margin: 28px auto; }
    header { margin-bottom: 18px; }
    h1 { margin: 0 0 8px; font-size: 28px; }
    header p { margin: 0; color: #67737f; }
    .content, label, .actions, section { border: 1px solid #dfe5ea; border-radius: 8px; background: #fff; padding: 16px; margin-bottom: 14px; }
    .content { line-height: 1.8; }
    .content h1, .content h2, .content h3 { margin: 16px 0 8px; }
    .content p, .content ul { margin: 0 0 12px; }
    .content a { color: #0f766e; }
    section { line-height: 1.8; }
    section h2 { margin: 0 0 10px; font-size: 18px; }
    section p, section ul { margin: 0 0 12px; }
    .metaGrid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
    .metaGrid div { display: grid; gap: 4px; }
    .metaGrid b { color: #67737f; font-size: 13px; }
    .metaGrid span { color: #172026; }
    .split { display: grid; grid-template-columns: minmax(0, 1fr) minmax(320px, 0.9fr); gap: 14px; }
    label { display: grid; gap: 8px; margin-bottom: 14px; color: #67737f; font-size: 13px; }
    input, textarea { width: 100%; box-sizing: border-box; border: 1px solid #dfe5ea; border-radius: 8px; padding: 10px; font: inherit; color: #172026; }
    textarea { min-height: 560px; font-family: Consolas, "Courier New", monospace; line-height: 1.6; resize: vertical; }
    button { border: 0; border-radius: 8px; padding: 10px 14px; color: #0f766e; background: #e2f5f2; cursor: pointer; font-weight: 700; }
    @media (max-width: 860px) { .split, .metaGrid { grid-template-columns: 1fr; } }
  </style>
</head>
<body>${body}</body>
</html>`;
}

function flattenKnowledgeTree(nodes: KnowledgeTreeNode[]): KnowledgeTreeNode[] {
  return nodes.flatMap((node) => [
    node,
    ...flattenKnowledgeTree(node.children || []),
  ]);
}

function filterKnowledgeTree(
  nodes: KnowledgeTreeNode[],
  query: string,
  tag: string,
  publishStatus = "",
): KnowledgeTreeNode[] {
  const keyword = query.trim().toLowerCase();
  return nodes
    .map((node) => {
      if (node.kind === "folder") {
        const children = filterKnowledgeTree(node.children, query, tag, publishStatus);
        return children.length ? { ...node, children } : null;
      }
      const matchQuery =
        !keyword ||
        node.title.toLowerCase().includes(keyword) ||
        node.path.toLowerCase().includes(keyword);
      const matchTag = !tag || node.tags?.includes(tag);
      const matchStatus = !publishStatus || node.publishStatus === publishStatus;
      return matchQuery && matchTag && matchStatus ? node : null;
    })
    .filter(Boolean) as KnowledgeTreeNode[];
}

function updateKnowledgeTreePublishStatus(
  nodes: KnowledgeTreeNode[],
  nodeId: string,
  status: { publishStatus: string; publishedAt: string; publishNote: string },
): KnowledgeTreeNode[] {
  return nodes.map((node) => {
    if (node.nodeId === nodeId || node.id === nodeId) {
      return {
        ...node,
        publishStatus: status.publishStatus,
        publishedAt: status.publishedAt,
        publishNote: status.publishNote,
      };
    }
    return {
      ...node,
      children: updateKnowledgeTreePublishStatus(node.children || [], nodeId, status),
    };
  });
}

function toDateTimeLocal(value: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function parseKnowledgeMarkdown(content: string, fallbackTitle: string) {
  const frontmatter = content.match(/^---\s*([\s\S]*?)\s*---/);
  const frontmatterText = frontmatter?.[1] || "";
  const titleFromFrontmatter = frontmatterText.match(/^title:\s*(.+)$/im)?.[1];
  const tagsFromFrontmatter = frontmatterText.match(/^tags:\s*(.+)$/im)?.[1];
  const titleFromHeading = content.match(/^#\s+(.+)$/m)?.[1];
  const labelLine = content.match(/^(标签|tags)：?\s*(.+)$/im)?.[2];
  return {
    title: cleanMetaValue(titleFromFrontmatter || titleFromHeading || fallbackTitle),
    tags: parseKnowledgeTags(tagsFromFrontmatter || labelLine || ""),
    content: frontmatter ? content.slice(frontmatter[0].length).trim() : content,
  };
}

function parseKnowledgeTags(value: string) {
  return cleanMetaValue(value)
    .replace(/^\[/, "")
    .replace(/\]$/, "")
    .split(/[,，、\s]+/)
    .map((tag) => cleanMetaValue(tag))
    .filter(Boolean);
}

function cleanMetaValue(value = "") {
  return value.trim().replace(/^["']|["']$/g, "");
}

function remoteKnowledgeNodeToLocal(node: RemoteKnowledgeNode): KnowledgeTreeNode {
  return {
    id: node.id,
    name: node.name,
    title: node.title,
    path: node.path,
    kind: node.kind,
    children: (node.children || []).map(remoteKnowledgeNodeToLocal),
    updatedAt: node.updatedAt ? Date.parse(node.updatedAt) : undefined,
    tags: node.tags || [],
    nodeId: node.nodeId || node.id,
    publishStatus: node.publishStatus || "unpublished",
    publishedAt: node.publishedAt || "",
    publishNote: node.publishNote || "",
  };
}

function markdownToHtml(markdown = "") {
  const lines = cleanMarkdownContent(markdown).split(/\n+/);
  const html: string[] = [];
  let inList = false;
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    const listMatch = line.match(/^[-*]\s+(.+)$/);
    if (listMatch) {
      if (!inList) {
        html.push("<ul>");
        inList = true;
      }
      html.push(`<li>${inlineMarkdown(listMatch[1])}</li>`);
      continue;
    }
    if (inList) {
      html.push("</ul>");
      inList = false;
    }
    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      html.push(`<h${level}>${inlineMarkdown(heading[2])}</h${level}>`);
    } else {
      html.push(`<p>${inlineMarkdown(line)}</p>`);
    }
  }
  if (inList) html.push("</ul>");
  return html.join("");
}

function inlineMarkdown(value: string) {
  return escapeHtml(value)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(
      /\[([^\]]+)]\((https?:\/\/[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noreferrer">$1</a>',
    );
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatDateTime(value = "") {
  if (!value) return "未记录";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-CN", { hour12: false });
}

function bindingModeLabel(mode?: AccountBindMode) {
  const labels: Record<AccountBindMode, string> = {
    manual: "手工登记",
    oauth: "开放平台授权",
    qr: "扫码登录",
    cookie: "会话托管",
    api: "API Token",
  };
  return labels[mode || "manual"];
}

function platformPublishModeLabel(mode: Platform["publishMode"]) {
  return mode === "api" ? "API 发布" : mode === "draft" ? "草稿箱" : "人工发布";
}

function bindingStatusLabel(status?: AccountBindStatus) {
  const labels: Record<AccountBindStatus, string> = {
    unbound: "未绑定",
    pending: "待确认",
    bound: "已绑定",
    expired: "已过期",
  };
  return labels[status || "unbound"];
}

function topicStatusLabel(status: Topic["status"]) {
  const labels: Record<Topic["status"], string> = {
    pending: "待处理",
    approved: "已确认",
    converted: "已转任务",
  };
  return labels[status] || status;
}

function fileDataUrl(file?: File) {
  if (!file) return Promise.resolve("");
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function bindingLabel(account: MediaAccount) {
  const expires = account.tokenExpiresAt ? ` / ${account.tokenExpiresAt}` : "";
  const subject = account.authSubject ? ` / ${account.authSubject}` : "";
  return `${bindingModeLabel(account.bindMode)} · ${bindingStatusLabel(account.bindStatus)}${subject}${expires}`;
}

function nextMonthDate() {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  return date.toISOString().slice(0, 10);
}

function summarize(text = "") {
  return text.replace(/\s+/g, " ").slice(0, 90) || "系统将在这里生成 AI 摘要。";
}

function buildLocalRewrittenArticle(
  sourceTitle: string,
  sourceBody: string,
  prompt?: AiRewritePrompt,
  extraRequirement = "",
) {
  const clean = cleanMarkdownContent(sourceBody);
  const summary = summarize(clean);
  const keywords = extractKeywords(`${sourceTitle} ${clean}`);
  const requirement = extraRequirement
    ? `\n\n> 补充要求：${extraRequirement}`
    : "";
  return `# ${sourceTitle}：这几个变化值得外贸企业关注

## 为什么这件事值得关注

${summary}${requirement}

## 核心信息梳理

这篇内容来自采集资料，原文重点可以概括为：围绕 ${keywords} 等关键词，相关政策、市场或操作规则正在发生变化。对外贸企业来说，真正需要关注的不是单一新闻本身，而是它可能带来的申报、清关、成本、时效和合规影响。

## 对业务的实际影响

1. 如果涉及关税、海关、进口、出口或边境监管，需要优先检查当前发货资料是否完整。
2. 如果涉及贸易政策变化，需要确认客户、供应商和目的国要求是否已经同步更新。
3. 如果涉及物流、货运、清关或查验，需要提前评估延误、补资料和额外成本风险。

## 发货前建议检查

- 商品名称、HS Code、申报要素是否一致。
- 发票、装箱单、合同、付款资料是否能相互对应。
- 目的国进口要求、关税政策和特殊监管要求是否有变化。
- 是否需要提前向客户说明潜在延误或成本调整。

## 结尾建议

这类内容适合持续跟踪，不建议等到货物已经出运后再补救。企业可以先把资料、申报口径和客户沟通话术统一起来，降低后续清关和交付风险。

---

生成方式：${prompt?.name || "本地规则洗稿"}。`;
}

function extractKeywords(text = "") {
  const candidates = ["清关", "DDP", "中亚", "大件", "税费", "风险", "资料", "物流", "口岸", "客户"];
  return candidates.filter((word) => text.includes(word)).slice(0, 5).join(",") || "物流,风险,获客";
}

function makeMasterArticle(task: ContentTask) {
  return `# ${task.title}

## 一、为什么这个问题值得关注
${task.direction || "这类内容适合先做成公众号级别的深度母版，再拆成多平台版本。"}

## 二、核心风险与判断标准
围绕“${task.contentType}”展开，结合业务线特点，说明客户最容易忽略的环节。

## 三、操作清单
1. 发货前确认资料完整性。
2. 提前核对申报要素与目的国要求。
3. 将异常节点写成可复用案例。
4. 为不同平台准备不同表达方式。

## 四、转化话术
如果你正在处理类似业务，可以把货物信息、目的地和时效要求整理出来，系统会辅助生成适合不同账号发布的内容版本。`;
}

function rewriteForPlatform(body: string, platform: string) {
  if (platform === "小红书") {
    return `【小红书图文版】\n开头先讲结论：${summarize(body)}\n\n适合拆成 6-8 页卡片，每页一个风险点，语气更直接，标签更强。`;
  }
  if (platform === "抖音" || platform === "视频号") {
    return `【短视频脚本版】\n开场钩子：很多货不是运输慢，而是清关资料一开始就错了。\n\n${summarize(body)}\n\n结尾：关注我，继续讲跨境物流避坑。`;
  }
  return `【${platform}版】\n${body}\n\n本文适合保留深度结构，并增加案例和清单。`;
}

function rewriteForAccount(body: string, account: MediaAccount) {
  return `${account.persona}\n\n${body}\n\n表达风格：${account.style}\n目标客户：${account.targetCustomer}\n${account.cta}`;
}

function aiLog(
  taskId: string,
  objectType: string,
  input: string,
  output: string,
): AiLog {
  return {
    id: uid("log"),
    taskId,
    objectType,
    input,
    output,
    cost: Number((0.04 + Math.random() * 0.2).toFixed(2)),
    status: "success",
    createdAt: now(),
  };
}

function makeImagePages(body: string, count: number) {
  const base = [
    "标题页：先给结论",
    "风险一：资料不完整",
    "风险二：申报不一致",
    "风险三：旺季查验增加",
    "解决方案：发货前清单",
    "案例页：真实业务场景",
    "CTA：私信获取方案",
    "总结页：保存备用",
  ];
  return base.slice(0, count).map((item, index) => `第${index + 1}页：${item}。${summarize(body).slice(0, 36)}`);
}

function makeImagePrompt(page: string, index: number, platform: string) {
  const role = index === 0 ? "封面强标题" : index === 1 ? "风险解释页" : "清单信息页";
  return `${platform || "图文平台"}，${role}：${page}，干净商务风，中文排版清晰，留白充足，适合手机阅读`;
}

function buildImageTextPackage(store: Store, project: ImageTextProject) {
  const version = findAccountVersion(store, project.accountVersionId);
  const account = version ? store.accounts.find((item) => item.id === version.accountId) : null;
  const platform = account ? store.platforms.find((item) => item.id === account.platformId) : null;
  const assets = project.assetIds
    ?.map((id) => store.assets.find((asset) => asset.id === id))
    .filter(Boolean) as MaterialAsset[] | undefined;
  return `# ${project.title}

平台：${platform?.name || ""}
账号：${account?.name || ""}
比例：${project.ratio}
封面：${project.coverTitle || project.title}
风格：${project.designStyle || ""}
状态：${project.status}

## 发布标题
${project.publishTitle || project.coverTitle || project.title}

## 发布正文
${project.publishContent || makeImageTextPublishContent(version)}

## 发布图片
${project.generatedImagePaths?.length ? project.generatedImagePaths.map((path, index) => `- 第 ${index + 1} 张：${path}`).join("\n") : "- 暂无成图"}

## 分页脚本
${project.pages
  .map(
    (page, index) => `### 第 ${index + 1} 页\n文案：${page}\n\n图片 Prompt：${project.imagePrompts[index] || makeImagePrompt(page, index, platform?.name || "")}`,
  )
  .join("\n\n")}

## 引用素材
${assets?.length ? assets.map((asset) => `- ${asset.name}：${asset.archiveDir ? `${asset.archiveDir}/${asset.fileName}` : asset.path || asset.storageKey}`).join("\n") : "- 暂无"}

## 原账号版本
${version?.body || ""}`;
}

function findAccountVersion(store: Store, accountVersionId: string) {
  return store.accountVersions.find((item) => item.id === accountVersionId);
}

function makeImageTextPublishContent(version?: AccountVersion) {
  if (!version) return "";
  const hashtags = version.hashtags
    ? version.hashtags
        .split(/[,，、\s]+/)
        .map((tag) => tag.trim())
        .filter(Boolean)
        .map((tag) => (tag.startsWith("#") ? tag : `#${tag}`))
        .join(" ")
    : "";
  return [version.body, version.cta, hashtags].filter(Boolean).join("\n\n");
}

function makeLocalImageTextVariant(
  title: string,
  content: string,
  index: number,
  strength: string,
) {
  const titlePrefixes =
    strength === "strong"
      ? ["别只看价格", "客户压价时", "外贸沟通里", "报价前先问清", "这句话更稳"]
      : strength === "light"
        ? ["实用话术", "外贸沟通", "报价沟通", "客户沟通", "清关沟通"]
        : ["外贸人常用", "客户问价时", "报价别急着降", "沟通更专业", "这类客户这样回"];
  const titleSuffixes = ["这样说", "更容易成交", "少踩坑", "更专业", "更稳妥"];
  const cleaned = content.split(/\n+/).map((line) => line.trim()).filter(Boolean);
  const rotated = cleaned.length
    ? cleaned.slice(index % cleaned.length).concat(cleaned.slice(0, index % cleaned.length))
    : [content];
  const connector =
    strength === "strong"
      ? "换个角度说："
      : strength === "light"
        ? "可以这样理解："
        : "实际沟通时可以这样处理：";
  return {
    title: `${titlePrefixes[index % titlePrefixes.length]}：${title.replace(/^.*?[:：]/, "").slice(0, 20)}${titleSuffixes[index % titleSuffixes.length]}`,
    content: [connector, ...rotated, `\n第 ${index} 版提示：表达可以变化，但核心信息和风险提醒保持一致。`].join("\n\n"),
  };
}

async function makeImageVariant(
  source: string,
  options: {
    quality: number;
    width: number;
    brightness: number;
    saturation: number;
    grayscale: boolean;
    watermark: string;
    logoText: string;
    variantIndex: number;
    pageIndex: number;
  },
) {
  const image = await loadImageElement(source);
  const scale = options.width ? options.width / image.naturalWidth : 1;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 初始化失败");
  const saturation = Math.max(0, 100 + options.saturation);
  const brightness = Math.max(20, 100 + options.brightness);
  ctx.filter = `${options.grayscale ? "grayscale(1)" : ""} brightness(${brightness}%) saturate(${saturation}%)`;
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  ctx.filter = "none";
  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.fillRect(canvas.width - 112, canvas.height - 32, 96, 20);
  ctx.fillStyle = "rgba(15,118,110,0.9)";
  ctx.font = `${Math.max(12, Math.round(canvas.width / 42))}px Arial`;
  ctx.fillText(`V${options.variantIndex}-${options.pageIndex}`, canvas.width - 106, canvas.height - 17);
  if (options.watermark) {
    ctx.save();
    ctx.globalAlpha = 0.16;
    ctx.fillStyle = "#0f766e";
    ctx.font = `${Math.max(18, Math.round(canvas.width / 18))}px Arial`;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(-0.32);
    ctx.fillText(options.watermark, -canvas.width / 3, 0);
    ctx.restore();
  }
  if (options.logoText) {
    ctx.fillStyle = "rgba(15,118,110,0.88)";
    ctx.fillRect(14, 14, Math.max(70, options.logoText.length * 14), 30);
    ctx.fillStyle = "#ffffff";
    ctx.font = "16px Arial";
    ctx.fillText(options.logoText, 24, 35);
  }
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (value) => (value ? resolve(value) : reject(new Error("图片生成失败"))),
      "image/jpeg",
      options.quality / 100,
    ),
  );
  const buffer = await blob.arrayBuffer();
  return {
    blob,
    hash: await sha256ArrayBuffer(buffer),
  };
}

function loadImageElement(source: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`图片读取失败：${source.slice(0, 80)}`));
    image.src = source;
  });
}

async function sha256ArrayBuffer(buffer: ArrayBuffer) {
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}

function buildVariantAttachmentPath(
  settings: AppSettings,
  project: ImageTextProject,
  variantIndex: number,
  pageIndex: number,
) {
  return joinPath(
    settings.attachmentRoot,
    "image-text-variants",
    new Date().toISOString().slice(0, 10),
    safeFileName(project.title),
    `variant-${String(variantIndex).padStart(3, "0")}`,
    `page-${pageIndex}.jpg`,
  );
}

function makeVideoScript(body: string) {
  return `开场钩子：很多物流问题，不是出在运输，而是出在清关前的准备。\n\n正文：${summarize(body)}\n\n结尾：想要减少延误，先把资料、申报和目的国要求核清楚。`;
}

function makeStoryboard(body: string) {
  return `镜头1：仓库/装车画面，字幕强调风险。\n镜头2：资料清单特写，解释关键字段。\n镜头3：路线或口岸画面，说明异常节点。\n镜头4：顾问口播总结，${summarize(body)}`;
}

function makeSubtitles(body: string) {
  return `1\n00:00:00,000 --> 00:00:03,000\n很多清关延误，发货前就埋下了。\n\n2\n00:00:03,000 --> 00:00:08,000\n${summarize(body)}`;
}

function makeDraftAccountVersionFromArticle(
  store: Store,
  article: MasterArticle,
): AccountVersion | null {
  const task = store.tasks.find((item) => item.id === article.taskId);
  const account =
    task?.targetAccountIds
      .map((id) => store.accounts.find((item) => item.id === id))
      .find(Boolean) ||
    store.accounts.find((item) => item.enabled) ||
    store.accounts[0];
  if (!account) return null;
  const existing = store.accountVersions.find(
    (item) => item.platformVersionId === article.id && item.accountId === account.id,
  );
  if (existing) return existing;
  return {
    id: uid("av"),
    platformVersionId: article.id,
    accountId: account.id,
    title: `${article.title} · ${account.name}`,
    body: rewriteForAccount(article.body, account),
    coverText: article.title.slice(0, 24),
    hashtags: extractKeywords(article.body)
      .split(",")
      .map((item) => `#${item}`)
      .join(" "),
    cta: account.cta,
    status: "待发布",
    similarityScore: 0.5,
    createdAt: now(),
  };
}

function makeEditNotes(body: string, platform: string) {
  return `${platform || "短视频平台"}混剪建议：开头 3 秒直接给结论；中段用资料清单、港口/仓库/路线素材快速切换；结尾保留 CTA。画面节奏以 2-4 秒一切为主，字幕突出关键词：${extractKeywords(body)}`;
}

function storyboardSegments(storyboard: string) {
  return storyboard.split(/\n+/).map((item) => item.trim()).filter(Boolean);
}

function buildVideoPackage(store: Store, project: VideoProject) {
  const version = store.accountVersions.find((item) => item.id === project.accountVersionId);
  const account = version ? store.accounts.find((item) => item.id === version.accountId) : null;
  const platform = account ? store.platforms.find((item) => item.id === account.platformId) : null;
  const assets = project.assetIds
    ?.map((id) => store.assets.find((asset) => asset.id === id))
    .filter(Boolean) as MaterialAsset[] | undefined;
  return `# ${project.title}

平台：${platform?.name || ""}
账号：${account?.name || ""}
比例：${project.aspectRatio || "9:16"}
时长：${project.duration || "60s"}
配音：${project.voice}
状态：${project.status}

## 口播脚本
${project.script}

## 分镜
${storyboardSegments(project.storyboard).map((segment, index) => `${index + 1}. ${segment}`).join("\n")}

## 字幕
${project.subtitles}

## 混剪说明
${project.editNotes || ""}

## 引用素材
${assets?.length ? assets.map((asset) => `- ${asset.name}：${asset.archiveDir ? `${asset.archiveDir}/${asset.fileName}` : asset.path || asset.storageKey}`).join("\n") : "- 暂无"}

## 原账号版本
${version?.body || ""}`;
}
function engagementScore(metric: MetricRecord) {
  return (
    metric.likes * 2 +
    metric.favorites * 3 +
    metric.comments * 4 +
    metric.shares * 5 +
    metric.followersGain * 8 +
    metric.views * 0.01
  );
}

function engagementRate(
  metric: Pick<
    MetricRecord,
    "views" | "likes" | "favorites" | "comments" | "shares"
  >,
) {
  if (!metric.views) return "0.00";
  return (
    ((metric.likes + metric.favorites + metric.comments + metric.shares) /
      metric.views) *
    100
  ).toFixed(2);
}

function metricPlanTitle(store: Store, publishPlanId: string) {
  const plan = store.publishPlans.find((item) => item.id === publishPlanId);
  const version = plan
    ? store.accountVersions.find((item) => item.id === plan.accountVersionId)
    : null;
  return version?.title || publishPlanId;
}

function metricAccountName(store: Store, publishPlanId: string) {
  const plan = store.publishPlans.find((item) => item.id === publishPlanId);
  return plan ? nameOf(store.accounts, plan.accountId) : "未设置";
}

function buildAnalyticsReport(store: Store) {
  const ranked = [...store.metrics].sort((a, b) => engagementScore(b) - engagementScore(a));
  const totals = store.metrics.reduce(
    (sum, item) => ({
      views: sum.views + item.views,
      likes: sum.likes + item.likes,
      favorites: sum.favorites + item.favorites,
      comments: sum.comments + item.comments,
      shares: sum.shares + item.shares,
      followersGain: sum.followersGain + item.followersGain,
    }),
    { views: 0, likes: 0, favorites: 0, comments: 0, shares: 0, followersGain: 0 },
  );
  return `# MediaPilotX 数据复盘

日期：${new Date().toLocaleDateString("zh-CN")}

## 总览
- 阅读/播放：${totals.views}
- 互动：${totals.likes + totals.favorites + totals.comments + totals.shares}
- 涨粉：${totals.followersGain}
- 互动率：${engagementRate(totals)}%

## Top 内容
${
  ranked
    .slice(0, 10)
    .map(
      (metric, index) =>
        `${index + 1}. ${metricPlanTitle(store, metric.publishPlanId)} · ${metricAccountName(store, metric.publishPlanId)} · 播放 ${metric.views} · 互动率 ${engagementRate(metric)}% · 涨粉 ${metric.followersGain}`,
    )
    .join("\n") || "暂无数据"
}

## 明细
${
  store.metrics
    .map(
      (metric) =>
        `- ${metricPlanTitle(store, metric.publishPlanId)}：播放 ${metric.views}，点赞 ${metric.likes}，收藏 ${metric.favorites}，评论 ${metric.comments}，转发 ${metric.shares}，涨粉 ${metric.followersGain}`,
    )
    .join("\n") || "暂无数据"
}`;
}
function renderPromptPreview(prompt: string) {
  const sample: Record<string, string> = {
    business_line: "美国DDP清关",
    platform: "小红书",
    account_persona: "专业清关顾问，表达直接，重视风险提醒",
    source_content: "客户发货前需要确认资料、申报和目的国要求，避免清关延误。",
  };
  return prompt.replace(
    /\{\{(\w+)\}\}/g,
    (_, key: string) => sample[key] || `{{${key}}}`,
  );
}

function businessCoverage(store: Store) {
  if (!store.businessLines.length) return "0";
  const covered = new Set(
    store.accounts.flatMap((account) => account.businessLineIds),
  );
  return ((covered.size / store.businessLines.length) * 100).toFixed(0);
}

function nextDateTime() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(10, 0, 0, 0);
  return date.toISOString();
}

function formatDate(value: string) {
  return value ? new Date(value).toLocaleString("zh-CN") : "未设置";
}

function rand(min: number, max: number) {
  return Math.floor(min + Math.random() * (max - min));
}

function buildLocalExportPackage(
  store: Store,
  plan: PublishPlan,
): ExportPackage {
  const version = store.accountVersions.find((item) => item.id === plan.accountVersionId);
  const account = store.accounts.find((item) => item.id === plan.accountId);
  const platform = store.platforms.find((item) => item.id === plan.platformId);
  const title = version?.title || "未命名发布包";
  const body = version?.body || "";
  const hashtags = version?.hashtags || "";
  const cta = version?.cta || account?.cta || "";
  const archive = buildArchiveName({
    root: "publish-packages",
    businessLineName: account?.persona || "content",
    platformName: platform?.name || "platform",
    type: "manual-publish",
    title,
    idValue: plan.id,
    extension: "json",
  });
  const markdown = `# ${title}

平台：${platform?.name || ""}
账号：${account?.name || ""}
计划发布时间：${formatDate(plan.scheduledAt)}

---

${body}

${hashtags ? `\n标签：${hashtags}\n` : ""}${cta ? `\nCTA：${cta}\n` : ""}`;
  const textContent = `${title}

${body}

${hashtags}

${cta}`.trim();
  return {
    id: uid("pkg"),
    publishPlanId: plan.id,
    accountVersionId: plan.accountVersionId,
    accountId: plan.accountId,
    platformId: plan.platformId,
    title,
    markdown,
    text: textContent,
    json: JSON.stringify(
      {
        title,
        body,
        hashtags,
        cta,
        platformName: platform?.name || "",
        accountName: account?.name || "",
        scheduledAt: plan.scheduledAt,
        publishPlanId: plan.id,
        archiveDir: archive.archiveDir,
        fileName: archive.fileName,
      },
      null,
      2,
    ),
    status: "ready",
    archiveDir: archive.archiveDir,
    fileName: archive.fileName,
    createdAt: now(),
  };
}
function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function safeFileName(value: string) {
  return value.replace(/[\\/:*?"<>|]/g, "_").slice(0, 80) || "publish-package";
}

function buildArchiveName(input: {
  root: string;
  businessLineName: string;
  platformName: string;
  type: string;
  title: string;
  idValue: string;
  extension: string;
}) {
  const date = new Date().toISOString().slice(0, 10);
  const idPart =
    input.idValue.replace(/[^a-zA-Z0-9]/g, "").slice(-8) ||
    Math.random().toString(36).slice(2, 10);
  const archiveDir = [
    input.root,
    slugPart(input.businessLineName, "business"),
    slugPart(input.platformName, "platform"),
    date,
  ].join("/");
  return {
    archiveDir,
    fileName: `${slugPart(input.type, "item")}-${slugPart(input.title, "untitled")}-${idPart}.${input.extension.replace(/^\./, "") || "bin"}`,
  };
}

function slugPart(value: string, fallback: string) {
  const slug = value
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return slug || fallback;
}

function fileExtension(filename: string) {
  const match = filename.match(/\.([a-zA-Z0-9]{1,12})$/);
  return match?.[1]?.toLowerCase() || "bin";
}

function attachmentPath(
  settings: AppSettings,
  archiveDir: string,
  fileName: string,
) {
  return joinPath(
    settings.attachmentRoot || "attachments",
    archiveDir,
    fileName,
  );
}

function cloudAttachmentPath(
  settings: AppSettings,
  archiveDir: string,
  fileName: string,
) {
  return joinPath(
    settings.cloudSyncPrefix || "attachments",
    archiveDir,
    fileName,
  ).replace(/\\/g, "/");
}

function joinPath(...parts: string[]) {
  const [first = "", ...rest] = parts.filter(Boolean);
  const root = first.replace(/[\\/]+$/g, "");
  const suffix = rest
    .map((part) => part.replace(/^[\\/]+|[\\/]+$/g, ""))
    .filter(Boolean)
    .join("\\");
  return suffix ? `${root}\\${suffix}` : root;
}

function formatBytes(value: number) {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function upsertById<T extends { id: string }>(rows: T[], row: T) {
  const index = rows.findIndex((item) => item.id === row.id);
  if (index >= 0) rows[index] = row;
  else rows.unshift(row);
}

createRoot(document.getElementById("root")!).render(<App />);






